/**
 * RealityKit Model Manager – Frontend
 * Viewer: three.js + USDZLoader, Konfiguration + Swift-Export für RealityKit.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { USDZLoader as SimpleUSDZLoader } from 'three/addons/loaders/USDZLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
// Gevendorte Kopie von three-usdz-loader (gepatcht: "./"-Texturpfade)
import { USDZLoader as WasmUSDZLoader } from './vendor/three-usdz-loader/USDZLoader.js';
// Gevendorte Kopie des USDZExporters (gepatcht: userData.usdName als Knotenname)
import { USDZExporter } from './vendor/USDZExporter.js';
// GLB-Export mit benannten Animations-Clips (MotionSync, Blender, …)
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
// Mesh-Dezimierung für kleinere Export-Dateien
import { MeshoptSimplifier } from 'meshoptimizer';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = {
  models: [],          // [{slug, file, size, modified, config}]
  current: null,       // aktuelles Modell-Objekt aus state.models
  config: null,        // aktive (editierbare) Konfiguration
  rawSize: null,       // THREE.Vector3 – Originalmaße des Modells in Metern
  dirty: false,
  segments: [],        // [{name, color, visible, sel: {meshIdx: Uint8Array}, highlights: []}]
};

const $ = (id) => document.getElementById(id);

// ---------------------------------------------------------------------------
// Three.js Viewer
// ---------------------------------------------------------------------------

const viewerEl = $('viewer');
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x14161a);

const camera = new THREE.PerspectiveCamera(50, 1, 0.001, 2000);
camera.position.set(1.5, 1.2, 2.2);

// Modell-Container: modelRoot trägt die User-Transformation
const modelRoot = new THREE.Group();
scene.add(modelRoot);
let modelObject = null;     // geladenes USDZ
let bboxHelper = null;
let wireframeOn = false;

// Renderer kann fehlschlagen (kein WebGL) – Konfiguration & Export
// müssen trotzdem funktionieren, nur die 3D-Vorschau entfällt dann.
let renderer = null;
let controls = null;

try {
  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  viewerEl.appendChild(renderer.domElement);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  // Beleuchtung
  const hemi = new THREE.HemisphereLight(0xffffff, 0x444455, 0.7);
  scene.add(hemi);
  const dirLight = new THREE.DirectionalLight(0xffffff, 1.6);
  dirLight.position.set(3, 6, 4);
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.set(2048, 2048);
  scene.add(dirLight);

  // Boden: Raster (1 m Zellen) + Schattenfänger
  const grid = new THREE.GridHelper(10, 10, 0x4c8dff, 0x2a2e36);
  grid.material.transparent = true;
  grid.material.opacity = 0.5;
  scene.add(grid);

  const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.ShadowMaterial({ opacity: 0.35 })
  );
  shadowPlane.rotation.x = -Math.PI / 2;
  shadowPlane.receiveShadow = true;
  scene.add(shadowPlane);

  scene.add(new THREE.AxesHelper(0.5));

  const resize = () => {
    const w = viewerEl.clientWidth, h = viewerEl.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };
  new ResizeObserver(resize).observe(viewerEl);
  resize();

  const clock = new THREE.Clock();
  renderer.setAnimationLoop(() => {
    applyJoysticks();   // Joystick-Eingaben in Kamera-Orbit/Roll/Zoom umsetzen
    controls.update();
    if (usdInstance) usdInstance.update(clock.getElapsedTime());  // USD-Animationen
    renderer.render(scene, camera);
  });
} catch (err) {
  console.error('WebGL nicht verfügbar:', err);
  $('viewer-empty').firstElementChild.innerHTML =
    'WebGL ist in diesem Browser nicht verfügbar – die 3D-Vorschau entfällt.<br>' +
    '<small>Maße, Konfiguration und Export funktionieren weiterhin.</small>';
}

// ---------------------------------------------------------------------------
// Modell laden & Transformation anwenden
// ---------------------------------------------------------------------------

// Primär: WASM-Loader (volle USD-Unterstützung inkl. USDC-Binärformat).
// Benötigt SharedArrayBuffer → COOP/COEP-Header. Fallback: einfacher
// three.js-Loader (nur USDA-basierte Dateien).
const wasmLoader = (typeof SharedArrayBuffer !== 'undefined')
  ? new WasmUSDZLoader('assets/usd')
  : null;
const simpleLoader = new SimpleUSDZLoader();
let usdInstance = null;       // USDZInstance des WASM-Loaders (für Animation/Cleanup)
let loadToken = 0;            // verhindert Races bei schnellem Modellwechsel

async function loadModel(model) {
  const token = ++loadToken;
  clearModel();
  $('viewer-empty').firstElementChild.innerHTML = 'Lade Modell…';
  $('viewer-empty').style.display = 'flex';

  try {
    const obj = await loadUsdz(model);
    if (token !== loadToken) {  // inzwischen anderes Modell gewählt
      disposeObject(obj);
      return;
    }
    modelObject = obj;
    obj.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    // Originalmaße (vor User-Transformation) bestimmen
    const rawBox = new THREE.Box3().setFromObject(obj);
    state.rawSize = rawBox.getSize(new THREE.Vector3());

    modelRoot.add(obj);
    applyTransform();
    frameModel();
    buildMeshRegistry();
    restoreSegmentsFromConfig();
    restoreSkeletonFromConfig();
    if (renderer) $('viewer-empty').style.display = 'none';
  } catch (err) {
    if (token !== loadToken) return;
    console.error('USDZ-Ladefehler:', err);
    $('viewer-empty').firstElementChild.innerHTML =
      'Vorschau nicht möglich.<br><small>Dieses USDZ konnte nicht geparst werden. ' +
      'Konfiguration &amp; Export funktionieren trotzdem.</small>';
    state.rawSize = null;
    updateDims();
  }
}

/** Lädt ein USDZ – erst per WASM-Loader, sonst per einfachem Loader. */
async function loadUsdz(model) {
  const url = model.file + '?v=' + encodeURIComponent(model.modified);

  if (wasmLoader) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const file = new File([blob], model.slug + '.usdz');
      const group = new THREE.Group();
      usdInstance = await wasmLoader.loadFile(file, group);
      return group;
    } catch (err) {
      console.warn('WASM-Loader fehlgeschlagen, versuche einfachen Loader:', err);
      usdInstance = null;
    }
  }
  return simpleLoader.loadAsync(url);
}

function clearModel() {
  resetSegmentation();
  resetSkeleton();
  if (usdInstance) {
    try { usdInstance.clear(); } catch (e) { /* bereits freigegeben */ }
    usdInstance = null;
  }
  if (modelObject) {
    modelRoot.remove(modelObject);
    disposeObject(modelObject);
    modelObject = null;
  }
  removeBBox();
}

function disposeObject(obj) {
  obj?.traverse((n) => {
    if (n.isMesh) {
      n.geometry?.dispose();
      const mats = Array.isArray(n.material) ? n.material : [n.material];
      mats.forEach((m) => m?.dispose());
    }
  });
}

/** Konfigurationswerte (Skalierung, Rotation, Position, Baseline) live anwenden. */
function applyTransform() {
  if (!modelObject || !state.config) { updateDims(); return; }
  const c = state.config;

  modelRoot.scale.set(c.scale.x, c.scale.y, c.scale.z);
  modelRoot.rotation.set(
    THREE.MathUtils.degToRad(c.rotation.x),
    THREE.MathUtils.degToRad(c.rotation.y),
    THREE.MathUtils.degToRad(c.rotation.z)
  );
  modelRoot.position.set(c.position.x, c.position.y, c.position.z);

  if (c.groundAlign) {
    modelRoot.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(modelRoot);
    if (!box.isEmpty()) {
      // Unterkante auf y=0, der User-Offset wirkt dann relativ zur Ebene
      modelRoot.position.y += -box.min.y + c.position.y;
    }
  }

  refreshBBox();
  updateDims();
}

function refreshBBox() {
  const wasOn = !!bboxHelper;
  removeBBox();
  if (wasOn) addBBox();
}
function addBBox() {
  if (!modelObject) return;
  modelRoot.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(modelRoot);
  bboxHelper = new THREE.Box3Helper(box, 0x4c8dff);
  scene.add(bboxHelper);
}
function removeBBox() {
  if (bboxHelper) { scene.remove(bboxHelper); bboxHelper = null; }
}

function frameBox(box) {
  if (!controls || box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z) * 0.5 || 0.5;
  const dist = radius / Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * 1.6;
  camera.up.set(0, 1, 0);   // etwaigen Joystick-Roll aufheben
  controls.target.copy(center);
  camera.position.set(center.x + dist * 0.7, center.y + dist * 0.5, center.z + dist * 0.8);
  camera.near = Math.max(dist / 1000, 0.0001);
  camera.far = dist * 100;
  camera.updateProjectionMatrix();
}

function frameModel() {
  if (!modelObject || !controls) return;
  modelRoot.updateMatrixWorld(true);
  frameBox(new THREE.Box3().setFromObject(modelRoot));
}

function fmtLen(meters) {
  if (!isFinite(meters)) return '–';
  if (Math.abs(meters) < 0.01) return (meters * 1000).toFixed(1) + ' mm';
  if (Math.abs(meters) < 1) return (meters * 100).toFixed(1) + ' cm';
  return meters.toFixed(2) + ' m';
}

function updateDims() {
  const o = $('dim-original'), s = $('dim-scaled');
  if (!state.rawSize) { o.textContent = '–'; s.textContent = '–'; return; }
  const r = state.rawSize, c = state.config;
  o.textContent = `${fmtLen(r.x)} × ${fmtLen(r.y)} × ${fmtLen(r.z)}`;
  if (c) {
    s.textContent = `${fmtLen(r.x * c.scale.x)} × ${fmtLen(r.y * c.scale.y)} × ${fmtLen(r.z * c.scale.z)}`;
  }
}

/** Kamera aus einer Standardrichtung auf das Modell ausrichten (Zoom bleibt). */
function viewPreset(dir) {
  if (!modelObject || !controls) return;
  modelRoot.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(modelRoot);
  if (box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const dist = camera.position.distanceTo(controls.target) || box.getSize(new THREE.Vector3()).length();
  camera.up.set(0, 1, 0);   // etwaigen Joystick-Roll aufheben
  controls.target.copy(center);
  camera.position.copy(center).addScaledVector(dir, dist);
  // Bei Draufsicht die Kamera minimal kippen, damit Orbit stabil bleibt
  if (Math.abs(dir.y) > 0.99) camera.position.z += dist * 0.001;
  camera.updateProjectionMatrix();
}

const VIEW_PRESETS = {
  front: new THREE.Vector3(0, 0, 1),
  side: new THREE.Vector3(1, 0, 0),
  top: new THREE.Vector3(0, 1, 0),
};
$('btn-view-front').addEventListener('click', () => viewPreset(VIEW_PRESETS.front));
$('btn-view-side').addEventListener('click', () => viewPreset(VIEW_PRESETS.side));
$('btn-view-top').addEventListener('click', () => viewPreset(VIEW_PRESETS.top));

// ---------------------------------------------------------------------------
// Joysticks: Modell über Steuerelemente drehen / rollen / zoomen
// ---------------------------------------------------------------------------
//
// „Drehen" orbitiert die Kamera um die Modellmitte (Azimut + Höhe) – exakt
// wie die linke Maustaste. „Roll / Zoom" rollt um die Blickachse (die dritte
// Drehachse, die der Orbit nicht abdeckt) und zoomt rein/raus. Beide wirken
// als Ratensteuerung: je weiter der Knopf ausgelenkt ist, desto schneller die
// Bewegung; loslassen stoppt. Ein Ansichts-Preset/Zentrieren setzt den Roll
// (camera.up) wieder auf senkrecht zurück.

const joyState = {
  rotate: { x: 0, y: 0 },
  rollzoom: { x: 0, y: 0 },
};
const JOY_RADIUS = 25;     // max. Pixel-Auslenkung des Knopfs
const RATE_ORBIT = 0.045;  // rad/Frame bei voller Auslenkung
const RATE_ROLL = 0.035;   // rad/Frame
const RATE_ZOOM = 0.03;    // Distanz-Anteil/Frame

function bindJoystick(id, stateKey) {
  const pad = $(id);
  if (!pad) return;
  const knob = pad.querySelector('.joy-knob');
  const st = joyState[stateKey];
  let active = false;

  const move = (e) => {
    const r = pad.getBoundingClientRect();
    let dx = e.clientX - (r.left + r.width / 2);
    let dy = e.clientY - (r.top + r.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > JOY_RADIUS) { dx *= JOY_RADIUS / len; dy *= JOY_RADIUS / len; }
    knob.style.transform = `translate(${dx}px, ${dy}px)`;
    st.x = dx / JOY_RADIUS;
    st.y = dy / JOY_RADIUS;   // y > 0 = Knopf unten
  };
  const reset = () => {
    active = false;
    pad.classList.remove('active');
    knob.style.transform = '';
    st.x = 0; st.y = 0;
  };

  pad.addEventListener('pointerdown', (e) => {
    active = true;
    pad.classList.add('active');
    try { pad.setPointerCapture(e.pointerId); } catch {}
    move(e);
    e.preventDefault();
  });
  pad.addEventListener('pointermove', (e) => { if (active) move(e); });
  pad.addEventListener('pointerup', reset);
  pad.addEventListener('pointercancel', reset);
  pad.addEventListener('lostpointercapture', reset);
}
bindJoystick('joy-rotate', 'rotate');
bindJoystick('joy-rollzoom', 'rollzoom');

const _joyOffset = new THREE.Vector3();
const _joySph = new THREE.Spherical();
const _joyFwd = new THREE.Vector3();

/** Kamera im Welt-Y-Raum um die Modellmitte orbitieren (wie die Maus). */
function orbitBy(dTheta, dPhi) {
  _joyOffset.copy(camera.position).sub(controls.target);
  _joySph.setFromVector3(_joyOffset);
  _joySph.theta -= dTheta;
  _joySph.phi   += dPhi;
  _joySph.phi = Math.max(0.001, Math.min(Math.PI - 0.001, _joySph.phi));
  _joyOffset.setFromSpherical(_joySph);
  camera.position.copy(controls.target).add(_joyOffset);
}

// Mini-Pfeile (Steuerkreuz): pro Klick einen festen Schritt sammeln, der
// dann über mehrere Frames sanft abgearbeitet wird.
const joyNudge = { theta: 0, phi: 0 };
const NUDGE_STEP = THREE.MathUtils.degToRad(15);   // ~15° pro Klick

function nudge(dTheta, dPhi) {
  joyNudge.theta += dTheta;
  joyNudge.phi += dPhi;
}

['view-dpad'].forEach((id) => {
  const pad = $(id);
  if (!pad) return;
  pad.addEventListener('click', (e) => {
    const dir = e.target.closest('.dpad-btn')?.dataset.dir;
    if (!dir) return;
    if (dir === 'left')  nudge(-NUDGE_STEP, 0);
    if (dir === 'right') nudge(+NUDGE_STEP, 0);
    if (dir === 'up')    nudge(0, -NUDGE_STEP);   // nach oben kippen → Höhe steigt
    if (dir === 'down')  nudge(0, +NUDGE_STEP);
  });
});

/** Pro Frame: Joystick-Auslenkung + Pfeil-Schritte in Kamerabewegung umsetzen. */
function applyJoysticks() {
  if (!controls) return;
  const rot = joyState.rotate, rz = joyState.rollzoom;

  // „Drehen"-Joystick: Orbit um die Modellmitte.
  if (rot.x || rot.y) orbitBy(rot.x * RATE_ORBIT, rot.y * RATE_ORBIT);

  // Mini-Pfeile: gesammelte Schritte sanft (20 %/Frame) abarbeiten.
  if (joyNudge.theta || joyNudge.phi) {
    const st = joyNudge.theta * 0.2, sp = joyNudge.phi * 0.2;
    orbitBy(st, sp);
    joyNudge.theta -= st; joyNudge.phi -= sp;
    if (Math.abs(joyNudge.theta) < 1e-4) joyNudge.theta = 0;
    if (Math.abs(joyNudge.phi) < 1e-4) joyNudge.phi = 0;
  }

  // „Roll": camera.up um die Blickachse drehen (dritte Achse).
  if (rz.x) {
    _joyFwd.copy(controls.target).sub(camera.position).normalize();
    camera.up.applyAxisAngle(_joyFwd, rz.x * RATE_ROLL).normalize();
  }
  // „Zoom": Abstand zur Modellmitte verändern (Knopf oben = reinzoomen).
  if (rz.y) {
    _joyOffset.copy(camera.position).sub(controls.target);
    const dist = _joyOffset.length() * (1 + rz.y * RATE_ZOOM);
    const min = camera.near * 2 + 1e-4;
    _joyOffset.setLength(Math.max(min, Math.min(dist, camera.far * 0.5)));
    camera.position.copy(controls.target).add(_joyOffset);
  }
}

window.addEventListener('keydown', (e) => {
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.key === '1') viewPreset(VIEW_PRESETS.front);
  if (e.key === '2') viewPreset(VIEW_PRESETS.side);
  if (e.key === '3') viewPreset(VIEW_PRESETS.top);
});

// Viewer-Tool-Buttons
$('btn-frame').addEventListener('click', frameModel);
$('btn-bbox').addEventListener('click', () => (bboxHelper ? removeBBox() : addBBox()));
$('btn-wireframe').addEventListener('click', () => {
  wireframeOn = !wireframeOn;
  modelObject?.traverse((n) => {
    if (n.isMesh && !n.userData.isHighlight) {
      const mats = Array.isArray(n.material) ? n.material : [n.material];
      mats.forEach((m) => { if (m) m.wireframe = wireframeOn; });
    }
  });
});

// ---------------------------------------------------------------------------
// Segmentierung
// ---------------------------------------------------------------------------
//
// Auswahl per Rechteck / Kreis / Polygon im Screen-Space: jedes Dreieck,
// dessen Schwerpunkt (projiziert) in der Form liegt, wird ausgewählt –
// bewusst "durch" das Modell hindurch (Vorder- UND Rückseite), damit z. B.
// ein Reifen aus einer Ansicht komplett erfasst und aus anderen Ansichten
// mit den Modi +/−/∩ verfeinert werden kann.

const SEGMENT_COLORS = ['#ff6b35', '#3ddc84', '#ffd166', '#ef476f', '#06d6a0',
                        '#9b5de5', '#00bbf9', '#f15bb5'];

let meshList = [];            // [{mesh, key, triCount, centroids}]
let selection = null;         // {meshIdx: Uint8Array} – aktuelle Arbeitsauswahl
let selectionHighlights = []; // Highlight-Meshes der Arbeitsauswahl
let currentTool = 'nav';      // nav | rect | circle | poly
let currentMode = 'new';      // new | add | sub | int

// --- Undo/Redo (Cmd/Strg+Z, Shift für Redo) ---
//
// Snapshot-basiert: vor jeder Mutation wird der Zustand von Auswahl,
// Segmenten und Skelett RLE-komprimiert auf den Stack gelegt. Die aktive
// Auswahl-Geste (Anfasspunkte) wird beim Wiederherstellen verworfen.

const UNDO_LIMIT = 30;
const undoStack = [];
const redoStack = [];

function serializeSelMap(m) {
  const o = {};
  for (const mi in m) {
    const r = rleEncode(m[mi]);
    if (r.length) o[mi] = r;
  }
  return o;
}

function deserializeSelMap(o) {
  const m = {};
  for (const mi in o) {
    const entry = meshList[mi];
    if (entry) m[mi] = rleDecode(o[mi], entry.triCount);
  }
  return m;
}

function snapshotState() {
  return {
    selection: selection ? serializeSelMap(selection) : null,
    segments: state.segments.map((seg) => ({
      name: seg.name, color: seg.color, visible: seg.visible, locked: !!seg.locked,
      gesture: seg.gesture, sel: serializeSelMap(seg.sel),
    })),
    skeleton: JSON.parse(JSON.stringify({
      bones: skeleton.bones,
      animations: skeleton.animations,
      poses: skeleton.poses,
      defaultPoseId: skeleton.defaultPoseId,
    })),
  };
}

/** Vor jeder Mutation aufrufen – macht den aktuellen Zustand rückholbar. */
function pushUndo() {
  undoStack.push(snapshotState());
  if (undoStack.length > UNDO_LIMIT) undoStack.shift();
  redoStack.length = 0;
}

function clearUndoHistory() {
  undoStack.length = 0;
  redoStack.length = 0;
}

function restoreSnapshot(s) {
  // Segmente
  state.segments.forEach((x) => removeHighlights(x.highlights));
  state.segments = s.segments.map((d) => ({
    name: d.name, color: d.color, visible: d.visible, locked: !!d.locked,
    gesture: d.gesture, sel: deserializeSelMap(d.sel), highlights: [],
  }));
  state.segments.forEach((seg) => rebuildSegmentHighlights(seg));
  invalidateSegGeomCache();

  // Skelett / Knochen
  stopAnimPreview();
  skeleton = JSON.parse(JSON.stringify(s.skeleton));
  if (!skeleton.bones) skeleton = { bones: [], animations: [], poses: [], defaultPoseId: null };
  if (!skeleton.poses) { skeleton.poses = []; skeleton.defaultPoseId = null; }
  posePos = {};
  if (!jointById(selectedJointId)) selectedJointId = null;
  if (activeSegName && !segmentByName(activeSegName)) activeSegName = null;

  // Auswahl (Geste/Anfasspunkte werden nicht wiederhergestellt)
  activeGesture = null;
  editingSegmentIndex = null;
  removeDepthHandles();
  selection = s.selection ? deserializeSelMap(s.selection) : null;
  rebuildSelectionHighlight();
  updateSelectionInfo();

  renderSegmentList();
  renderSkeletonUi();
  if (skeletonTabActive) buildRig(); else disposeRig();
  state.dirty = true;
}

function undo() {
  if (!undoStack.length) { toast('Nichts rückgängig zu machen', true); return; }
  redoStack.push(snapshotState());
  restoreSnapshot(undoStack.pop());
  toast('Rückgängig gemacht');
}

function redo() {
  if (!redoStack.length) { toast('Nichts wiederherzustellen', true); return; }
  undoStack.push(snapshotState());
  restoreSnapshot(redoStack.pop());
  toast('Wiederhergestellt');
}

window.addEventListener('keydown', (e) => {
  if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  e.preventDefault();
  if (e.shiftKey) redo(); else undo();
});

function buildMeshRegistry() {
  meshList = [];
  modelObject?.traverse((n) => {
    if (n.isMesh && n.geometry?.attributes?.position && !n.userData.isHighlight) {
      const g = n.geometry;
      const triCount = Math.floor((g.index ? g.index.count : g.attributes.position.count) / 3);
      if (triCount > 0) {
        meshList.push({ mesh: n, key: meshList.length + ':' + (n.name || 'mesh'), triCount, centroids: null });
      }
    }
  });
}

function resetSegmentation() {
  centroidHash = null;
  checkedSegments.clear();
  clearSelection();
  state.segments.forEach((s) => removeHighlights(s.highlights));
  state.segments = [];
  meshList = [];
  renderSegmentList();
}

/** Dreiecks-Schwerpunkte im lokalen Mesh-Raum vorberechnen (einmal pro Mesh). */
function ensureCentroids(entry) {
  if (entry.centroids) return;
  const g = entry.mesh.geometry;
  const pos = g.attributes.position;
  const index = g.index;
  const cs = new Float32Array(entry.triCount * 3);
  for (let t = 0; t < entry.triCount; t++) {
    let x = 0, y = 0, z = 0;
    for (let k = 0; k < 3; k++) {
      const vi = index ? index.getX(t * 3 + k) : t * 3 + k;
      x += pos.getX(vi); y += pos.getY(vi); z += pos.getZ(vi);
    }
    cs[t * 3] = x / 3; cs[t * 3 + 1] = y / 3; cs[t * 3 + 2] = z / 3;
  }
  entry.centroids = cs;
}

// --- Gesten-basierte Auswahl mit nachträglich einstellbarer Tiefe ---
//
// Jede gezeichnete Form wird als "Geste" gespeichert: Treffer-Maske + Tiefe
// jedes Dreiecks entlang der Blickrichtung beim Zeichnen. Zwei Anfasspunkte
// im Viewer begrenzen den Tiefenbereich [d0, d1] – so lässt sich z. B. ein
// Kreis um den Reifen nachträglich auf die Reifentiefe beschneiden, ohne
// die Gegenseite des Modells zu erfassen.

let activeGesture = null;       // s. applyShapeGesture()
let editingSegmentIndex = null; // Index des Segments, dessen Geste bearbeitet wird

function shapeTestFn(def) {
  switch (def.type) {
    case 'rect':
      return (x, y) => x >= def.minX && x <= def.maxX && y >= def.minY && y <= def.maxY;
    case 'circle': {
      const r2 = def.r * def.r;
      return (x, y) => {
        const dx = x - def.cx, dy = y - def.cy;
        return dx * dx + dy * dy <= r2;
      };
    }
    case 'poly':
      return (x, y) => pointInPolygon(x, y, def.points);
  }
  return () => false;
}

function shapeCenter(def) {
  if (def.type === 'rect') return { x: (def.minX + def.maxX) / 2, y: (def.minY + def.maxY) / 2 };
  if (def.type === 'circle') return { x: def.cx, y: def.cy };
  const c = { x: 0, y: 0 };
  def.points.forEach((p) => { c.x += p.x; c.y += p.y; });
  c.x /= def.points.length; c.y /= def.points.length;
  return c;
}

function liveProjectionCtx() {
  scene.updateMatrixWorld(true);
  return {
    viewProj: new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
    camPos: camera.getWorldPosition(new THREE.Vector3()),
    camDir: camera.getWorldDirection(new THREE.Vector3()),
    w: viewerEl.clientWidth, h: viewerEl.clientHeight,
  };
}

/** Treffer-Masken + Tiefen (entlang ctx.camDir) für eine Form berechnen. */
function computeHitsAndDepths(def, ctx) {
  const test = shapeTestFn(def);
  const hits = {}, depths = {};
  let dMin = Infinity, dMax = -Infinity;
  const mvp = new THREE.Matrix4();
  const cp = ctx.camPos, cd = ctx.camDir;

  meshList.forEach((entry, mi) => {
    ensureCentroids(entry);
    mvp.multiplyMatrices(ctx.viewProj, entry.mesh.matrixWorld);
    const e = mvp.elements;
    const M = entry.mesh.matrixWorld.elements;
    const cs = entry.centroids;
    let hit = null, dep = null;
    for (let t = 0; t < entry.triCount; t++) {
      const x = cs[t * 3], y = cs[t * 3 + 1], z = cs[t * 3 + 2];
      const pw = e[3] * x + e[7] * y + e[11] * z + e[15];
      if (pw <= 0) continue;
      const sx = ((e[0] * x + e[4] * y + e[8] * z + e[12]) / pw + 1) / 2 * ctx.w;
      const sy = (1 - (e[1] * x + e[5] * y + e[9] * z + e[13]) / pw) / 2 * ctx.h;
      if (!test(sx, sy)) continue;
      if (!hit) {
        hit = new Uint8Array(entry.triCount);
        dep = new Float32Array(entry.triCount);
      }
      hit[t] = 1;
      // Tiefe = Abstand des Welt-Schwerpunkts entlang der Blickrichtung
      const wx = M[0] * x + M[4] * y + M[8] * z + M[12];
      const wy = M[1] * x + M[5] * y + M[9] * z + M[13];
      const wz = M[2] * x + M[6] * y + M[10] * z + M[14];
      const d = (wx - cp.x) * cd.x + (wy - cp.y) * cd.y + (wz - cp.z) * cd.z;
      dep[t] = d;
      if (d < dMin) dMin = d;
      if (d > dMax) dMax = d;
    }
    if (hit) { hits[mi] = hit; depths[mi] = dep; }
  });
  return { hits, depths, dMin, dMax };
}

/** Form anwenden: erzeugt die aktive Geste (volle Tiefe, dann anpassbar). */
function applyShapeGesture(def) {
  if (!meshList.length || !renderer) return;
  const ctx = liveProjectionCtx();
  const { hits, depths, dMin, dMax } = computeHitsAndDepths(def, ctx);
  if (dMin === Infinity) {
    toast('Keine Geometrie in der Form getroffen', true);
    return;
  }
  pushUndo();
  const base = {};
  if (currentMode !== 'new' && selection) {
    for (const mi in selection) base[mi] = selection[mi].slice();
  }
  // Tiefen-Achse: Strahl von der Kamera durch das Zentrum der Form
  const c = shapeCenter(def);
  const ndc = new THREE.Vector3((c.x / ctx.w) * 2 - 1, -((c.y / ctx.h) * 2 - 1), 0.5);
  const p = ndc.applyMatrix4(ctx.viewProj.clone().invert());
  const axisDir = p.sub(ctx.camPos).normalize();

  activeGesture = {
    mode: currentMode, shape: def, base, hits, depths,
    d0: dMin, d1: dMax, dMin, dMax,
    camPos: ctx.camPos, camDir: ctx.camDir, axisDir,
    viewProj: ctx.viewProj, viewport: { w: ctx.w, h: ctx.h },
  };
  recombineGesture(true);
  updateDepthHandles();
}

/** Basis + (Treffer ∧ Tiefenbereich) gemäß Modus zur Auswahl kombinieren. */
function recombineGesture(rebuildNow) {
  const g = activeGesture;
  if (!g) return;
  const out = {};
  const keys = new Set([...Object.keys(g.base), ...Object.keys(g.hits)]);
  for (const mi of keys) {
    const entry = meshList[mi];
    if (!entry) continue;
    const base = g.base[mi], hits = g.hits[mi], dep = g.depths[mi];
    const o = new Uint8Array(entry.triCount);
    const lo = g.d0 - 1e-6, hi = g.d1 + 1e-6;
    for (let t = 0; t < entry.triCount; t++) {
      const b = base ? base[t] : 0;
      const h = (hits && hits[t] && dep[t] >= lo && dep[t] <= hi) ? 1 : 0;
      let v = 0;
      switch (g.mode) {
        case 'new': v = h; break;
        case 'add': v = b | h; break;
        case 'sub': v = b & (h ? 0 : 1); break;
        case 'int': v = b & h; break;
      }
      o[t] = v;
    }
    out[mi] = o;
  }
  selection = out;
  updateSelectionInfo();
  scheduleHighlightRebuild(rebuildNow);
}

// Highlight-Rebuild drosseln (beim Ziehen der Anfasspunkte)
let highlightTimer = null;
function scheduleHighlightRebuild(now) {
  if (now) {
    clearTimeout(highlightTimer);
    highlightTimer = null;
    rebuildSelectionHighlight();
    return;
  }
  if (highlightTimer) return;
  highlightTimer = setTimeout(() => {
    highlightTimer = null;
    rebuildSelectionHighlight();
  }, 150);
}

// --- Tiefen-Anfasspunkte ---

let handleGroup = null;   // Gruppe mit Linie + 2 Kugeln
let dragHandleIndex = null;
const handleRaycaster = new THREE.Raycaster();

function depthToPoint(g, d) {
  const k = Math.max(g.axisDir.dot(g.camDir), 1e-6);
  return g.camPos.clone().add(g.axisDir.clone().multiplyScalar(d / k));
}

function handleSize() {
  modelRoot.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(modelRoot);
  if (box.isEmpty()) return 0.02;
  return box.getSize(new THREE.Vector3()).length() * 0.015;
}

function removeDepthHandles() {
  if (handleGroup) {
    scene.remove(handleGroup);
    handleGroup.traverse((n) => { n.geometry?.dispose(); n.material?.dispose(); });
    handleGroup = null;
  }
  dragHandleIndex = null;
}

function updateDepthHandles() {
  removeDepthHandles();
  const g = activeGesture;
  if (!g || !renderer) return;
  const size = handleSize();
  handleGroup = new THREE.Group();

  const mkSphere = (d, color) => {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(size, 20, 14),
      new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 })
    );
    m.position.copy(depthToPoint(g, d));
    m.renderOrder = 10;
    return m;
  };
  const near = mkSphere(g.d0, '#ffd166');  // vordere Tiefengrenze
  const far = mkSphere(g.d1, '#4c8dff');   // hintere Tiefengrenze
  near.userData.handleIndex = 0;
  far.userData.handleIndex = 1;

  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([near.position, far.position]),
    new THREE.LineBasicMaterial({ color: '#ff6b35', depthTest: false, transparent: true, opacity: 0.8 })
  );
  line.renderOrder = 9;

  handleGroup.add(line, near, far);
  handleGroup.userData.handles = [near, far];
  scene.add(handleGroup);
}

function refreshHandlePositions() {
  if (!handleGroup || !activeGesture) return;
  const g = activeGesture;
  const [near, far] = handleGroup.userData.handles;
  near.position.copy(depthToPoint(g, g.d0));
  far.position.copy(depthToPoint(g, g.d1));
  handleGroup.children[0].geometry.setFromPoints([near.position, far.position]);
}

function pickHandle(e) {
  if (!handleGroup || !renderer) return null;
  const r = renderer.domElement.getBoundingClientRect();
  handleRaycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  ), camera);
  const hits = handleRaycaster.intersectObjects(handleGroup.userData.handles, false);
  return hits.length ? hits[0].object : null;
}

/** Maus-Strahl auf die Tiefen-Achse projizieren → Tiefe in Metern. */
function mouseDepthOnAxis(e) {
  const g = activeGesture;
  const r = renderer.domElement.getBoundingClientRect();
  handleRaycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  ), camera);
  const A = g.camPos, u = g.axisDir;
  const B = handleRaycaster.ray.origin, v = handleRaycaster.ray.direction;
  const w0 = A.clone().sub(B);
  const b = u.dot(v), d = u.dot(w0), eDot = v.dot(w0);
  const denom = 1 - b * b;
  if (Math.abs(denom) < 1e-6) return null;   // Blick fast entlang der Achse
  const s = (b * eDot - d) / denom;          // Parameter auf der Achse
  return s * u.dot(g.camDir);
}

// Capture-Phase auf dem Viewer: Anfasspunkte haben Vorrang vor OrbitControls
// und den Zeichenwerkzeugen – funktionieren dadurch in JEDEM Werkzeug.
viewerEl.addEventListener('pointerdown', (e) => {
  if (e.button !== 0) return;
  const h = pickHandle(e);
  if (!h) return;
  e.stopPropagation();
  e.preventDefault();
  pushUndo();
  dragHandleIndex = h.userData.handleIndex;
  viewerEl.setPointerCapture(e.pointerId);
}, true);

viewerEl.addEventListener('pointermove', (e) => {
  if (dragHandleIndex === null) {
    // Hover-Feedback
    if (handleGroup && pickHandle(e)) viewerEl.style.cursor = 'ew-resize';
    else if (viewerEl.style.cursor === 'ew-resize') {
      viewerEl.style.cursor = currentTool === 'nav' ? 'grab' : 'crosshair';
    }
    return;
  }
  e.stopPropagation();
  const g = activeGesture;
  if (!g) return;
  const depth = mouseDepthOnAxis(e);
  if (depth === null) return;
  const pad = (g.dMax - g.dMin) * 0.05 + 1e-4;
  if (dragHandleIndex === 0) {
    g.d0 = Math.min(Math.max(depth, g.dMin - pad), g.d1);
  } else {
    g.d1 = Math.max(Math.min(depth, g.dMax + pad), g.d0);
  }
  refreshHandlePositions();
  recombineGesture(false);
}, true);

viewerEl.addEventListener('pointerup', (e) => {
  if (dragHandleIndex === null) return;
  e.stopPropagation();
  dragHandleIndex = null;
  recombineGesture(true);   // finaler, ungedrosselter Rebuild
}, true);

function selectionCount(selMap) {
  let n = 0;
  for (const mi in selMap) {
    const sel = selMap[mi];
    for (let i = 0; i < sel.length; i++) n += sel[i];
  }
  return n;
}

function updateSelectionInfo() {
  const n = selection ? selectionCount(selection) : 0;
  let text = n ? `Auswahl: ${n.toLocaleString('de-DE')} Dreiecke` : 'Keine Auswahl';
  if (n && activeGesture) {
    text += ` · Tiefe ${fmtLen(activeGesture.d1 - activeGesture.d0)}`;
  }
  $('seg-status').textContent = text;
}

function clearSelection(updateUi = true) {
  selection = null;
  removeHighlights(selectionHighlights);
  selectionHighlights = [];
  activeGesture = null;
  editingSegmentIndex = null;
  removeDepthHandles();
  clearGroupHover();
  if (updateUi) updateSelectionInfo();
}

// --- Highlight-Darstellung ---

/** Baut für eine Auswahl pro Mesh ein Overlay-Mesh (Kind des Quell-Meshes). */
function buildHighlights(selMap, colorHex, opacity) {
  const result = [];
  for (const mi in selMap) {
    const entry = meshList[mi];
    if (!entry) continue;
    const sel = selMap[mi];
    const g = entry.mesh.geometry;
    const pos = g.attributes.position;
    const index = g.index;

    let count = 0;
    for (let i = 0; i < sel.length; i++) count += sel[i];
    if (!count) continue;

    const out = new Float32Array(count * 9);
    let o = 0;
    for (let t = 0; t < sel.length; t++) {
      if (!sel[t]) continue;
      for (let k = 0; k < 3; k++) {
        const vi = index ? index.getX(t * 3 + k) : t * 3 + k;
        out[o++] = pos.getX(vi); out[o++] = pos.getY(vi); out[o++] = pos.getZ(vi);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(out, 3));
    const mat = new THREE.MeshBasicMaterial({
      color: colorHex, transparent: true, opacity,
      depthTest: true, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
    const hl = new THREE.Mesh(geo, mat);
    hl.userData.isHighlight = true;
    hl.renderOrder = 1;
    entry.mesh.add(hl);   // erbt automatisch die Mesh-Transformation
    result.push(hl);
  }
  return result;
}

function removeHighlights(list) {
  (list || []).forEach((hl) => {
    hl.parent?.remove(hl);
    hl.geometry.dispose();
    hl.material.dispose();
  });
}

function rebuildSelectionHighlight() {
  removeHighlights(selectionHighlights);
  selectionHighlights = selection ? buildHighlights(selection, '#ff6b35', 0.55) : [];
}

function rebuildSegmentHighlights(seg) {
  removeHighlights(seg.highlights);
  seg.highlights = seg.visible ? buildHighlights(seg.sel, seg.color, 0.4) : [];
}

// --- Werkzeuge & Zeichnen ---

const overlay = $('select-overlay');
const octx = overlay.getContext('2d');
let drag = null;        // {x0, y0, x1, y1} für Rechteck/Kreis
let polyPoints = [];    // Polygon-Punkte

function syncOverlaySize() {
  overlay.width = viewerEl.clientWidth;
  overlay.height = viewerEl.clientHeight;
}
new ResizeObserver(syncOverlaySize).observe(viewerEl);
syncOverlaySize();

function setTool(tool) {
  currentTool = tool;
  cancelPolygon();
  drag = null;
  drawOverlay();
  if (tool !== 'group') clearGroupHover();
  document.querySelectorAll('.tool-btn').forEach((b) =>
    b.classList.toggle('active', b.id === 'tool-' + tool));
  if (controls) {
    if (tool === 'nav') {
      controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
      controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    } else {
      // Linke Taste zeichnet/wählt, rechte Taste orbitiert weiter
      controls.mouseButtons.LEFT = null;
      controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    }
  }
  viewerEl.style.cursor =
    tool === 'nav' ? 'grab' : (tool === 'group' ? 'pointer' : 'crosshair');
  if (tool === 'group') updateGroupStatus();
}

['nav', 'rect', 'circle', 'poly', 'group'].forEach((tool) => {
  $('tool-' + tool).addEventListener('click', () => setTool(tool));
});

document.querySelectorAll('.mode-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    currentMode = btn.dataset.mode;
    document.querySelectorAll('.mode-btn').forEach((b) =>
      b.classList.toggle('active', b === btn));
  });
});

function pointerPos(e) {
  const r = renderer?.domElement.getBoundingClientRect()
    ?? viewerEl.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

function drawOverlay() {
  octx.clearRect(0, 0, overlay.width, overlay.height);
  octx.strokeStyle = '#ff6b35';
  octx.fillStyle = 'rgba(255, 107, 53, 0.12)';
  octx.lineWidth = 1.5;
  octx.setLineDash([6, 4]);

  if (drag && currentTool === 'rect') {
    const x = Math.min(drag.x0, drag.x1), y = Math.min(drag.y0, drag.y1);
    const w = Math.abs(drag.x1 - drag.x0), h = Math.abs(drag.y1 - drag.y0);
    octx.fillRect(x, y, w, h);
    octx.strokeRect(x, y, w, h);
  } else if (drag && currentTool === 'circle') {
    const r = Math.hypot(drag.x1 - drag.x0, drag.y1 - drag.y0);
    octx.beginPath();
    octx.arc(drag.x0, drag.y0, r, 0, Math.PI * 2);
    octx.fill();
    octx.stroke();
  } else if (polyPoints.length) {
    octx.beginPath();
    octx.moveTo(polyPoints[0].x, polyPoints[0].y);
    polyPoints.slice(1).forEach((p) => octx.lineTo(p.x, p.y));
    if (polyPoints.mouse) octx.lineTo(polyPoints.mouse.x, polyPoints.mouse.y);
    octx.stroke();
    octx.setLineDash([]);
    polyPoints.forEach((p, i) => {
      octx.beginPath();
      octx.arc(p.x, p.y, i === 0 ? 6 : 3.5, 0, Math.PI * 2);
      octx.fillStyle = i === 0 ? '#ffd166' : '#ff6b35';
      octx.fill();
    });
  }
}

function pointInPolygon(x, y, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function cancelPolygon() {
  polyPoints = [];
  drawOverlay();
}

function closePolygon() {
  if (polyPoints.length >= 3) {
    applyShapeGesture({
      type: 'poly',
      points: polyPoints.map((p) => ({ x: p.x, y: p.y })),
    });
  }
  cancelPolygon();
}

function bindSelectionPointerEvents(el) {
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || currentTool === 'nav' || !modelObject) return;
    const p = pointerPos(e);
    if (currentTool === 'rect' || currentTool === 'circle') {
      drag = { x0: p.x, y0: p.y, x1: p.x, y1: p.y };
      el.setPointerCapture(e.pointerId);
    } else if (currentTool === 'poly') {
      // Klick nahe am Startpunkt schließt das Polygon
      if (polyPoints.length >= 3 &&
          Math.hypot(p.x - polyPoints[0].x, p.y - polyPoints[0].y) < 12) {
        closePolygon();
      } else {
        polyPoints.push(p);
        drawOverlay();
      }
    }
  });

  el.addEventListener('pointermove', (e) => {
    if (currentTool === 'nav') return;
    const p = pointerPos(e);
    if (drag) {
      drag.x1 = p.x; drag.y1 = p.y;
      drawOverlay();
    } else if (currentTool === 'poly' && polyPoints.length) {
      polyPoints.mouse = p;
      drawOverlay();
    }
  });

  el.addEventListener('pointerup', (e) => {
    if (e.button !== 0 || !drag) return;
    const d = drag;
    drag = null;
    drawOverlay();
    const dist = Math.hypot(d.x1 - d.x0, d.y1 - d.y0);
    if (dist < 4) return;  // Mini-Klick: keine Auswahl
    if (currentTool === 'rect') {
      applyShapeGesture({
        type: 'rect',
        minX: Math.min(d.x0, d.x1), maxX: Math.max(d.x0, d.x1),
        minY: Math.min(d.y0, d.y1), maxY: Math.max(d.y0, d.y1),
      });
    } else if (currentTool === 'circle') {
      applyShapeGesture({ type: 'circle', cx: d.x0, cy: d.y0, r: dist });
    }
  });

  el.addEventListener('dblclick', () => {
    if (currentTool === 'poly') closePolygon();
  });
}
if (renderer) bindSelectionPointerEvents(renderer.domElement);

// ---------------------------------------------------------------------------
// Werkzeug „Teile gruppieren": ganze Mesh-Teile (USD-Prims) per Hover/Klick
// in die Auswahl aufnehmen und mit „✓ Segment" zu einem Segment zusammenfassen.
// ---------------------------------------------------------------------------

const groupRaycaster = new THREE.Raycaster();
let groupHoverIdx = -1;
let groupHoverHl = [];

/** Index des Mesh-Teils unter dem Cursor (oder -1). */
function pickMeshPart(e) {
  if (!renderer || !meshList.length) return -1;
  const r = renderer.domElement.getBoundingClientRect();
  groupRaycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  ), camera);
  const hits = groupRaycaster.intersectObjects(meshList.map((m) => m.mesh), false);
  for (const h of hits) {
    if (h.object.userData.isHighlight) continue;
    const mi = meshList.findIndex((m) => m.mesh === h.object);
    if (mi >= 0) return mi;
  }
  return -1;
}

function clearGroupHover() {
  removeHighlights(groupHoverHl);
  groupHoverHl = [];
  groupHoverIdx = -1;
}

/** Ist das gesamte Mesh-Teil mi schon in der Auswahl? */
function isPartSelected(mi) {
  const a = selection && selection[mi];
  if (!a) return false;
  for (let i = 0; i < a.length; i++) if (!a[i]) return false;
  return true;
}

function selectedPartCount() {
  if (!selection) return 0;
  let n = 0;
  for (const mi in selection) if (isPartSelected(mi)) n++;
  return n;
}

function updateGroupStatus() {
  if (currentTool !== 'group') return;
  const n = selectedPartCount();
  $('seg-status').textContent = n
    ? `${n} Teil${n === 1 ? '' : 'e'} ausgewählt – „✓ Segment" fasst zusammen`
    : 'Teil anklicken zum Auswählen';
}

function setGroupHover(mi) {
  if (mi === groupHoverIdx) return;
  removeHighlights(groupHoverHl);
  groupHoverHl = [];
  groupHoverIdx = mi;
  // Bereits ausgewählte Teile tragen schon ihr Auswahl-Highlight
  if (mi >= 0 && !isPartSelected(mi)) {
    const entry = meshList[mi];
    const full = new Uint8Array(entry.triCount).fill(1);
    groupHoverHl = buildHighlights({ [mi]: full }, '#ffd166', 0.35);
  }
}

/** Mesh-Teil in der Auswahl umschalten (ganzes Teil rein/raus). */
function togglePart(mi) {
  const entry = meshList[mi];
  if (!entry) return;
  pushUndo();
  if (!selection) selection = {};
  if (isPartSelected(mi)) delete selection[mi];
  else selection[mi] = new Uint8Array(entry.triCount).fill(1);
  // aktive Zeichen-Geste/Tiefen-Anfasspunkte gelten hier nicht mehr
  activeGesture = null;
  editingSegmentIndex = null;
  removeDepthHandles();
  clearGroupHover();
  rebuildSelectionHighlight();
  updateSelectionInfo();
  updateGroupStatus();
  state.dirty = true;
}

function bindGroupPointerEvents(el) {
  let downPos = null;
  el.addEventListener('pointermove', (e) => {
    if (currentTool !== 'group') return;
    setGroupHover(pickMeshPart(e));
  });
  el.addEventListener('pointerleave', () => {
    if (currentTool === 'group') setGroupHover(-1);
  });
  el.addEventListener('pointerdown', (e) => {
    if (currentTool !== 'group' || e.button !== 0) return;
    downPos = { x: e.clientX, y: e.clientY };
  });
  el.addEventListener('pointerup', (e) => {
    if (currentTool !== 'group' || e.button !== 0 || !downPos) return;
    const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
    downPos = null;
    if (moved > 4) return;        // war ein Ziehen (Orbit), kein Klick
    const mi = pickMeshPart(e);
    if (mi >= 0) togglePart(mi);
  });
}
if (renderer) bindGroupPointerEvents(renderer.domElement);

window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') cancelPolygon();
  if (e.key === 'Enter' && currentTool === 'poly') closePolygon();
});

// --- Segment-Verwaltung ---

$('btn-seg-clear').addEventListener('click', () => {
  if (selection || activeGesture) pushUndo();
  clearSelection();
});

/** Aktive Geste serialisierbar machen (für Persistenz + spätere Bearbeitung). */
function serializeGesture() {
  const g = activeGesture;
  if (!g) return null;
  const base = {};
  for (const mi in g.base) {
    const entry = meshList[mi];
    const ranges = rleEncode(g.base[mi]);
    if (entry && ranges.length) base[entry.key] = ranges;
  }
  return {
    mode: g.mode, shape: g.shape,
    d0: g.d0, d1: g.d1,
    camPos: g.camPos.toArray(), camDir: g.camDir.toArray(),
    axisDir: g.axisDir.toArray(),
    viewProj: g.viewProj.toArray(),
    viewport: g.viewport,
    base,
  };
}

/** Gespeicherte Geste eines Segments reaktivieren (Tiefe nachbearbeiten). */
function editSegmentGesture(i) {
  const seg = state.segments[i];
  if (!seg?.gesture) {
    toast('Für dieses Segment ist keine Auswahl-Geste gespeichert', true);
    return;
  }
  const sg = seg.gesture;
  const ctx = {
    viewProj: new THREE.Matrix4().fromArray(sg.viewProj),
    camPos: new THREE.Vector3().fromArray(sg.camPos),
    camDir: new THREE.Vector3().fromArray(sg.camDir),
    w: sg.viewport.w, h: sg.viewport.h,
  };
  scene.updateMatrixWorld(true);
  const { hits, depths, dMin, dMax } = computeHitsAndDepths(sg.shape, ctx);
  if (dMin === Infinity) {
    toast('Geste passt nicht mehr zum Modell', true);
    return;
  }
  pushUndo();
  const base = {};
  for (const key in sg.base || {}) {
    const mi = meshList.findIndex((m) => m.key === key);
    if (mi >= 0) base[mi] = rleDecode(sg.base[key], meshList[mi].triCount);
  }
  activeGesture = {
    mode: sg.mode, shape: sg.shape, base, hits, depths,
    d0: sg.d0, d1: sg.d1, dMin, dMax,
    camPos: ctx.camPos, camDir: ctx.camDir,
    axisDir: new THREE.Vector3().fromArray(sg.axisDir),
    viewProj: ctx.viewProj, viewport: sg.viewport,
  };
  editingSegmentIndex = i;
  recombineGesture(true);
  updateDepthHandles();
  toast(`"${seg.name}" wird bearbeitet – Tiefe per Anfasspunkte ziehen, „✓ Segment" überschreibt`);
}

$('btn-seg-save').addEventListener('click', () => {
  if (!selection || !selectionCount(selection)) {
    toast('Erst eine Auswahl treffen (Rechteck, Kreis oder Polygon)', true);
    return;
  }
  const editing = editingSegmentIndex;
  const defaultName = editing !== null
    ? state.segments[editing].name
    : 'Segment ' + (state.segments.length + 1);
  const name = prompt('Name des Segments (z. B. "Reifen vorne links"):', defaultName);
  if (!name) return;
  pushUndo();

  const sel = {};
  for (const mi in selection) {
    if (selection[mi].some((v) => v)) sel[mi] = selection[mi].slice();
  }
  const gesture = serializeGesture();

  if (editing !== null && state.segments[editing]) {
    const seg = state.segments[editing];
    removeHighlights(seg.highlights);
    invalidateSegGeomCache(seg.name);
    seg.name = name.trim();
    seg.sel = sel;
    seg.gesture = gesture;
    seg.highlights = [];
    rebuildSegmentHighlights(seg);
    toast(`Segment "${seg.name}" aktualisiert – Speichern nicht vergessen`);
  } else {
    const seg = {
      name: name.trim(),
      color: SEGMENT_COLORS[state.segments.length % SEGMENT_COLORS.length],
      visible: true,
      sel,
      gesture,
      highlights: [],
    };
    state.segments.push(seg);
    rebuildSegmentHighlights(seg);
    toast(`Segment "${seg.name}" angelegt – Speichern nicht vergessen`);
  }
  clearSelection();
  state.dirty = true;
  renderSegmentList();
});

/** Welt-Bounding-Box eines Segments (über die Dreiecks-Schwerpunkte). */
function segmentBox(seg) {
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  scene.updateMatrixWorld(true);
  for (const mi in seg.sel) {
    const entry = meshList[mi];
    if (!entry) continue;
    ensureCentroids(entry);
    const sel = seg.sel[mi], cs = entry.centroids;
    const M = entry.mesh.matrixWorld;
    for (let t = 0; t < sel.length; t++) {
      if (!sel[t]) continue;
      v.set(cs[t * 3], cs[t * 3 + 1], cs[t * 3 + 2]).applyMatrix4(M);
      box.expandByPoint(v);
    }
  }
  return box;
}

/** Klick auf ein Segment: einblenden, hinzoomen, kurz aufblinken lassen. */
function focusSegment(i) {
  const seg = state.segments[i];
  if (!seg) return;
  if (!seg.visible) {
    seg.visible = true;
    rebuildSegmentHighlights(seg);
    renderSegmentList();
  }
  const box = segmentBox(seg);
  if (!box.isEmpty()) frameBox(box.expandByScalar(box.getSize(new THREE.Vector3()).length() * 0.2));
  seg.highlights.forEach((hl) => { hl.material.opacity = 0.85; });
  setTimeout(() => seg.highlights.forEach((hl) => { hl.material.opacity = 0.4; }), 900);
}

const checkedSegments = new Set();   // Namen der markierten Segmente

function renderSegmentList() {
  const ul = $('segment-list');
  ul.innerHTML = '';
  // Markierungen von gelöschten Segmenten entfernen
  const names = new Set(state.segments.map((s) => s.name));
  for (const n of [...checkedSegments]) if (!names.has(n)) checkedSegments.delete(n);

  if (!state.segments.length) {
    ul.innerHTML = '<li class="empty">Noch keine Segmente</li>';
    updateMultiBar();
    return;
  }
  state.segments.forEach((seg, i) => {
    const li = document.createElement('li');
    const count = selectionCount(seg.sel);
    li.innerHTML = `
      <input type="checkbox" class="seg-check" title="Für Mehrfach-Aktionen markieren"
             ${checkedSegments.has(seg.name) ? 'checked' : ''}>
      <span class="seg-color" style="background:${seg.color}"></span>
      <span class="seg-name" title="Doppelklick: umbenennen">${escapeHtml(seg.name)}
        <small>${count.toLocaleString('de-DE')} Dreiecke</small></span>
      <button class="icon-btn" data-act="toggle" title="Ein-/ausblenden">${seg.visible ? '👁' : '◌'}</button>
      <button class="icon-btn ${seg.locked ? 'active' : ''}" data-act="lock" title="${seg.locked ? 'Position gesperrt – bleibt bei Animationen fix. Klick: entsperren (bewegt sich mit).' : 'Bewegt sich mit dem umgebenden animierten Segment. Klick: Position sperren (bleibt fix).'}">${seg.locked ? '🔒' : '🔓'}</button>
      <button class="icon-btn" data-act="edit" title="Geste bearbeiten (Tiefe per Anfasspunkte)" ${seg.gesture ? '' : 'disabled'}>✏</button>
      <button class="icon-btn" data-act="select" title="In Auswahl laden (zum Verfeinern)">↺</button>
      <button class="icon-btn" data-act="delete" title="Segment löschen">🗑</button>`;
    li.addEventListener('click', (e) => {
      if (e.target.closest('.icon-btn') || e.target.closest('.seg-check')) return;
      focusSegment(i);
    });
    li.querySelector('.seg-check').addEventListener('change', (e) => {
      if (e.target.checked) checkedSegments.add(seg.name);
      else checkedSegments.delete(seg.name);
      updateMultiBar();
    });
    li.querySelector('.seg-name').addEventListener('dblclick', () => renameSegment(i));
    li.querySelector('[data-act=toggle]').addEventListener('click', () => {
      seg.visible = !seg.visible;
      rebuildSegmentHighlights(seg);
      renderSegmentList();
    });
    li.querySelector('[data-act=lock]').addEventListener('click', () => {
      pushUndo();
      seg.locked = !seg.locked;
      state.dirty = true;
      renderSegmentList();
      if (skeletonTabActive) buildRig();
      toast(seg.locked
        ? `„${seg.name}" gesperrt – bleibt bei Animationen an seiner Position`
        : `„${seg.name}" entsperrt – bewegt sich mit dem umgebenden Segment mit`);
    });
    li.querySelector('[data-act=edit]').addEventListener('click', () => editSegmentGesture(i));
    li.querySelector('[data-act=select]').addEventListener('click', () => {
      pushUndo();
      clearSelection(false);
      selection = {};
      for (const mi in seg.sel) selection[mi] = seg.sel[mi].slice();
      updateSelectionInfo();
      rebuildSelectionHighlight();
      toast(`"${seg.name}" in Auswahl geladen – mit ✓ als neues Segment speichern`);
    });
    li.querySelector('[data-act=delete]').addEventListener('click', () => {
      if (!confirm(`Segment "${seg.name}" löschen?`)) return;
      pushUndo();
      removeHighlights(seg.highlights);
      invalidateSegGeomCache(seg.name);
      dropBone(seg.name);
      state.segments.splice(i, 1);
      state.dirty = true;
      renderSegmentList();
    });
    ul.appendChild(li);
  });
  updateMultiBar();
}

function renameSegment(i) {
  const seg = state.segments[i];
  if (!seg) return;
  const name = prompt('Segment umbenennen:', seg.name);
  if (!name || name.trim() === seg.name) return;
  pushUndo();
  const newName = name.trim();
  // Knochenlinie und Cache folgen dem neuen Namen
  const bone = boneOfSegment(seg.name);
  if (bone) bone.segment = newName;
  if (activeSegName === seg.name) activeSegName = newName;
  invalidateSegGeomCache(seg.name);
  if (checkedSegments.delete(seg.name)) checkedSegments.add(newName);
  seg.name = newName;
  state.dirty = true;
  renderSegmentList();
  renderSkeletonUi();
}

// --- Mehrfach-Aktionen ---

function checkedIndices() {
  return state.segments
    .map((seg, i) => (checkedSegments.has(seg.name) ? i : -1))
    .filter((i) => i >= 0);
}

function updateMultiBar() {
  const bar = $('segment-multi-bar');
  const n = checkedIndices().length;
  bar.hidden = n === 0;
  $('multi-count').textContent = `${n} markiert`;
}

$('btn-multi-visibility').addEventListener('click', () => {
  checkedIndices().forEach((i) => {
    const seg = state.segments[i];
    seg.visible = !seg.visible;
    rebuildSegmentHighlights(seg);
  });
  renderSegmentList();
});

$('btn-multi-mirror').addEventListener('click', () => {
  const idx = checkedIndices();
  if (idx.length) mirrorSegments(idx);
});

// Markierte Segmente zu einem einzigen zusammenfassen (gruppieren)
$('btn-multi-group').addEventListener('click', () => {
  const idx = checkedIndices();
  if (idx.length < 2) {
    toast('Mindestens zwei Segmente markieren, um sie zu gruppieren', true);
    return;
  }
  const name = prompt(
    `${idx.length} Segmente zu einem zusammenfassen – Name:`,
    state.segments[idx[0]].name);
  if (!name) return;
  pushUndo();

  // Vereinigung aller Auswahlmasken der markierten Segmente
  const merged = {};
  idx.forEach((i) => {
    const seg = state.segments[i];
    for (const mi in seg.sel) {
      const src = seg.sel[mi];
      const dst = merged[mi] || (merged[mi] = new Uint8Array(meshList[mi].triCount));
      for (let t = 0; t < src.length; t++) if (src[t]) dst[t] = 1;
    }
  });

  // Originalsegmente (höchste Indizes zuerst) samt Knochen/Highlights entfernen
  idx.slice().sort((a, b) => b - a).forEach((i) => {
    const seg = state.segments[i];
    removeHighlights(seg.highlights);
    invalidateSegGeomCache(seg.name);
    dropBone(seg.name);
    state.segments.splice(i, 1);
  });

  const seg = {
    name: name.trim(),
    color: SEGMENT_COLORS[state.segments.length % SEGMENT_COLORS.length],
    visible: true,
    sel: merged,
    gesture: null,
    highlights: [],
  };
  state.segments.push(seg);
  rebuildSegmentHighlights(seg);
  checkedSegments.clear();
  state.dirty = true;
  renderSegmentList();
  toast(`${idx.length} Segmente zu „${seg.name}" zusammengefasst – Speichern nicht vergessen`);
});

$('btn-multi-delete').addEventListener('click', () => {
  const idx = checkedIndices();
  if (!idx.length) return;
  if (!confirm(`${idx.length} markierte Segmente löschen?`)) return;
  pushUndo();
  idx.reverse().forEach((i) => {
    const seg = state.segments[i];
    removeHighlights(seg.highlights);
    invalidateSegGeomCache(seg.name);
    dropBone(seg.name);
    state.segments.splice(i, 1);
  });
  state.dirty = true;
  renderSegmentList();
});

// --- Spiegeln (Symmetrie an der X-Achse durch die Modellmitte) ---
//
// Für jedes ausgewählte Dreieck wird der an der YZ-Ebene gespiegelte
// Schwerpunkt gesucht; alle Dreiecke in dessen Umgebung (räumlicher Hash)
// bilden das gespiegelte Segment. Funktioniert auch bei unsymmetrischer
// Tesselierung (KI-Modelle), die Kante darf danach manuell verfeinert werden.

let centroidHash = null;   // {cells: Map<int, number[]>, h, center}

function hashKey(ix, iy, iz) {
  return ((ix + 512) << 20) | ((iy + 512) << 10) | (iz + 512);
}

function ensureCentroidHash() {
  if (centroidHash) return centroidHash;
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const box = new THREE.Box3();
  const v = new THREE.Vector3();

  meshList.forEach((entry) => {
    ensureCentroids(entry);
    const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
    const e = rel.elements;
    const cs = entry.centroids;
    const mc = new Float32Array(cs.length);
    for (let t = 0; t < entry.triCount; t++) {
      const x = cs[t * 3], y = cs[t * 3 + 1], z = cs[t * 3 + 2];
      mc[t * 3] = e[0] * x + e[4] * y + e[8] * z + e[12];
      mc[t * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
      mc[t * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];
      box.expandByPoint(v.set(mc[t * 3], mc[t * 3 + 1], mc[t * 3 + 2]));
    }
    entry.modelCentroids = mc;
  });

  const diag = box.getSize(new THREE.Vector3()).length() || 1;
  const h = diag / 200;
  const cells = new Map();
  meshList.forEach((entry, mi) => {
    const mc = entry.modelCentroids;
    for (let t = 0; t < entry.triCount; t++) {
      const key = hashKey(
        Math.floor(mc[t * 3] / h),
        Math.floor(mc[t * 3 + 1] / h),
        Math.floor(mc[t * 3 + 2] / h));
      let arr = cells.get(key);
      if (!arr) cells.set(key, arr = []);
      arr.push(mi, t);
    }
  });
  centroidHash = { cells, h, center: box.getCenter(new THREE.Vector3()) };
  return centroidHash;
}

function mirrorSelMap(selMap) {
  const { cells, h, center } = ensureCentroidHash();
  const out = {};
  const r2 = (h * 1.5) ** 2;
  for (const mi in selMap) {
    const entry = meshList[mi];
    if (!entry) continue;
    const sel = selMap[mi];
    const mc = entry.modelCentroids;
    for (let t = 0; t < sel.length; t++) {
      if (!sel[t]) continue;
      const qx = 2 * center.x - mc[t * 3];
      const qy = mc[t * 3 + 1], qz = mc[t * 3 + 2];
      const ix = Math.floor(qx / h), iy = Math.floor(qy / h), iz = Math.floor(qz / h);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dz = -1; dz <= 1; dz++) {
            const arr = cells.get(hashKey(ix + dx, iy + dy, iz + dz));
            if (!arr) continue;
            for (let k = 0; k < arr.length; k += 2) {
              const cmi = arr[k], ct = arr[k + 1];
              const cc = meshList[cmi].modelCentroids;
              const ddx = cc[ct * 3] - qx, ddy = cc[ct * 3 + 1] - qy, ddz = cc[ct * 3 + 2] - qz;
              if (ddx * ddx + ddy * ddy + ddz * ddz <= r2) {
                (out[cmi] ||= new Uint8Array(meshList[cmi].triCount))[ct] = 1;
              }
            }
          }
        }
      }
    }
  }
  return out;
}

function mirrorSegments(indices) {
  if (!modelObject) { toast('Kein Modell geladen', true); return; }
  pushUndo();
  toast('Spiegele…');
  setTimeout(() => {
    let added = 0;
    for (const i of indices) {
      const seg = state.segments[i];
      if (!seg) continue;
      const sel = mirrorSelMap(seg.sel);
      if (!Object.keys(sel).length) continue;
      const copy = {
        name: seg.name + ' (gespiegelt)',
        color: SEGMENT_COLORS[state.segments.length % SEGMENT_COLORS.length],
        visible: true, sel, gesture: null, highlights: [],
      };
      state.segments.push(copy);
      rebuildSegmentHighlights(copy);
      added++;
    }
    state.dirty = true;
    renderSegmentList();
    toast(added ? `${added} Segment${added === 1 ? '' : 'e'} gespiegelt` : 'Spiegeln ergab keine Treffer', !added);
  }, 30);
}

// --- Teile aus dem USDZ erkennen / alle Segmente löschen ---

/** Benannte Meshes (USD-Prims) des Modells als Segmente übernehmen. */
function detectSegmentsFromModel() {
  if (!meshList.length) {
    toast('Kein Modell geladen', true);
    return;
  }
  // Meshes mit gleichem Namen zu einem Segment zusammenfassen
  const groups = new Map();
  meshList.forEach((entry, mi) => {
    const raw = (entry.mesh.name || '').trim();
    const label = raw || 'Teil ' + (mi + 1);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(mi);
  });

  const existing = new Set(state.segments.map((s) => s.name));
  pushUndo();
  let added = 0;
  for (const [label, indices] of groups) {
    if (existing.has(label)) continue;
    const sel = {};
    indices.forEach((mi) => {
      sel[mi] = new Uint8Array(meshList[mi].triCount).fill(1);
    });
    const seg = {
      name: label,
      color: SEGMENT_COLORS[state.segments.length % SEGMENT_COLORS.length],
      visible: true,
      sel,
      gesture: null,   // strukturbasiert, keine Zeichen-Geste
      highlights: [],
    };
    state.segments.push(seg);
    rebuildSegmentHighlights(seg);
    added++;
  }
  if (added) {
    state.dirty = true;
    renderSegmentList();
    toast(`${added} Teil${added === 1 ? '' : 'e'} aus dem USDZ übernommen – Speichern nicht vergessen`);
  } else {
    toast(groups.size
      ? 'Alle Teile sind bereits als Segmente vorhanden'
      : 'Keine benannten Teile im Modell gefunden', true);
  }
}

function clearAllSegments() {
  if (!state.segments.length) {
    toast('Keine Segmente vorhanden', true);
    return;
  }
  if (!confirm(`Wirklich alle ${state.segments.length} Segmente löschen?`)) return;
  pushUndo();
  state.segments.forEach((s) => removeHighlights(s.highlights));
  state.segments = [];
  invalidateSegGeomCache();
  // Alle Segmente weg → auch alle Knochenlinien hinfällig
  skeleton.bones = [];
  skeleton.animations.forEach((a) => { a.keys = []; });
  posePos = {};
  selectedJointId = null;
  activeSegName = null;
  clearSelection();
  state.dirty = true;
  renderSegmentList();
  toast('Alle Segmente gelöscht – Speichern nicht vergessen');
}

$('btn-detect-segments').addEventListener('click', detectSegmentsFromModel);
$('btn-clear-segments').addEventListener('click', clearAllSegments);

// ---------------------------------------------------------------------------
// Animation (Knochenlinien)
// ---------------------------------------------------------------------------
//
// Pro Segment eine direkt ziehbare Knochenlinie (Joints = Linienpunkte). Der
// erste Punkt ist die Wurzel/Magnetpunkt – wie alle Punkte ziehbar; die Naht
// zum Rest des Modells hält das Glue. Punkte ziehen verformt das
// Segment je Knochenabschnitt (drehen + entlang der Achse strecken); geteilte
// Gelenkpunkte halten die Naht zu. Doppelklick auf die Linie fügt ein Gelenk
// ein und trennt das Segment dort auf. Keyframes speichern Punkt-Positionen;
// der Export bäckt pro Abschnitt Transform-Timesamples (24 fps) ins USDZ.

const ANIM_FPS = 24;

// Animationsmodell: Knochen-Linien pro Segment, direkt ziehbar (Bewegen + Strecken).
let skeleton = { bones: [], animations: [], poses: [], defaultPoseId: null };
// bones:      [{id, segment, joints:[{id, rest:[x,y,z]}]}]  – joints[0] = Wurzel/Magnetpunkt
// animations: [{name, keys:[{jointId, t, x, y, z, ease}]}]   – x,y,z = posierte Position (Modell-Raum)
// poses:      [{id, name, joints:{jointId:[x,y,z]}}]         – benannte Stellungen der Punkte
// defaultPoseId: Pose, die als AUSGANGSLAGE gilt (null = modellierte Ruhelage, z. B. T-Pose).
//   Wichtig: `joint.rest` bleibt immer die Ruhelage, in der die Geometrie modelliert
//   ist – daraus rechnet boneMatrix. Die Standardpose ist nur eine andere „aktuelle"
//   Stellung, auf die alles zurückfällt, was nicht gezogen/gekeyt ist.

let selectedJointId = null;
let jointCounter = 1;
let boneCounter = 1;
let poseCounter = 1;
let activeSegName = null;     // im Tab gewähltes Segment (dessen Knochenlinie bearbeitet wird)
let posePos = {};            // jointId -> [x,y,z] aktuelle Position (Vorschau); fehlt = Ruheposition
let boneTool = false;        // "Knochen zeichnen" aktiv
let boneDraw = null;         // {p0:[x,y,z]} zwischen erstem und zweitem Klick
let rig = null;              // {root, skins:[], spheres:[], lines:[]}
let segGeomCache = new Map();// segmentName → [{geo, mat}]

// Weiche Wurzel-Verankerung: der magnetische Wurzelpunkt (und ein kurzes Stück
// dahinter) klebt an den umliegenden Segmenten – die Hülle reißt am Magnet nicht
// auf, sondern dehnt sich wie Kaugummi. Über die Knochenlänge geht die
// Verankerung von 0 (fest am Magnet) auf 1 (frei beweglich) über.
let allowSeparation = false;        // Checkbox „Oberfläche trennen erlauben"
const ROOT_GLUE_FRAC = 0.28;        // Anteil der Knochenlänge, über den angeklebt wird
let animPlaying = null;      // {anim, t0}
let activeAnimIndex = 0;     // gewählte Animation in der Liste
let skeletonTabActive = false;

/** Knochen-Transformation: bildet die Ruhestrecke A→B auf die aktuelle Strecke ab
 *  (rotieren + entlang der Achse strecken). Endpunkte treffen exakt. */
function boneMatrix(restA, restB, curA, curB) {
  const restVec = new THREE.Vector3().subVectors(restB, restA);
  const curVec = new THREE.Vector3().subVectors(curB, curA);
  const restLen = restVec.length(), curLen = curVec.length();
  const restDir = restLen > 1e-9 ? restVec.clone().multiplyScalar(1 / restLen) : new THREE.Vector3(1, 0, 0);
  const curDir = curLen > 1e-9 ? curVec.clone().multiplyScalar(1 / curLen) : restDir.clone();
  const factor = restLen > 1e-9 ? curLen / restLen : 1;
  const s = factor - 1, d = restDir;
  const S = new THREE.Matrix3().set(
    1 + s * d.x * d.x, s * d.x * d.y, s * d.x * d.z,
    s * d.y * d.x, 1 + s * d.y * d.y, s * d.y * d.z,
    s * d.z * d.x, s * d.z * d.y, 1 + s * d.z * d.z);
  const R3 = new THREE.Matrix3().setFromMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(
    new THREE.Quaternion().setFromUnitVectors(restDir, curDir)));
  const L = R3.multiply(S);
  const e = L.elements;
  const M = new THREE.Matrix4().set(
    e[0], e[3], e[6], 0,
    e[1], e[4], e[7], 0,
    e[2], e[5], e[8], 0,
    0, 0, 0, 1);
  M.setPosition(curA.clone().sub(restA.clone().applyMatrix3(L)));
  return M;
}

// --- Bone-/Joint-Helfer ---

function boneById(id) { return skeleton.bones.find((b) => b.id === id) || null; }
function boneOfSegment(name) { return skeleton.bones.find((b) => b.segment === name) || null; }

/** Knochenlinie eines Segments samt Keyframes/Pose still entfernen (ohne UI/Undo). */
function dropBone(segName) {
  const bone = boneOfSegment(segName);
  if (!bone) return;
  const ids = new Set(bone.joints.map((j) => j.id));
  skeleton.bones = skeleton.bones.filter((b) => b !== bone);
  skeleton.animations.forEach((a) => { a.keys = a.keys.filter((k) => !ids.has(k.jointId)); });
  skeleton.poses.forEach((p) => ids.forEach((id) => delete p.joints[id]));
  ids.forEach((id) => delete posePos[id]);
  if (ids.has(selectedJointId)) selectedJointId = null;
}

/** {bone, joint, index} zu einer Joint-Id oder null. */
function findJoint(jid) {
  for (const bone of skeleton.bones) {
    const i = bone.joints.findIndex((j) => j.id === jid);
    if (i >= 0) return { bone, joint: bone.joints[i], index: i };
  }
  return null;
}
function jointById(jid) { return findJoint(jid)?.joint || null; }

/** Lesbares Label "Segment·N" für Tabellen/Zeitleiste. */
function jointLabel(jid) {
  const f = findJoint(jid);
  if (!f) return '?';
  const role = f.index === 0 ? 'Wurzel' : f.index === f.bone.joints.length - 1 ? 'Spitze' : 'Gelenk ' + f.index;
  return `${f.bone.segment} · ${role}`;
}

function jointRestVec(joint) { return new THREE.Vector3(joint.rest[0], joint.rest[1], joint.rest[2]); }

// --- Posen: benannte Stellungen aller Knochenpunkte ---

function poseById(id) { return skeleton.poses.find((p) => p.id === id) || null; }
function defaultPose() { return skeleton.defaultPoseId ? poseById(skeleton.defaultPoseId) : null; }
function allJointIds() { return skeleton.bones.flatMap((b) => b.joints.map((j) => j.id)); }

/**
 * Ausgangslage eines Punktes: die Standardpose, sonst die modellierte Ruhelage.
 * Jede Stelle, die „wo steht der Punkt, wenn ihn niemand bewegt" beantwortet,
 * geht hierüber – Vorschau, Ruhe-Keyframes und die gebackenen Exporte.
 */
function jointBaseVec(jid) {
  const p = defaultPose()?.joints[jid];
  if (p) return new THREE.Vector3(p[0], p[1], p[2]);
  const j = jointById(jid);
  return j ? jointRestVec(j) : new THREE.Vector3();
}

/** Position eines Punktes in einer Pose (fehlt er dort: seine Ausgangslage). */
function poseJointVec(pose, jid) {
  const p = pose?.joints[jid];
  return p ? new THREE.Vector3(p[0], p[1], p[2]) : jointBaseVec(jid);
}

/** Stellung aller Punkte einschließlich der gerade gezogenen festhalten. */
function captureJoints() {
  const out = {};
  for (const jid of allJointIds()) {
    const v = jointCurVec(jid);
    out[jid] = [v.x, v.y, v.z];
  }
  return out;
}

/** Aktuelle (posierte) Position eines Joints; fehlt sie, die Ausgangslage. */
function jointCurVec(jid) {
  const p = posePos[jid];
  if (p) return new THREE.Vector3(p[0], p[1], p[2]);
  return jointBaseVec(jid);
}

/** Model-Raum-Zentroide aller Dreiecke eines Mesh-Eintrags (gecacht). */
function ensureModelCentroids(entry) {
  if (entry.modelCentroids) return entry.modelCentroids;
  ensureCentroids(entry);
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
  const e = rel.elements, cs = entry.centroids;
  const mc = new Float32Array(cs.length);
  for (let t = 0; t < entry.triCount; t++) {
    const x = cs[t * 3], y = cs[t * 3 + 1], z = cs[t * 3 + 2];
    mc[t * 3] = e[0] * x + e[4] * y + e[8] * z + e[12];
    mc[t * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
    mc[t * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];
  }
  entry.modelCentroids = mc;
  return mc;
}

/**
 * Dreiecke des Segments den Knochen-Abschnitten zuordnen (Auto-Auftrennen):
 * jedes Dreieck kommt zu dem Abschnitt, dessen Strecke seinem Zentroid am
 * nächsten ist. Liefert ein Array (Länge = Knochenanzahl) von sel-Maps.
 */
function assignPieces(bone) {
  const seg = segmentByName(bone.segment);
  const nBones = Math.max(1, bone.joints.length - 1);
  const pieces = Array.from({ length: nBones }, () => ({}));
  if (!seg) return pieces;
  const A = bone.joints.map((j) => jointRestVec(j));
  for (const mi in seg.sel) {
    const entry = meshList[mi];
    if (!entry) continue;
    const mask = seg.sel[mi];
    const mc = ensureModelCentroids(entry);
    for (let t = 0; t < entry.triCount; t++) {
      if (!mask[t]) continue;
      const px = mc[t * 3], py = mc[t * 3 + 1], pz = mc[t * 3 + 2];
      let best = 0, bestD = Infinity;
      for (let i = 0; i < nBones; i++) {
        const a = A[i], b = A[i + 1] || A[i];
        const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
        const len2 = abx * abx + aby * aby + abz * abz || 1e-12;
        let u = ((px - a.x) * abx + (py - a.y) * aby + (pz - a.z) * abz) / len2;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        const dx = px - (a.x + abx * u), dy = py - (a.y + aby * u), dz = pz - (a.z + abz * u);
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) { bestD = d; best = i; }
      }
      (pieces[best][mi] ||= new Uint8Array(entry.triCount))[t] = 1;
    }
  }
  return pieces;
}

// --- Naht-Verankerung: die Hülle reißt an keiner Segmentgrenze auf -----------
//
// Jeder Vertex eines animierten Segments, dessen Ruheposition mit Geometrie
// AUSSERHALB des Segments geteilt wird (Nachbar-Segment oder statischer Rest),
// ist ein „Naht-Vertex". Naht-Vertices bleiben an ihrer Ruhelage angeklebt
// (Glue = 0); nach innen läuft die Verankerung über ein kurzes Band weich auf 1
// (frei beweglich). So dehnt sich die Fläche wie Kaugummi statt aufzureißen –
// und zwar an ALLEN Rändern, nicht nur am Wurzel-/Magnetpunkt.

/**
 * ALLE Randvertices (offene Kanten) einer Teil-Geometrie. Nur über das
 * SEGMENT, nicht das ganze Modell → schnell. Verschweißt die (vom USD-Loader als
 * Triangle-Soup gelieferte) Geometrie per Position, zählt Kantennutzung: eine
 * Kante, die nur EIN Dreieck nutzt, liegt am Rand. Liefert Flag je Vertex.
 * Achtung: Das sind Kandidaten – welche Ränder wirklich Nähte sind (mit
 * Geometrie außerhalb des Segments geteilt), filtert restrictSeamsToOutside.
 */
function subsetBoundaryFlags(geo) {
  const pos = geo.attributes.position;
  const n = pos.count;
  const idx = geo.index;
  const triCount = (idx ? idx.count : n) / 3;
  const arr = pos.array;
  const bits = new Uint32Array(arr.buffer, arr.byteOffset, n * 3);
  // Verschweißen per exakter Position (gleiche Floats → gleiche Bits)
  const weldMap = new Map();
  const weld = new Uint32Array(n);
  for (let v = 0; v < n; v++) {
    const key = bits[v * 3] + ',' + bits[v * 3 + 1] + ',' + bits[v * 3 + 2];
    let w = weldMap.get(key);
    if (w === undefined) { w = weldMap.size; weldMap.set(key, w); }
    weld[v] = w;
  }
  // Kantennutzung auf verschweißten Indizes zählen (Kante mit Nutzung 1 = Rand)
  const edge = new Map();   // "min|max" → count
  const edgeV = new Map();  // "min|max" → [min, max]
  const gv = (i) => weld[idx ? idx.getX(i) : i];
  const bump = (x, y) => {
    const lo = x < y ? x : y, hi = x < y ? y : x;
    const k = lo + '|' + hi;
    edge.set(k, (edge.get(k) || 0) + 1);
    if (!edgeV.has(k)) edgeV.set(k, [lo, hi]);
  };
  for (let t = 0; t < triCount; t++) {
    const a = gv(t * 3), b = gv(t * 3 + 1), c = gv(t * 3 + 2);
    bump(a, b); bump(b, c); bump(c, a);
  }
  const wBoundary = new Uint8Array(weldMap.size);
  for (const [k, count] of edge) {
    if (count === 1) { const e = edgeV.get(k); wBoundary[e[0]] = 1; wBoundary[e[1]] = 1; }
  }
  const flags = new Uint8Array(n);
  for (let v = 0; v < n; v++) flags[v] = wBoundary[weld[v]];
  return flags;
}

/**
 * Kern der Außen-Naht-Filterung (rein, testbar – siehe test-seam-outside.mjs):
 * löscht die Rand-Flags aller Vertices, deren Ruheposition NICHT (innerhalb
 * der Toleranzzelle `cell`) von der Außen-Geometrie geteilt wird.
 * skins: [{rest, boundary}] eines Segments; visitOutside(emit) ruft
 * emit(x,y,z) für jeden Vertex außerhalb des Segments auf (Modell-Raum).
 */
function filterSeamsByOutside(skins, visitOutside, cell) {
  // Kandidaten (Randvertices) in ihre Rasterzelle UND alle Nachbarzellen
  // eintragen → ein Außen-Vertex braucht nur seine eigene Zelle nachzuschlagen.
  const cand = new Map();   // Zellschlüssel → flaches Array [skinIdx, vertIdx, …]
  let any = false;
  skins.forEach((s, si) => {
    const rest = s.rest, b = s.boundary;
    if (!b) return;
    for (let v = 0; v < b.length; v++) {
      if (!b[v]) continue;
      any = true;
      const cx = Math.round(rest[v * 3] / cell), cy = Math.round(rest[v * 3 + 1] / cell), cz = Math.round(rest[v * 3 + 2] / cell);
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++)
          for (let dz = -1; dz <= 1; dz++) {
            const k = (cx + dx) + '|' + (cy + dy) + '|' + (cz + dz);
            let a = cand.get(k); if (!a) { a = []; cand.set(k, a); }
            a.push(si, v);
          }
    }
  });
  if (!any) return;
  const keep = skins.map((s) => (s.boundary ? new Uint8Array(s.boundary.length) : null));
  visitOutside((x, y, z) => {
    const k = Math.round(x / cell) + '|' + Math.round(y / cell) + '|' + Math.round(z / cell);
    const a = cand.get(k);
    if (!a) return;
    for (let j = 0; j < a.length; j += 2) keep[a[j]][a[j + 1]] = 1;
  });
  skins.forEach((s, si) => {
    const b = s.boundary, kp = keep[si];
    if (!b || !kp) return;
    for (let v = 0; v < b.length; v++) if (b[v] && !kp[v]) b[v] = 0;
  });
}

/**
 * Naht-Flags eines Segments auf ECHTE Nähte einschränken: Ein Randvertex zählt
 * nur dann als Naht (und wird per Glue verankert), wenn seine Ruheposition auch
 * von Geometrie AUSSERHALB des Segments genutzt wird (Nachbar-Segment oder
 * statischer Rest) – z. B. der Schnittring, wo das Segment aus der Hülle
 * geschnitten wurde. Innere offene Ränder – etwa die Stoßringe zwischen den
 * Teil-Meshes eines mehrteiligen Segments (Arm aus Schulter + Unterarm + Hand)
 * oder Ränder ganz ohne Nachbargeometrie – werden freigegeben: alle Teile
 * hängen am selben Knochen und bewegen sich gemeinsam. Ohne diesen Filter
 * kleben die inneren Ringe an der Ruhelage fest → das Segment lässt sich nicht
 * frei bewegen und reißt an den Übergängen zu den frei beweglichen Teilen auf.
 * effSel = Dreiecks-Auswahl des Segments je Mesh (alles andere ist „außen").
 * childSels (optional) = Auswahl-Masken von KIND-Segmenten, die diesem Segment
 * folgen („bewegt sich mit"): deren Nähte werden NICHT verankert – die Kinder
 * reiten auf der Eltern-Transform mit, die Naht bleibt von selbst geschlossen.
 * Würde man sie verankern, klebte die Eltern-Seite an der Ruhelage fest und
 * risse beim Bewegen von der mitfahrenden Kind-Seite ab (Humanoid: Schulter/
 * Hüfte/Hals). Die Flags werden IN PLACE reduziert. Toleranz: feines Raster
 * wie beim Dezimieren (~0,01 % der Segmentgröße), da der USD-Loader an
 * geteilten Kanten oft minimal abweichende Floats liefert.
 */
function restrictSeamsToOutside(skins, effSel, childSels) {
  // Toleranzzelle aus der Bounding-Box des Segments ableiten
  let min = Infinity, max = -Infinity;
  for (const s of skins) {
    const rest = s.rest;
    for (let i = 0; i < rest.length; i++) {
      if (rest[i] < min) min = rest[i];
      if (rest[i] > max) max = rest[i];
    }
  }
  const cell = (max > min ? (max - min) : 1) * 1e-4;
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const p = new THREE.Vector3();
  filterSeamsByOutside(skins, (emit) => {
    meshList.forEach((entry, mi) => {
      const sel = effSel ? effSel[mi] : null;
      const childs = (childSels || []).map((cs) => cs[mi]).filter(Boolean);
      const g = entry.mesh.geometry;
      const pos = g.attributes.position, index = g.index;
      const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
      const visited = new Uint8Array(pos.count);
      for (let t = 0; t < entry.triCount; t++) {
        if (sel && sel[t]) continue;                     // gehört zum Segment → nicht „außen"
        if (childs.some((m) => m[t])) continue;          // Kind-Segment → folgt mit, keine Naht
        for (let k = 0; k < 3; k++) {
          const vi = index ? index.getX(t * 3 + k) : t * 3 + k;
          if (visited[vi]) continue;
          visited[vi] = 1;
          p.set(pos.getX(vi), pos.getY(vi), pos.getZ(vi)).applyMatrix4(rel);
          emit(p.x, p.y, p.z);
        }
      }
    });
  }, cell);
}

/**
 * Auswahl-Masken aller Segmente, die `parentName` als Eltern haben und ihm
 * folgen (nicht gesperrt). Deren Nähte werden bei der Naht-Verankerung des
 * Eltern-Segments ausgenommen – siehe restrictSeamsToOutside.
 */
function childSelsOf(parentName, animatedNames, owners) {
  if (!owners) return [];
  return [...animatedNames]
    .filter((n) => n !== parentName && !segmentByName(n)?.locked
      && findParentSegment(n, animatedNames) === parentName)
    .map((n) => exclusiveSelOf(n, owners));
}

/** Punkt → Index des nächstgelegenen Knochen-Abschnitts einer Knochenlinie. */
function nearestBoneSection(restJoints, point) {
  let bestK = 0, bestD = Infinity;
  const ab = new THREE.Vector3(), proj = new THREE.Vector3(), tmp = new THREE.Vector3();
  for (let k = 0; k < restJoints.length - 1; k++) {
    const a = restJoints[k];
    ab.subVectors(restJoints[k + 1], a);
    const len2 = Math.max(ab.lengthSq(), 1e-12);
    let u = tmp.subVectors(point, a).dot(ab) / len2;
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    proj.copy(a).addScaledVector(ab, u);
    const d = proj.distanceToSquared(point);
    if (d < bestD) { bestD = d; bestK = k; }
  }
  return bestK;
}

/**
 * Eltern-Segment für „bewegt sich mit": das KLEINSTE animierte Segment (mit
 * Knochen), das die Dreiecke des Kindes weitgehend enthält und größer ist.
 * Beispiel: „Körper" ist Eltern von „Schnabel". Liefert den Namen oder null.
 */
function findParentSegment(childName, animatedNames) {
  // Explizit gesetzte Eltern (z. B. Humanoid-Schablone: Arme/Beine/Kopf →
  // Torso) haben Vorrang vor der Enthaltenseins-Heuristik darunter.
  const explicit = boneOfSegment(childName)?.parent;
  if (explicit && explicit !== childName && animatedNames.has(explicit)) return explicit;
  const child = segmentByName(childName);
  if (!child) return null;
  const childCount = segTriCountGlobal(child);
  let best = null, bestCount = Infinity;
  for (const name of animatedNames) {
    if (name === childName) continue;
    const P = segmentByName(name);
    if (!P) continue;
    const pCount = segTriCountGlobal(P);
    if (pCount <= childCount || pCount >= bestCount) continue;   // Eltern muss größer (und das kleinste solche) sein
    let inside = 0, total = 0;
    for (const mi in child.sel) {
      const cs = child.sel[mi], ps = P.sel[mi];
      for (let t = 0; t < cs.length; t++) if (cs[t]) { total++; if (ps && ps[t]) inside++; }
    }
    if (total && inside / total >= 0.5) { best = name; bestCount = pCount; }
  }
  return best;
}

/** Globale Dreieckszahl eines Segments (über alle Quell-Meshes). */
function segTriCountGlobal(seg) {
  let n = 0;
  for (const mi in seg.sel) { const s = seg.sel[mi]; if (s) for (let t = 0; t < s.length; t++) if (s[t]) n++; }
  return n;
}

/**
 * Eindeutiger Besitzer je Dreieck. Beanspruchen dürfen nur:
 *  • ANIMIERTE Segmente (eigener Knochen) – werden von ihrem Knochen bewegt.
 *  • GESPERRTE Segmente (🔒) – bleiben statisch an ihrer Position.
 * Unter den Beanspruchern gewinnt das KLEINERE Segment (z. B. „Schnabel" gegen
 * „Körper"), Gleichstand → kleinerer Index.
 * Ein NICHT gesperrtes Segment OHNE Knochen beansprucht NICHTS – seine Dreiecke
 * fallen damit an das umgebende animierte Segment und werden mitbewegt (Standard).
 * Liefert pro Mesh ein Array mit dem Segmentnamen (oder null) je Dreieck.
 */
function triOwners(animatedNames, lockedNames) {
  const claims = (name) => !!((animatedNames && animatedNames.has(name)) || (lockedNames && lockedNames.has(name)));
  const ranked = state.segments
    .map((seg, si) => ({ seg, si, n: segTriCountGlobal(seg) }))
    .filter((r) => claims(r.seg.name))
    .sort((a, b) => a.n - b.n || a.si - b.si);   // kleineres Segment gewinnt
  return meshList.map((entry, mi) => {
    const owner = new Array(entry.triCount).fill(null);
    for (const { seg } of ranked) {
      const sel = seg.sel[mi];
      if (!sel) continue;
      for (let t = 0; t < entry.triCount; t++) if (sel[t] && owner[t] === null) owner[t] = seg.name;
    }
    return owner;
  });
}

/** Statische Geometrie (alles, was kein animierter Knochen treibt): gesperrte
 *  Segmente + herrenlose Dreiecke. Owner-basiert, damit nichts doppelt oder
 *  verloren geht. animatedNames = Set der animierten (beweglichen) Segmentnamen. */
function staticOwnerParts(owners, animatedNames) {
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const parts = [];
  meshList.forEach((entry, mi) => {
    const o = owners[mi];
    const mask = new Uint8Array(entry.triCount);
    let any = false;
    for (let t = 0; t < entry.triCount; t++) {
      const name = o[t];
      if (!name || !animatedNames.has(name)) { mask[t] = 1; any = true; }
    }
    if (!any) return;
    const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
    const geo = buildSubsetGeometry(entry, mask, rel);
    if (!geo) return;
    const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
    parts.push({ geo, mat });
  });
  return parts;
}

/** Herrenlose Dreiecke (owner === null) als Parts – der „Rest" fürs Backen. */
function unownedParts(owners) {
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const parts = [];
  meshList.forEach((entry, mi) => {
    const o = owners[mi];
    const mask = new Uint8Array(entry.triCount);
    let any = false;
    for (let t = 0; t < entry.triCount; t++) if (o[t] === null) { mask[t] = 1; any = true; }
    if (!any) return;
    const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
    const geo = buildSubsetGeometry(entry, mask, rel);
    if (!geo) return;
    const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
    parts.push({ geo, mat });
  });
  return parts;
}

/**
 * Effektive (exklusive) Dreiecks-Auswahl eines Segments: nur Dreiecke, die ihm
 * laut triOwners() tatsächlich gehören. Damit verliert „Körper" die Dreiecke,
 * die zum kleineren „Schnabel" gehören – beide bleiben unabhängig animierbar.
 */
function exclusiveSelOf(segName, owners) {
  const out = {};
  meshList.forEach((entry, mi) => {
    const o = owners[mi];
    let any = false;
    const mask = new Uint8Array(entry.triCount);
    for (let t = 0; t < entry.triCount; t++) if (o[t] === segName) { mask[t] = 1; any = true; }
    if (any) out[mi] = mask;
  });
  return out;
}

/** Pro Vertex: nächster Knochen-Abschnitt (für die starre Teil-Transform). */
function assignSections(rest, restJoints) {
  const n = rest.length / 3;
  const section = new Uint8Array(n);
  const p = new THREE.Vector3(), ab = new THREE.Vector3(), proj = new THREE.Vector3(), tmp = new THREE.Vector3();
  for (let v = 0; v < n; v++) {
    p.set(rest[v * 3], rest[v * 3 + 1], rest[v * 3 + 2]);
    let bestK = 0, bestD = Infinity;
    for (let k = 0; k < restJoints.length - 1; k++) {
      const a = restJoints[k];
      ab.subVectors(restJoints[k + 1], a);
      const len2 = Math.max(ab.lengthSq(), 1e-12);
      let u = tmp.subVectors(p, a).dot(ab) / len2;
      u = u < 0 ? 0 : u > 1 ? 1 : u;
      proj.copy(a).addScaledVector(ab, u);
      const d = proj.distanceToSquared(p);
      if (d < bestD) { bestD = d; bestK = k; }
    }
    section[v] = bestK;
  }
  return section;
}

/**
 * Distanzbasierte Glue-Gewichte (0 am Rand/Naht, weich auf 1 über `band` nach
 * innen). skins: [{ rest, boundary }] eines Segments. `boundary` = Randflags aus
 * subsetBoundaryFlags. Die Naht-Punkte werden auf ein feines Raster ausgedünnt
 * (höchstens einer je Zelle) → begrenzte Dichte, kein O(N²)-Freeze.
 */
function computeSeamGlue(skins, band) {
  const glues = skins.map((s) => new Float32Array(s.rest.length / 3).fill(1));
  if (!(band > 0)) return glues;

  // Naht-Punkte (Randvertices) sammeln + auf feines Raster ausdünnen
  const fine = band / 4;
  const fkey = (x, y, z) => Math.round(x / fine) + '|' + Math.round(y / fine) + '|' + Math.round(z / fine);
  const seamCell = new Map();   // feiner Schlüssel → [x,y,z]
  for (const s of skins) {
    const rest = s.rest, b = s.boundary;
    if (!b) continue;
    for (let i = 0; i < b.length; i++) if (b[i]) {
      const x = rest[i * 3], y = rest[i * 3 + 1], z = rest[i * 3 + 2];
      const k = fkey(x, y, z);
      if (!seamCell.has(k)) seamCell.set(k, [x, y, z]);
    }
  }
  if (!seamCell.size) return glues;

  // grobes Raster (Zelle = band): je Zelle die ausgedünnten Naht-Punkte
  const cell = band;
  const grid = new Map();
  for (const p of seamCell.values()) {
    const k = Math.floor(p[0] / cell) + '|' + Math.floor(p[1] / cell) + '|' + Math.floor(p[2] / cell);
    let a = grid.get(k); if (!a) { a = []; grid.set(k, a); } a.push(p);
  }
  const band2 = band * band;
  skins.forEach((s, si) => {
    const rest = s.rest, n = rest.length / 3, glue = glues[si];
    for (let vi = 0; vi < n; vi++) {
      const x = rest[vi * 3], y = rest[vi * 3 + 1], z = rest[vi * 3 + 2];
      const cx = Math.floor(x / cell), cy = Math.floor(y / cell), cz = Math.floor(z / cell);
      let best = band2;
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++)
          for (let dz = -1; dz <= 1; dz++) {
            const a = grid.get((cx + dx) + '|' + (cy + dy) + '|' + (cz + dz));
            if (!a) continue;
            for (let j = 0; j < a.length; j++) {
              const p = a[j];
              const ex = x - p[0], ey = y - p[1], ez = z - p[2];
              const d = ex * ex + ey * ey + ez * ez;
              if (d < best) best = d;
            }
          }
      if (best >= band2) continue;   // glue bleibt 1
      const t = Math.sqrt(best) / band;   // 0 am Rand … 1 am Bandende
      glue[vi] = t * t * (3 - 2 * t);      // Smoothstep
    }
  });
  // Die Naht-Vertices SELBST exakt auf 0 festnageln: die Raster-Ausdünnung
  // oben darf nur den weichen Verlauf im Inneren speisen. Ein ausgedünnter
  // Randvertex bekäme sonst Glue > 0, bewegte sich beim Posieren ein Stück
  // mit und risse die Naht haarfein auf (sichtbar als schwarze Ritzen an
  // der Segmentgrenze).
  skins.forEach((s, si) => {
    const b = s.boundary, glue = glues[si];
    if (!b) return;
    for (let i = 0; i < b.length; i++) if (b[i]) glue[i] = 0;
  });
  return glues;
}

/** Knochen-Matrizen aller Abschnitte für die aktuellen Joint-Positionen. */
function boneMatrices(restJoints, cur) {
  const Ms = [];
  for (let k = 0; k < restJoints.length - 1; k++) Ms.push(boneMatrix(restJoints[k], restJoints[k + 1], cur[k], cur[k + 1]));
  return Ms;
}

/**
 * Weiche Deformation in `out` schreiben: jeder Vertex bekommt die starre
 * Transform seines Abschnitts, gemischt mit der Ruhelage über glue.
 * allow=true („Trennung erlauben") → glue ignoriert, alles bewegt sich voll.
 * post (optional) = Eltern-Transform, die NACH der Eigendeformation angewandt
 * wird → das Segment „reitet" auf dem Eltern-Segment mit (Parenting).
 * pMs/psec (optional) = ALLE Abschnitts-Matrizen des Eltern-Knochens + je
 * Vertex der nächste Eltern-Abschnitt: Naht-Vertices (glue 0) folgen dann
 * exakt der Eltern-Verformung IHRES Punktes – dieselbe Transform, die die
 * Eltern-Oberfläche dort selbst bekommt → die Naht bleibt unter JEDER
 * Eltern-Pose geschlossen (auch bei starkem Beugen), das Innere dehnt sich
 * weiter wie Gummi.
 */
function deformPositions(out, rest, section, glue, Ms, allow, post, pMs, psec) {
  const p = new THREE.Vector3(), moved = new THREE.Vector3(), anchor = new THREE.Vector3();
  const e = post ? post.elements : null;
  const n = rest.length / 3;
  const anchored = !!(pMs && psec);
  for (let v = 0; v < n; v++) {
    const ix = v * 3;
    p.set(rest[ix], rest[ix + 1], rest[ix + 2]);
    moved.copy(p).applyMatrix4(Ms[section[v]]);
    const g = allow ? 1 : (glue ? glue[v] : 1);
    let x, y, z;
    if (anchored) {
      // Mit Eltern-Anker: linear zwischen Eltern-Transform DIESES Vertex
      // (g=0, nahtdicht) und der vollen Eigenverformung samt Mitreiten (g=1)
      // – identisch zum LBS-Skinning der Exporte (2 Einflüsse je Vertex).
      x = moved.x; y = moved.y; z = moved.z;
      if (e) {
        const nx = e[0] * x + e[4] * y + e[8] * z + e[12];
        const ny = e[1] * x + e[5] * y + e[9] * z + e[13];
        const nz = e[2] * x + e[6] * y + e[10] * z + e[14];
        x = nx; y = ny; z = nz;
      }
      if (g < 1) {
        anchor.copy(p).applyMatrix4(pMs[psec[v]]);
        x = anchor.x + (x - anchor.x) * g;
        y = anchor.y + (y - anchor.y) * g;
        z = anchor.z + (z - anchor.z) * g;
      }
    } else {
      // Ohne Eltern: Eigenverformung mit der Ruhelage mischen (wie gehabt)
      x = p.x + (moved.x - p.x) * g;
      y = p.y + (moved.y - p.y) * g;
      z = p.z + (moved.z - p.z) * g;
      if (e) {
        const nx = e[0] * x + e[4] * y + e[8] * z + e[12];
        const ny = e[1] * x + e[5] * y + e[9] * z + e[13];
        const nz = e[2] * x + e[6] * y + e[10] * z + e[14];
        x = nx; y = ny; z = nz;
      }
    }
    out[ix] = x; out[ix + 1] = y; out[ix + 2] = z;
  }
}

/** Alle Abschnitts-Matrizen des Eltern-Knochens (aktuelle Pose), sonst null. */
function parentMatrices(parent) {
  if (!parent) return null;
  const pcur = parent.jointIds.map((id) => jointCurVec(id));
  return boneMatrices(parent.restJoints, pcur);
}

/** Eltern-Info eines Segments fürs Mitbewegen (oder null, wenn gesperrt/keine Eltern). */
function parentInfoFor(seg, childRoot, animatedNames) {
  if (!seg || seg.locked) return null;
  const pName = findParentSegment(seg.name, animatedNames);
  const pBone = pName ? boneOfSegment(pName) : null;
  if (!pBone || pBone.joints.length < 2) return null;
  const pRest = pBone.joints.map((j) => jointRestVec(j));
  return { jointIds: pBone.joints.map((j) => j.id), restJoints: pRest, section: nearestBoneSection(pRest, childRoot) };
}

/** Alle Eltern-Abschnitts-Matrizen zu einer Animationszeit t (gebackene Exporte). */
function evalParentMatrices(parent, anim, t) {
  if (!parent) return null;
  const cur = parent.jointIds.map((id) => {
    const pp = evalJointPose(anim, id, t);
    return pp ? new THREE.Vector3(pp.x, pp.y, pp.z) : jointBaseVec(id);
  });
  return boneMatrices(parent.restJoints, cur);
}

/** Eltern-Transform (am Wurzel-Abschnitt des Kindes) zu einer Animationszeit t. */
function evalParentPost(parent, anim, t) {
  const Ms = evalParentMatrices(parent, anim, t);
  return Ms ? (Ms[parent.section] || null) : null;
}

/** Welt-Matrix eines Abschnitts-Joints (buildSkelRig) für einen Clip zur Zeit t. */
function sectionWorldAt(sj, clip, t) {
  const pa = evalJointPose(clip, sj.aId, t), pb = evalJointPose(clip, sj.bId, t);
  const curA = pa ? new THREE.Vector3(pa.x, pa.y, pa.z) : jointBaseVec(sj.aId);
  const curB = pb ? new THREE.Vector3(pb.x, pb.y, pb.z) : jointBaseVec(sj.bId);
  return new THREE.Matrix4().multiplyMatrices(boneMatrix(sj.restA, sj.restB, curA, curB), sj.bindM);
}

/**
 * LOKALE Anim-Matrix eines Abschnitts: Kettenglieder (k>0) relativ zum
 * LIVE-Welt-Zustand des vorigen Glieds (die Hierarchie komponiert dann exakt
 * die Welt-Transform, nichts wird doppelt angewandt); das erste Glied eines
 * Kind-Segments reitet über Eltern-Bind⁻¹ auf dem Eltern-Joint mit.
 */
function sectionLocalAt(sj, clip, t) {
  const world = sectionWorldAt(sj, clip, t);
  if (sj.prev) return new THREE.Matrix4().multiplyMatrices(sectionWorldAt(sj.prev, clip, t).invert(), world);
  if (sj.parentBindInv) return new THREE.Matrix4().multiplyMatrices(sj.parentBindInv, world);
  return world;
}

// --- Tab-Umschaltung Segmente/Skelett ---

document.querySelectorAll('.subtab').forEach((tab) => {
  tab.addEventListener('click', () => {
    document.querySelectorAll('.subtab').forEach((t) =>
      t.classList.toggle('active', t === tab));
    const skel = tab.dataset.subtab === 'skeleton';
    $('tab-segments').hidden = skel;
    $('tab-skeleton').hidden = !skel;
    skeletonTabActive = skel;
    if (skel) {
      setTool('nav');
      clearSelection();
      buildRig();
    } else {
      stopAnimPreview();
      cancelHumanoidFit();
      disposeRig();
    }
  });
});

function resetSkeleton() {
  stopAnimPreview();
  cancelHumanoidFit();
  disposeRig();
  skeleton = { bones: [], animations: [], poses: [], defaultPoseId: null };
  activeAnimIndex = 0;
  allowSeparation = false;
  if ($('cfg-allow-separation')) $('cfg-allow-separation').checked = false;
  selectedJointId = null;
  posePos = {};
  activeSegName = null;
  boneTool = false;
  boneDraw = null;
  jointCounter = 1;
  boneCounter = 1;
  poseCounter = 1;
  segGeomCache.clear();
  renderSkeletonUi();
}

function invalidateSegGeomCache(segName) {
  if (segName === undefined) segGeomCache.clear();
  else segGeomCache.delete(segName);
}

// --- Rig: Knochen-Deform-Vorschau ---

function segmentByName(name) {
  return state.segments.find((s) => s.name === name) || null;
}

/** Teil-Geometrien eines Segments (Modell-Raum), gecacht. */
function segmentParts(segName) {
  if (segGeomCache.has(segName)) return segGeomCache.get(segName);
  const seg = segmentByName(segName);
  if (!seg || !modelObject) return [];
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const parts = [];
  for (const mi in seg.sel) {
    const entry = meshList[mi];
    if (!entry || !seg.sel[mi].some((v) => v)) continue;
    const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
    const geo = buildSubsetGeometry(entry, seg.sel[mi], rel);
    if (!geo) continue;
    const mat = Array.isArray(entry.mesh.material)
      ? entry.mesh.material[0] : entry.mesh.material;
    parts.push({ geo, mat });
  }
  segGeomCache.set(segName, parts);
  return parts;
}

/** Rest-Geometrie: alles außerhalb der angegebenen Segmente. */
function restPartsExcluding(segNames) {
  if (!segNames.length) return null;
  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  const parts = [];
  meshList.forEach((entry, mi) => {
    const rest = new Uint8Array(entry.triCount).fill(1);
    segNames.forEach((n) => {
      const s = segmentByName(n)?.sel[mi];
      if (s) for (let t = 0; t < rest.length; t++) if (s[t]) rest[t] = 0;
    });
    if (!rest.some((v) => v)) return;
    const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
    const geo = buildSubsetGeometry(entry, rest, rel);
    if (!geo) return;
    const mat = Array.isArray(entry.mesh.material)
      ? entry.mesh.material[0] : entry.mesh.material;
    parts.push({ geo, mat });
  });
  return parts;
}

function disposeRig() {
  if (!rig) return;
  modelRoot.remove(rig.root);
  rig.root.traverse((n) => {
    // Piece-/Rest-Geometrien werden frisch gebaut → freigeben; Materialien sind
    // mit dem Modell geteilt und bleiben. Nur Gizmo-Materialien freigeben.
    n.geometry?.dispose();
    if (n.userData.isRigVisual) n.material?.dispose();
  });
  rig = null;
  if (modelObject) modelObject.visible = true;
}

function buildRig() {
  disposeRig();
  if (!modelObject || !skeleton.bones.length) {
    if (modelObject) modelObject.visible = true;
    renderSkeletonUi();
    return;
  }

  const root = new THREE.Group();
  const skins = [], spheres = [], lines = [];
  const size = handleSize() * 0.9;
  const animatedSegs = new Set();

  scene.updateMatrixWorld(true);
  const invRoot = modelObject.matrixWorld.clone().invert();
  // Besitz-Zuordnung (für Überlappung/Sperre) ist optional: scheitert sie, baut
  // das Rig trotzdem (mit voller Auswahl) – Posieren darf nie blockiert werden.
  // Animiert = hat einen Knochen (auch wenn gesperrt – die Sperre verhindert nur
  // das Mitbewegen mit dem Eltern-Segment, nicht die eigene Animation).
  const animatedNames = new Set(
    skeleton.bones.filter((b) => { const s = segmentByName(b.segment); return s && b.joints.length >= 2; })
      .map((b) => b.segment));
  const lockedNames = new Set(state.segments.filter((s) => s.locked).map((s) => s.name));
  let owners = null;
  try {
    owners = triOwners(animatedNames, lockedNames);   // eindeutiger Besitzer je Dreieck
  } catch (err) {
    console.warn('Besitz-Zuordnung übersprungen:', err);
  }

  for (const bone of skeleton.bones) {
    const seg = segmentByName(bone.segment);
    if (!seg || bone.joints.length < 2) continue;
    animatedSegs.add(bone.segment);

    // Knochen-Stützpunkte (Ruhelage) + kumulierte Bogenlänge ab der Wurzel
    const restJoints = bone.joints.map((j) => jointRestVec(j));
    let total = 0;
    for (let i = 0; i < restJoints.length - 1; i++) total += restJoints[i].distanceTo(restJoints[i + 1]);
    total = total || 1e-6;
    const jointIds = bone.joints.map((j) => j.id);

    // „Bewegt sich mit": Eltern-Segment (nur wenn NICHT gesperrt). Das Kind
    // reitet dann auf der Eltern-Transform an seinem Wurzelpunkt mit.
    const parentInfo = parentInfoFor(seg, restJoints[0], animatedNames);

    // Ein zusammenhängendes (geskinntes) Mesh je Quell-Mesh des Segments –
    // pro Vertex: nächster Knochen-Abschnitt + Glue-Gewicht (0 an geteilten
    // Rändern, weich auf 1 nach innen → die Hülle reißt nirgends auf).
    const boneSkins = [];
    // Exklusive Auswahl (ohne Dreiecke kleinerer Segmente). Fällt die Besitz-
    // Berechnung aus ODER bleibt dem Segment nichts übrig, die volle Auswahl
    // nehmen – so wird IMMER ein Skin gebaut und das Segment bleibt posierbar.
    let eff = owners ? exclusiveSelOf(bone.segment, owners) : seg.sel;
    if (!Object.keys(eff).length) eff = seg.sel;
    for (const mi in eff) {
      const entry = meshList[mi];
      if (!entry) continue;
      const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
      const geo = buildSubsetGeometry(entry, eff[mi], rel);
      if (!geo) continue;
      const rest = new Float32Array(geo.attributes.position.array);   // Ruhelage festhalten
      const section = assignSections(rest, restJoints);
      // je Vertex der nächste ELTERN-Abschnitt → nahtdichter Anker beim Mitreiten
      const psec = parentInfo ? assignSections(rest, parentInfo.restJoints) : null;
      const boundary = subsetBoundaryFlags(geo);   // Randvertices (nur über dieses Segment)
      const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.matrixAutoUpdate = false;
      mesh.userData.skin = { rest, section, psec, boundary, glue: null, jointIds, restJoints, parent: parentInfo };
      root.add(mesh);
      skins.push(mesh);
      boneSkins.push(mesh);
    }
    // Naht-Verankerung: nur ECHTE Nähte (Positionen, die mit Geometrie
    // außerhalb des Segments geteilt werden) verankern – innere Ränder
    // (z. B. Stöße zwischen den Teil-Meshes des Segments) bleiben frei.
    const band = ROOT_GLUE_FRAC * total;
    let glues = boneSkins.map(() => null);
    try {
      restrictSeamsToOutside(boneSkins.map((m) => m.userData.skin), eff,
        childSelsOf(bone.segment, animatedNames, owners));
      glues = computeSeamGlue(boneSkins.map((m) => m.userData.skin), band);
    } catch (err) {
      console.warn('Naht-Verankerung übersprungen:', err);
    }
    boneSkins.forEach((m, i) => { m.userData.skin.glue = glues[i] || null; });

    // Knochen-Linie
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(bone.joints.map(() => new THREE.Vector3())),
      new THREE.LineBasicMaterial({ color: '#f15bb5', depthTest: false, transparent: true, opacity: 0.9 }));
    line.renderOrder = 11;
    line.userData.isRigVisual = true;
    root.add(line);
    lines.push({ line, bone });

    // Joint-Kugeln (erste = Wurzel/Magnetpunkt)
    bone.joints.forEach((jt, i) => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(size, 16, 12),
        new THREE.MeshBasicMaterial({ depthTest: false, transparent: true, opacity: 0.95 }));
      sphere.renderOrder = 12;
      sphere.userData.isRigVisual = true;
      sphere.userData.jointId = jt.id;
      sphere.userData.boneId = bone.id;
      sphere.userData.isRoot = i === 0;
      root.add(sphere);
      spheres.push(sphere);
    });
  }

  // Statischer Rest (gesperrte Segmente + herrenlose Dreiecke) bleibt stehen;
  // nicht gesperrte Teilsegmente sind bereits Teil ihres animierten Segments.
  if (skins.length) {
    const staticParts = owners ? staticOwnerParts(owners, animatedNames)
      : (restPartsExcluding([...animatedSegs]) || []);
    for (const { geo, mat } of staticParts) root.add(new THREE.Mesh(geo, mat));
    modelObject.visible = false;
  } else {
    modelObject.visible = true;
  }

  modelRoot.add(root);
  rig = { root, skins, spheres, lines };
  updateRig();
  renderSkeletonUi();
}

/**
 * Pose anwenden: jeden Vertex der geskinnten Segmente weich verformen.
 * Pro Vertex wird die starre Transform seines Knochen-Abschnitts (rotieren +
 * strecken) angewandt und über das Glue-Gewicht mit der Ruhelage gemischt:
 * 0 an jeder geteilten Naht (klebt fest) → 1 im Inneren (frei). Mit „Trennung
 * erlauben" wird das Glue ignoriert, alles bewegt sich voll und kann ablösen.
 */
function deformSkin(mesh) {
  const sk = mesh.userData.skin;
  const cur = sk.jointIds.map((id) => jointCurVec(id));
  const Ms = boneMatrices(sk.restJoints, cur);
  // Eltern-Matrizen (alle Abschnitte): post = Mitreiten am Wurzel-Abschnitt,
  // sk.psec = nahtdichter Per-Vertex-Anker (siehe deformPositions)
  const pMs = parentMatrices(sk.parent);   // null, falls kein Eltern-Segment / gesperrt
  const post = pMs ? (pMs[sk.parent.section] || null) : null;
  const pos = mesh.geometry.attributes.position;
  deformPositions(pos.array, sk.rest, sk.section, sk.glue, Ms, allowSeparation, post, pMs, sk.psec);
  pos.needsUpdate = true;
  mesh.geometry.computeVertexNormals();
  mesh.geometry.computeBoundingSphere();
}

/** Posen anwenden: Segmente verformen, Kugeln und Linien aktualisieren. */
function updateRig() {
  if (!rig) return;
  for (const mesh of rig.skins) deformSkin(mesh);
  for (const s of rig.spheres) {
    s.position.copy(jointCurVec(s.userData.jointId));
    s.material.color.set(s.userData.isRoot ? '#06d6a0'
      : s.userData.jointId === selectedJointId ? '#ffd166' : '#f15bb5');
    s.scale.setScalar(s.userData.isRoot ? 1.4 : 1);
  }
  for (const { line, bone } of rig.lines) {
    line.geometry.setFromPoints(bone.joints.map((j) => jointCurVec(j.id)));
  }
}

// --- Knochen zeichnen & Punkte ziehen (Capture-Phase, nur im Animations-Tab) ---

let jointDrag = null;   // {jointId, plane} beim Posieren eines Punktes

function pickRigObject(e, objects) {
  if (!renderer || !objects.length) return null;
  const r = renderer.domElement.getBoundingClientRect();
  handleRaycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  ), camera);
  const hits = handleRaycaster.intersectObjects(objects, false);
  return hits.length ? hits[0] : null;
}

/** Sichtbare Modell-Meshes (Modell oder Rig-Teile) für Klickpunkt-Treffer. */
function pickableMeshes() {
  const meshes = [];
  if (rig) rig.root.traverse((n) => { if (n.isMesh && !n.userData.isRigVisual) meshes.push(n); });
  if (modelObject && modelObject.visible) modelObject.traverse((n) => { if (n.isMesh) meshes.push(n); });
  return meshes;
}

/** Klickpunkt auf der Modelloberfläche im Modell-Raum (oder null). */
function pickModelPoint(e) {
  const hit = pickRigObject(e, pickableMeshes());
  if (!hit) return null;
  return (rig ? rig.root : modelRoot).worldToLocal(hit.point.clone());
}

viewerEl.addEventListener('pointerdown', (e) => {
  if (!skeletonTabActive || e.button !== 0) return;
  if (e.detail >= 2) return;   // zweiter Klick eines Doppelklicks → kein Drag

  // Humanoid-Schablone anpassen: Vorlage-Punkte ziehen (Ruhelage der Gelenke)
  if (humanoidFit) {
    const hit = pickRigObject(e, humanoidFit.spheres);
    if (hit) {
      const world = hit.object.getWorldPosition(new THREE.Vector3());
      templateDrag = {
        sphere: hit.object,
        point: hit.object.userData.point,   // geteilte Instanz (Becken/Hals)
        plane: new THREE.Plane().setFromNormalAndCoplanarPoint(
          camera.getWorldDirection(new THREE.Vector3()), world),
      };
      e.stopPropagation(); e.preventDefault();
      viewerEl.setPointerCapture(e.pointerId);
    }
    return;   // im Schablonen-Modus keine anderen Viewer-Aktionen
  }

  // Knochen zeichnen: zwei Klicks setzen Wurzel und Spitze
  if (boneTool) {
    const p = pickModelPoint(e);
    if (!p) { toast('Bitte auf das Modell klicken', true); return; }
    e.stopPropagation(); e.preventDefault();
    if (!boneDraw) {
      boneDraw = { p0: [p.x, p.y, p.z] };
      toast('Startpunkt gesetzt – jetzt das andere Ende des Segments klicken');
    } else {
      createBoneLine(boneDraw.p0, [p.x, p.y, p.z]);
      boneDraw = null;
      setBoneTool(false);
    }
    return;
  }

  if (!rig) return;
  // Joint-Kugel ziehen = posieren. Auch die Wurzel (🧲 Magnetpunkt) ist
  // verschiebbar – die Naht zum Rest des Modells hält das Glue fest, die
  // Hülle dehnt sich dort wie Kaugummi statt abzureißen.
  const sphereHit = pickRigObject(e, rig.spheres);
  if (sphereHit) {
    const jid = sphereHit.object.userData.jointId;
    selectJoint(jid);
    const world = rig.root.localToWorld(jointCurVec(jid));
    pushUndo();
    jointDrag = {
      jointId: jid,
      plane: new THREE.Plane().setFromNormalAndCoplanarPoint(
        camera.getWorldDirection(new THREE.Vector3()), world),
    };
    e.stopPropagation(); e.preventDefault();
    viewerEl.setPointerCapture(e.pointerId);
  }
}, true);

viewerEl.addEventListener('pointermove', (e) => {
  if (!jointDrag && !templateDrag) return;
  e.stopPropagation();
  const r = renderer.domElement.getBoundingClientRect();
  handleRaycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  ), camera);
  const hit = new THREE.Vector3();
  // Schablonen-Punkt ziehen = Ruhelage der Vorlage anpassen
  if (templateDrag) {
    if (!handleRaycaster.ray.intersectPlane(templateDrag.plane, hit)) return;
    templateDrag.point.copy(modelRoot.worldToLocal(hit.clone()));
    templateDrag.sphere.position.copy(templateDrag.point);
    updateHumanoidGizmo();
    return;
  }
  if (!handleRaycaster.ray.intersectPlane(jointDrag.plane, hit)) return;
  const local = rig.root.worldToLocal(hit.clone());
  posePos[jointDrag.jointId] = [local.x, local.y, local.z];
  updateRig();
}, true);

viewerEl.addEventListener('pointerup', (e) => {
  if (templateDrag) {
    e.stopPropagation();
    // Beim Loslassen die Tiefe wieder mittig ins Modell setzen
    centerTemplateDepth(templateDrag.point);
    templateDrag.sphere.position.copy(templateDrag.point);
    updateHumanoidGizmo();
    templateDrag = null;
    return;
  }
  if (!jointDrag) return;
  e.stopPropagation();
  state.dirty = true;
  jointDrag = null;
}, true);

// Doppelklick auf die Knochenlinie: dort ein Gelenk einfügen (Segment auto-auftrennen)
viewerEl.addEventListener('dblclick', (e) => {
  if (!skeletonTabActive || !rig || boneTool || humanoidFit) return;
  const p = pickModelPoint(e);
  if (!p) return;
  e.stopPropagation(); e.preventDefault();
  insertJointOnBone(p);
}, true);

/** Eine Knochenlinie (Wurzel→Spitze) für das aktive Segment anlegen. */
function createBoneLine(p0, p1) {
  if (!activeSegName) { toast('Erst ein Segment wählen', true); return; }
  const existing = boneOfSegment(activeSegName);
  if (existing && !confirm(`„${activeSegName}" hat schon eine Knochenlinie – ersetzen?`)) return;
  pushUndo();
  if (existing) skeleton.bones = skeleton.bones.filter((b) => b !== existing);
  const bone = {
    id: 'b' + (boneCounter++),
    segment: activeSegName,
    joints: [
      { id: 'j' + (jointCounter++), rest: [...p0] },
      { id: 'j' + (jointCounter++), rest: [...p1] },
    ],
  };
  skeleton.bones.push(bone);
  selectedJointId = bone.joints[1].id;
  posePos = {};
  state.dirty = true;
  buildRig();
  toast('Knochen gezeichnet – Spitze ziehen zum Posieren, Doppelklick auf die Linie = Gelenk (Ellenbogen)');
}

/** Auf der nächstgelegenen Knochenstrecke ein Gelenk einfügen und neu auftrennen. */
function insertJointOnBone(point) {
  let best = null;
  for (const bone of skeleton.bones) {
    for (let i = 0; i < bone.joints.length - 1; i++) {
      const a = jointRestVec(bone.joints[i]), b = jointRestVec(bone.joints[i + 1]);
      const ab = new THREE.Vector3().subVectors(b, a);
      let u = point.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-12);
      u = Math.max(0.05, Math.min(0.95, u));
      const proj = a.clone().addScaledVector(ab, u);
      const d = proj.distanceToSquared(point);
      if (!best || d < best.d) best = { d, bone, i, proj };
    }
  }
  if (!best) { toast('Erst einen Knochen zeichnen (✎)', true); return; }
  pushUndo();
  const nj = { id: 'j' + (jointCounter++), rest: [best.proj.x, best.proj.y, best.proj.z] };
  best.bone.joints.splice(best.i + 1, 0, nj);
  selectedJointId = nj.id;
  posePos = {};   // Auftrennen ändert die Abschnitte → Vorschau-Pose zurücksetzen
  state.dirty = true;
  buildRig();
  toast('Gelenk eingefügt – das Segment wird hier automatisch geteilt');
}

function setBoneTool(on) {
  boneTool = on;
  boneDraw = null;
  $('btn-bone-tool')?.classList.toggle('active', on);
  if (viewerEl) viewerEl.style.cursor = on ? 'crosshair' : '';
}

// ---------------------------------------------------------------------------
// Humanoid-Schablone: komplettes Skelett (Torso, Kopf, Arme, Beine) einpassen
// ---------------------------------------------------------------------------
//
// „🧍 Humanoid-Schablone" legt eine vordefinierte Gelenk-Vorlage über das
// Modell (Proportionen aus der Bounding-Box geschätzt). Die orangen Punkte
// zieht man auf Schultern, Ellbogen, Hände, Hüfte, Knie und Füße; „✓
// Übernehmen" ordnet dann JEDES Dreieck des Modells der nächstgelegenen
// Kette zu, legt daraus Segmente an (torso, kopf, arm_links, …) und
// verknüpft die Ketten über eine feste Eltern-Hierarchie mit dem Torso –
// die Hüfte bewegen nimmt so den ganzen Körper mit.

let humanoidFit = null;    // {chains:[{seg,parent,points:[Vector3]}], group, spheres, lines}
let templateDrag = null;   // {sphere, chain, index, plane} beim Ziehen eines Vorlage-Punkts

/**
 * Gelenk-Vorlage aus der Welt-Bounding-Box des Modells (Y = oben im Viewer).
 * Die BREITEN-Achse (Armspannweite) ist die größere horizontale Ausdehnung
 * der Box – egal ob das Modell entlang X oder Z steht; die jeweils andere
 * horizontale Achse ist die TIEFE. Geteilte Punkte sind DIESELBE Vector3-
 * Instanz: EIN Becken-Punkt (Wurzel von Torso und beiden Beinen), EIN
 * Hals-Punkt (Torso-Spitze = Kopf-Wurzel) – keine doppelten Kugeln mehr.
 */
function humanoidChainsFromBBox() {
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(modelObject);
  const size = box.getSize(new THREE.Vector3());
  const c = box.getCenter(new THREE.Vector3());
  const H = size.y || 1;
  const wAxis = (size.x >= size.z) ? 'x' : 'z';   // Breite = größere horizontale Achse
  const dAxis = wAxis === 'x' ? 'z' : 'x';
  const W = size[wAxis] || 1;
  // Welt-Punkt: seitlicher Versatz w (Meter) entlang der Breitenachse, Höhe f·H
  const pt = (w, f) => {
    const v = c.clone();
    v.y = box.min.y + f * H;
    v[wAxis] = c[wAxis] + w;
    return modelRoot.worldToLocal(v);   // gleicher Raum wie Knochen-Klickpunkte
  };
  // Arme ausgestreckt (Box deutlich breiter als hoch) oder hängend?
  const armsOut = W > 0.55 * H;
  const schulter = 0.11 * H;                       // Schulterabstand von der Mitte
  const hand = armsOut ? 0.46 * W : 0.15 * H;      // Hand: Boxrand bzw. neben der Hüfte
  const ellbogen = (schulter + hand) / 2;
  const arm = (s) => (armsOut
    ? [pt(s * schulter, 0.80), pt(s * ellbogen, 0.79), pt(s * hand, 0.78)]
    : [pt(s * schulter, 0.80), pt(s * (schulter + 0.02 * H), 0.62), pt(s * hand, 0.44)]);
  // Geteilte Punkte (gleiche Instanz → eine Kugel, gemeinsames Ziehen)
  const becken = pt(0, 0.52);
  const hals = pt(0, 0.86);
  const bein = (s) => [becken, pt(s * 0.07 * H, 0.27), pt(s * 0.09 * H, 0.04)];
  // „links/rechts" aus Sicht der FIGUR (Blick zur Kamera). Schaut das Modell
  // von der Kamera weg, die Segmente einfach umbenennen.
  const chains = [
    { seg: 'torso', parent: null, points: [becken, pt(0, 0.66), pt(0, 0.78), hals] },
    { seg: 'kopf', parent: 'torso', points: [hals, pt(0, 0.98)] },
    { seg: 'arm_rechts', parent: 'torso', points: arm(-1) },
    { seg: 'arm_links', parent: 'torso', points: arm(1) },
    { seg: 'bein_rechts', parent: 'torso', points: bein(-1) },
    { seg: 'bein_links', parent: 'torso', points: bein(1) },
  ];
  const depthDir = new THREE.Vector3(dAxis === 'x' ? 1 : 0, 0, dAxis === 'z' ? 1 : 0);
  return { chains, depthDir, depthSpan: size.length() || 1 };
}

/**
 * Punkt (Modell-Raum) in der TIEFE automatisch mittig ins Modell setzen:
 * von beiden Seiten entlang der Tiefenachse auf die Oberfläche schießen und
 * den Punkt zwischen Vorder- und Rückseite zentrieren. So zieht man die
 * Vorlage nur in Höhe und Seite – die Linie läuft immer mittig durchs Modell.
 */
function centerTemplateDepth(p) {
  if (!humanoidFit || !modelObject) return;
  const dir = humanoidFit.depthDir, span = humanoidFit.depthSpan;
  const world = modelRoot.localToWorld(p.clone());
  const meshes = [];
  modelObject.traverse((n) => { if (n.isMesh && !n.userData.isHighlight) meshes.push(n); });
  if (!meshes.length) return;
  const cast = (origin, d) => {
    const rc = new THREE.Raycaster(origin, d, 0, span * 2);
    const h = rc.intersectObjects(meshes, false);
    return h.length ? h[0].point : null;
  };
  const a = cast(world.clone().addScaledVector(dir, -span), dir);
  const b = cast(world.clone().addScaledVector(dir, span), dir.clone().negate());
  if (!a || !b) return;   // neben dem Modell → Tiefe unverändert lassen
  const mid = a.clone().add(b).multiplyScalar(0.5);
  world.addScaledVector(dir, mid.sub(world).dot(dir));   // nur Tiefenanteil übernehmen
  p.copy(modelRoot.worldToLocal(world));
}

function startHumanoidFit() {
  if (!modelObject) { toast('Kein Modell geladen', true); return; }
  cancelHumanoidFit();
  setBoneTool(false);
  stopAnimPreview();
  disposeRig();   // Vorlage über dem unverformten Modell anpassen
  const { chains, depthDir, depthSpan } = humanoidChainsFromBBox();
  const group = new THREE.Group();
  const spheres = [], lines = [];
  humanoidFit = { chains, group, spheres, lines, depthDir, depthSpan };

  // Alle Punkte gleich mittig in die Tiefe des Modells setzen (jede Instanz
  // nur einmal – Becken/Hals sind zwischen den Ketten geteilt)
  const unique = [...new Set(chains.flatMap((c) => c.points))];
  unique.forEach((p) => centerTemplateDepth(p));

  const size = handleSize() * 1.1;
  const roots = new Set(chains.map((c) => c.points[0]));
  chains.forEach((chain) => {
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(chain.points),
      new THREE.LineBasicMaterial({ color: '#ff9f1c', depthTest: false, transparent: true, opacity: 0.95 }));
    line.renderOrder = 11;
    line.userData.isRigVisual = true;
    group.add(line);
    lines.push({ line, chain });
  });
  unique.forEach((p) => {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(size, 16, 12),
      new THREE.MeshBasicMaterial({ color: roots.has(p) ? '#06d6a0' : '#ff9f1c', depthTest: false, transparent: true, opacity: 0.95 }));
    sphere.renderOrder = 12;
    sphere.userData.isRigVisual = true;
    sphere.position.copy(p);
    sphere.userData.point = p;   // geteilte Instanz → Becken bewegt Torso UND Beine
    group.add(sphere);
    spheres.push(sphere);
  });
  modelRoot.add(group);
  updateHumanoidButtons();
  toast('Schablone eingeblendet – Punkte nur in Höhe/Seite ziehen, die Tiefe bleibt automatisch mittig im Modell. Dann „✓ Übernehmen"');
}

function updateHumanoidGizmo() {
  if (!humanoidFit) return;
  for (const { line, chain } of humanoidFit.lines) line.geometry.setFromPoints(chain.points);
}

function cancelHumanoidFit() {
  if (!humanoidFit) return;
  modelRoot.remove(humanoidFit.group);
  humanoidFit.group.traverse((n) => { n.geometry?.dispose(); n.material?.dispose(); });
  humanoidFit = null;
  templateDrag = null;
  updateHumanoidButtons();
}

function updateHumanoidButtons() {
  const on = !!humanoidFit;
  $('btn-humanoid')?.classList.toggle('active', on);
  if ($('btn-humanoid-apply')) $('btn-humanoid-apply').hidden = !on;
  if ($('btn-humanoid-cancel')) $('btn-humanoid-cancel').hidden = !on;
}

/** Abstand² Punkt → Polylinie (für die Dreiecks-Zuordnung der Schablone). */
function distToChainSq(points, px, py, pz) {
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
    const len2 = abx * abx + aby * aby + abz * abz || 1e-12;
    let u = ((px - a.x) * abx + (py - a.y) * aby + (pz - a.z) * abz) / len2;
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const dx = px - (a.x + abx * u), dy = py - (a.y + aby * u), dz = pz - (a.z + abz * u);
    const d = dx * dx + dy * dy + dz * dz;
    if (d < best) best = d;
  }
  return best;
}

/** Schablone übernehmen: Dreiecke zuordnen, Segmente + Skelett anlegen. */
function applyHumanoidTemplate() {
  if (!humanoidFit || !modelObject) return;
  const chains = humanoidFit.chains;

  // 1) Jedes Dreieck der nächstgelegenen Kette zuordnen (über den Schwerpunkt)
  const sels = chains.map(() => ({}));
  meshList.forEach((entry, mi) => {
    const mc = ensureModelCentroids(entry);
    const masks = chains.map(() => null);
    for (let t = 0; t < entry.triCount; t++) {
      const px = mc[t * 3], py = mc[t * 3 + 1], pz = mc[t * 3 + 2];
      let best = 0, bestD = Infinity;
      for (let k = 0; k < chains.length; k++) {
        const d = distToChainSq(chains[k].points, px, py, pz);
        if (d < bestD) { bestD = d; best = k; }
      }
      (masks[best] ||= new Uint8Array(entry.triCount))[t] = 1;
    }
    masks.forEach((m, k) => { if (m) sels[k][mi] = m; });
  });

  pushUndo();
  stopAnimPreview();

  // 2) Die Schablone ersetzt das GESAMTE Skelett: bestehende Knochenlinien
  //    (samt ihrer Keyframes) entfernen, sonst konkurrieren alte animierte
  //    Segmente mit den neuen Körperteil-Ketten um die Dreiecke. Segmente
  //    selbst bleiben erhalten; alles ist per Cmd/Strg+Z rückholbar.
  for (const b of [...skeleton.bones]) dropBone(b.segment);

  // Gleichnamige alte Segmente ersetzen
  for (const c of chains) {
    const i = state.segments.findIndex((s) => s.name === c.seg);
    if (i >= 0) {
      dropBone(c.seg);
      removeHighlights(state.segments[i].highlights);
      invalidateSegGeomCache(c.seg);
      state.segments.splice(i, 1);
    }
  }

  // 3) Neue Segmente + Knochenketten mit fester Eltern-Hierarchie (→ torso)
  chains.forEach((c, k) => {
    const seg = {
      name: c.seg,
      color: SEGMENT_COLORS[k % SEGMENT_COLORS.length],
      visible: false,   // Overlay bei Bedarf im Tab „Segmente" einblenden
      sel: sels[k],
      gesture: null,
      highlights: [],
    };
    state.segments.push(seg);
    skeleton.bones.push({
      id: 'b' + (boneCounter++),
      segment: c.seg,
      ...(c.parent ? { parent: c.parent } : {}),
      joints: c.points.map((p) => ({ id: 'j' + (jointCounter++), rest: [p.x, p.y, p.z] })),
    });
  });

  posePos = {};
  selectedJointId = null;
  activeSegName = 'torso';
  cancelHumanoidFit();
  state.dirty = true;
  renderSegmentList();
  renderSkeletonUi();
  if (skeletonTabActive) buildRig();
  toast('Humanoid-Skelett angelegt: torso, kopf, arm_links/-rechts, bein_links/-rechts. Arme/Beine/Kopf folgen dem Torso – Speichern nicht vergessen');
}

// --- Animations-UI (Segment wählen, Knochen, Pose) ---

function selectJoint(id) {
  selectedJointId = id;
  if (rig) updateRig();
}

function renderSkeletonUi() {
  renderSegmentChooser();
  renderPoseList();
  renderAnimUi();
}

/** Segment-Liste mit aktiver Auswahl; zeigt, ob ein Knochen vorhanden ist. */
function renderSegmentChooser() {
  const ul = $('joint-list');
  if (!ul) return;
  ul.innerHTML = '';
  if (!state.segments.length) {
    ul.innerHTML = '<li class="empty">Erst im Tab „Segmente" ein Segment markieren,<br>dann hier wählen und einen Knochen zeichnen.</li>';
    return;
  }
  if (activeSegName && !segmentByName(activeSegName)) activeSegName = null;
  if (!activeSegName) activeSegName = state.segments[0].name;
  const animatedNames = new Set(
    skeleton.bones.filter((b) => { const s = segmentByName(b.segment); return s && b.joints.length >= 2; })
      .map((b) => b.segment));
  state.segments.forEach((seg) => {
    const li = document.createElement('li');
    li.className = seg.name === activeSegName ? 'selected' : '';
    const bone = boneOfSegment(seg.name);
    const tag = bone ? `🦴 ${bone.joints.length - 1} Knochen` : 'kein Knochen';
    // Schloss nur zeigen, wenn ein Eltern-Segment existiert (dann ist „mitbewegen" sinnvoll)
    const parentName = bone ? findParentSegment(seg.name, animatedNames) : null;
    const lockBtn = parentName
      ? `<button class="icon-btn ${seg.locked ? 'active' : ''}" data-act="lock" title="${seg.locked
          ? `🔒 Fest: bleibt an seiner Position, wenn „${escapeHtml(parentName)}" sich bewegt. Klick: mitbewegen.`
          : `🔓 Bewegt sich mit „${escapeHtml(parentName)}" mit. Klick: Position sperren (bleibt fix).`}">${seg.locked ? '🔒' : '🔓'}</button>`
      : '';
    li.innerHTML = `<span class="seg-name">${escapeHtml(seg.name)} <small>${tag}</small></span>` + lockBtn +
      (bone
        ? `<button class="icon-btn" data-act="redraw" title="Knochen neu zeichnen (ersetzt die bestehende Linie)">✎</button>
           <button class="icon-btn" data-act="del" title="Knochenlinie dieses Segments löschen">🗑</button>`
        : '');
    li.addEventListener('click', (e) => {
      if (e.target.closest('.icon-btn')) return;
      activeSegName = seg.name;
      setBoneTool(false);
      renderSkeletonUi();
    });
    li.querySelector('[data-act=lock]')?.addEventListener('click', () => {
      pushUndo();
      seg.locked = !seg.locked;
      state.dirty = true;
      buildRig();
      renderSkeletonUi();
      renderSegmentList();
      toast(seg.locked
        ? `„${seg.name}" bleibt fix – folgt „${parentName}" nicht mehr`
        : `„${seg.name}" bewegt sich mit „${parentName}" mit`);
    });
    li.querySelector('[data-act=redraw]')?.addEventListener('click', () => {
      activeSegName = seg.name;
      renderSkeletonUi();
      setBoneTool(true);
      toast(`„${seg.name}" neu zeichnen: erst Startpunkt, dann Endpunkt klicken – die alte Linie wird ersetzt`);
    });
    li.querySelector('[data-act=del]')?.addEventListener('click', () => {
      if (confirm(`Knochenlinie von „${seg.name}" löschen?`)) removeBoneOfSegment(seg.name);
    });
    ul.appendChild(li);
  });
}

function removeBoneOfSegment(name) {
  if (!boneOfSegment(name)) return;
  pushUndo();
  dropBone(name);
  state.dirty = true;
  buildRig();
  renderSkeletonUi();
  toast(`Knochenlinie von „${name}" gelöscht`);
}

/** Den aktuell gewählten Knochenpunkt/das Gelenk entfernen. */
function removeSelectedJoint() {
  const f = findJoint(selectedJointId);
  if (!f) { toast('Erst einen Knochenpunkt im Viewer anklicken', true); return; }
  const { bone, index } = f;
  // Bleiben weniger als 2 Punkte übrig → ganze Linie löschen
  if (bone.joints.length <= 2) {
    if (!confirm(`Das ist der letzte Knochen von „${bone.segment}" – ganze Linie löschen?`)) return;
    removeBoneOfSegment(bone.segment);
    return;
  }
  pushUndo();
  const removedId = bone.joints[index].id;
  bone.joints.splice(index, 1);
  skeleton.animations.forEach((a) => { a.keys = a.keys.filter((k) => k.jointId !== removedId); });
  delete posePos[removedId];
  posePos = {};   // Abschnitte ändern sich → Pose zurücksetzen (wie beim Einfügen)
  selectedJointId = bone.joints[Math.min(index, bone.joints.length - 1)].id;
  state.dirty = true;
  buildRig();
  renderSkeletonUi();
  toast('Knochenpunkt entfernt');
}

$('btn-bone-tool').addEventListener('click', () => {
  if (!modelObject) { toast('Kein Modell geladen', true); return; }
  if (!activeSegName) { toast('Erst ein Segment in der Liste wählen', true); return; }
  setBoneTool(!boneTool);
  if (boneTool) {
    toast(`Knochen für „${activeSegName}" zeichnen: erst Startpunkt (z. B. Schulter), dann Endpunkt (z. B. Hand) klicken`);
  }
});

$('btn-bone-reset').addEventListener('click', () => {
  posePos = {};
  if (rig) updateRig();
  toast('Pose zurückgesetzt (Ruhestellung)');
});

$('btn-del-joint').addEventListener('click', removeSelectedJoint);

$('btn-humanoid').addEventListener('click', () => {
  if (humanoidFit) { cancelHumanoidFit(); toast('Schablone ausgeblendet'); return; }
  startHumanoidFit();
});
$('btn-humanoid-apply').addEventListener('click', applyHumanoidTemplate);
$('btn-humanoid-cancel').addEventListener('click', () => { cancelHumanoidFit(); toast('Schablone verworfen'); });

$('cfg-allow-separation').addEventListener('change', (e) => {
  allowSeparation = e.target.checked;
  state.dirty = true;
  if (rig) updateRig();
  toast(allowSeparation
    ? 'Oberfläche darf sich jetzt an den Rändern ablösen'
    : 'Alle geteilten Ränder kleben fest an den Nachbarflächen (keine Trennung)');
});

$('btn-clear-skeleton').addEventListener('click', () => {
  if (!skeleton.bones.length && !skeleton.animations.length) {
    toast('Keine Knochen/Animationen vorhanden', true);
    return;
  }
  if (!confirm('Alle Knochenlinien und Animationen löschen?')) return;
  pushUndo();
  resetSkeleton();
  state.dirty = true;
  buildRig();
  toast('Knochen & Animationen gelöscht – Speichern nicht vergessen');
});

// --- Animationen ---

function currentAnim() {
  return skeleton.animations[activeAnimIndex] || null;
}

function animDuration(anim) {
  return anim?.keys.length ? Math.max(...anim.keys.map((k) => k.t)) : 0;
}

function renderAnimUi() {
  renderAnimList();
  renderKeyframeTable();
}

/** Sichtbare Animationsliste (ersetzt das frühere Dropdown). */
function renderAnimList() {
  const ul = $('anim-list');
  if (!ul) return;
  ul.innerHTML = '';
  if (!skeleton.animations.length) {
    ul.innerHTML = '<li class="empty">Noch keine Animation – mit „＋ Neu" anlegen.</li>';
    return;
  }
  if (activeAnimIndex >= skeleton.animations.length) activeAnimIndex = skeleton.animations.length - 1;
  if (activeAnimIndex < 0) activeAnimIndex = 0;
  skeleton.animations.forEach((a, i) => {
    const li = document.createElement('li');
    li.className = 'anim-item' + (i === activeAnimIndex ? ' active' : '');
    const playing = animPlaying && animPlaying.anim === a;
    const n = a.keys.length;
    li.innerHTML = `
      <span class="anim-name">${escapeHtml(a.name)}
        <small>${animDuration(a).toFixed(1)} s · ${n} Keyframe${n === 1 ? '' : 's'}</small></span>
      <button class="icon-btn" data-act="play" title="Vorschau abspielen/stoppen">${playing ? '⏹' : '▶'}</button>
      <button class="icon-btn" data-act="del" title="Animation löschen">🗑</button>`;
    li.addEventListener('click', (e) => {
      if (e.target.closest('.icon-btn')) return;
      setActiveAnim(i);
    });
    li.querySelector('[data-act=play]').addEventListener('click', () => togglePlayAnim(i));
    li.querySelector('[data-act=del]').addEventListener('click', () => deleteAnim(i));
    ul.appendChild(li);
  });
}

function setActiveAnim(i) {
  if (i === activeAnimIndex) return;
  stopAnimPreview();
  activeAnimIndex = i;
  renderAnimList();
  renderKeyframeTable();
}

function deleteAnim(i) {
  const anim = skeleton.animations[i];
  if (!anim) return;
  if (!confirm(`Animation "${anim.name}" löschen?`)) return;
  pushUndo();
  if (animPlaying && animPlaying.anim === anim) stopAnimPreview();
  skeleton.animations.splice(i, 1);
  if (activeAnimIndex >= skeleton.animations.length) {
    activeAnimIndex = Math.max(0, skeleton.animations.length - 1);
  }
  state.dirty = true;
  renderAnimUi();
}

$('btn-add-anim').addEventListener('click', () => {
  const name = prompt('Name der Animation (z. B. "Arm heben"):',
    'Animation ' + (skeleton.animations.length + 1));
  if (!name) return;
  pushUndo();
  skeleton.animations.push({ name: name.trim(), keys: [] });
  activeAnimIndex = skeleton.animations.length - 1;
  state.dirty = true;
  renderAnimUi();
  toast(`Animation "${name.trim()}" angelegt – Punkte ziehen, Zeit setzen, „＋ Keyframe aus Pose"`);
});

/** Position weicht von der Ausgangslage (Standardpose bzw. Ruhelage) ab? */
function isPosed(jid) {
  const p = posePos[jid];
  if (!p || !jointById(jid)) return false;
  const b = jointBaseVec(jid);
  return Math.abs(p[0] - b.x) > 1e-5
      || Math.abs(p[1] - b.y) > 1e-5
      || Math.abs(p[2] - b.z) > 1e-5;
}

$('btn-add-key').addEventListener('click', () => {
  const anim = currentAnim();
  if (!anim) { toast('Erst eine Animation anlegen (＋)', true); return; }
  // Alle aktuell gezogenen Punkte keyen (ganze Kette auf einmal),
  // sonst den gewählten Punkt (z. B. um eine Ruhepose festzuhalten).
  const allJoints = skeleton.bones.flatMap((b) => b.joints.map((j) => j.id));
  let ids = allJoints.filter((jid) => isPosed(jid));
  if (!ids.length && selectedJointId && jointById(selectedJointId)) ids = [selectedJointId];
  if (!ids.length) { toast('Erst einen Punkt ziehen oder auswählen', true); return; }
  const t = Math.max(0, parseFloat($('key-time').value) || 0);
  pushUndo();
  for (const jid of ids) {
    const cur = jointCurVec(jid);
    // Keyframe bei 0, damit die Animation aus der Ausgangslage startet
    if (t > 0 && !anim.keys.some((k) => k.jointId === jid && k.t === 0)) {
      const b = jointBaseVec(jid);
      anim.keys.push({ jointId: jid, t: 0, x: b.x, y: b.y, z: b.z, ease: 'lin' });
    }
    const existing = anim.keys.find((k) => k.jointId === jid && k.t === t);
    if (existing) { existing.x = cur.x; existing.y = cur.y; existing.z = cur.z; }
    else anim.keys.push({ jointId: jid, t, x: cur.x, y: cur.y, z: cur.z, ease: 'lin' });
  }
  anim.keys.sort((a, b) => a.t - b.t || a.jointId.localeCompare(b.jointId));
  state.dirty = true;
  renderAnimUi();
  toast(`Keyframe @ ${t.toFixed(1)} s für ${ids.length} Punkt${ids.length === 1 ? '' : 'e'} gesetzt`);
});

/**
 * Keyframe „gehe zur Zeit t in diese Stellung": `pose === null` bedeutet zurück
 * in die Ausgangslage (Standardpose bzw. Ruhelage), sonst in die benannte Pose.
 * Gekeyt werden alle Punkte, die in der Animation schon vorkommen, plus die,
 * die diese Pose gegenüber der Ausgangslage überhaupt bewegt.
 */
function addPoseKeyframe(pose) {
  const anim = currentAnim();
  if (!anim) { toast('Erst eine Animation anlegen (＋)', true); return; }
  const inAnim = new Set(anim.keys.map((k) => k.jointId));
  const ids = allJointIds().filter((jid) =>
    inAnim.has(jid) || (pose && !jointBaseVec(jid).equals(poseJointVec(pose, jid))));
  if (!ids.length) {
    toast(pose
      ? `Die Pose „${pose.name}" entspricht der Ausgangslage – nichts zu keyen`
      : 'Die Animation hat noch keine Keyframes – erst Punkte ziehen und keyen', true);
    return;
  }
  const t = Math.max(0, parseFloat($('key-time').value) || 0);
  pushUndo();
  for (const jid of ids) {
    // Keyframe bei 0, damit die Bewegung aus der Ausgangslage startet
    if (t > 0 && !anim.keys.some((k) => k.jointId === jid && k.t === 0)) {
      const b = jointBaseVec(jid);
      anim.keys.push({ jointId: jid, t: 0, x: b.x, y: b.y, z: b.z, ease: 'lin' });
    }
    const p = pose ? poseJointVec(pose, jid) : jointBaseVec(jid);
    const existing = anim.keys.find((k) => k.jointId === jid && k.t === t);
    if (existing) { existing.x = p.x; existing.y = p.y; existing.z = p.z; }
    else anim.keys.push({ jointId: jid, t, x: p.x, y: p.y, z: p.z, ease: 'lin' });
  }
  anim.keys.sort((a, b) => a.t - b.t || a.jointId.localeCompare(b.jointId));
  applyPose(pose);   // Vorschau gleich in die gekeyte Stellung bringen
  state.dirty = true;
  renderAnimUi();
  const n = `${ids.length} Punkt${ids.length === 1 ? '' : 'e'}`;
  toast(pose
    ? `Wechsel in Pose „${pose.name}" @ ${t.toFixed(1)} s für ${n} gesetzt`
    : `Rückkehr zur Ausgangslage @ ${t.toFixed(1)} s für ${n} gesetzt`);
}

// „↺ Ausgangslage": die Figur kehrt zur angegebenen Zeit von selbst in die
// Ausgangsstellung zurück, ohne dass man die Punkte neu ziehen muss.
$('btn-add-rest-key').addEventListener('click', () => addPoseKeyframe(null));

function renderKeyframeTable() {
  const tbody = $('keyframe-rows');
  tbody.innerHTML = '';
  const anim = currentAnim();
  renderTimeline();
  if (!anim) return;
  anim.keys.forEach((k, idx) => {
    const tr = document.createElement('tr');
    const label = jointLabel(k.jointId);
    tr.innerHTML = `
      <td title="${escapeHtml(label)}">${escapeHtml(label.length > 16 ? label.slice(0, 15) + '…' : label)}</td>
      <td><input type="number" step="0.1" min="0" value="${k.t}" data-f="t"></td>
      <td><input type="number" step="0.01" value="${(k.x || 0).toFixed(3)}" data-f="x"></td>
      <td><input type="number" step="0.01" value="${(k.y || 0).toFixed(3)}" data-f="y"></td>
      <td><input type="number" step="0.01" value="${(k.z || 0).toFixed(3)}" data-f="z"></td>
      <td><select data-f="ease" title="Interpolation zum vorigen Keyframe">
        <option value="lin"${k.ease !== 'smooth' ? ' selected' : ''}>–</option>
        <option value="smooth"${k.ease === 'smooth' ? ' selected' : ''}>S</option>
      </select></td>
      <td><button class="icon-btn" title="Keyframe löschen">🗑</button></td>`;
    tr.querySelectorAll('input').forEach((inp) => {
      inp.addEventListener('change', () => {
        pushUndo();
        k[inp.dataset.f] = parseFloat(inp.value) || 0;
        if (inp.dataset.f === 't') anim.keys.sort((a, b) => a.t - b.t);
        state.dirty = true;
        renderAnimUi();
      });
    });
    tr.querySelector('select').addEventListener('change', (e) => {
      pushUndo();
      k.ease = e.target.value;
      state.dirty = true;
    });
    tr.querySelector('button').addEventListener('click', () => {
      pushUndo();
      anim.keys.splice(idx, 1);
      state.dirty = true;
      renderAnimUi();
    });
    tbody.appendChild(tr);
  });
}

// --- Zeitleiste mit Scrubber ---

function timelineMax() {
  return Math.max(3, animDuration(currentAnim()));
}

function renderTimeline() {
  const anim = currentAnim();
  const max = timelineMax();
  const scrub = $('timeline-scrub');
  scrub.max = max;
  const marks = $('timeline-marks');
  marks.innerHTML = '';
  if (anim) {
    for (const k of anim.keys) {
      const dot = document.createElement('span');
      dot.className = 'timeline-mark';
      dot.style.left = (k.t / max * 100) + '%';
      dot.title = `${jointLabel(k.jointId)} @ ${k.t.toFixed(1)} s`;
      dot.addEventListener('click', () => {
        scrub.value = k.t;
        scrubTo(k.t);
      });
      marks.appendChild(dot);
    }
  }
}

/** Rig auf den Animationszustand zur Zeit t stellen (reine Vorschau). */
function setRigToAnimTime(anim, t) {
  if (!rig) return;
  for (const bone of skeleton.bones) {
    for (const j of bone.joints) {
      const p = evalJointPose(anim, j.id, t);
      if (p) posePos[j.id] = [p.x, p.y, p.z];
      else delete posePos[j.id];   // keine Keys → Ruheposition
    }
  }
  updateRig();
}

function scrubTo(t) {
  $('timeline-time').textContent = t.toFixed(1).replace('.', ',') + ' s';
  $('key-time').value = t;
  const anim = currentAnim();
  if (anim && anim.keys.length) {
    if (!rig) buildRig();
    setRigToAnimTime(anim, t);
  }
}

$('timeline-scrub').addEventListener('input', () => {
  stopAnimPreview();
  scrubTo(parseFloat($('timeline-scrub').value) || 0);
});

$('key-time').addEventListener('change', () => {
  const t = Math.max(0, parseFloat($('key-time').value) || 0);
  $('timeline-scrub').value = Math.min(t, timelineMax());
  $('timeline-time').textContent = t.toFixed(1).replace('.', ',') + ' s';
});

/** Position eines Punkts zur Zeit t interpolieren (linear oder sanft). */
function evalJointPose(anim, jointId, t) {
  const keys = anim.keys.filter((k) => k.jointId === jointId);
  if (!keys.length) return null;
  if (t <= keys[0].t) return keys[0];
  const last = keys[keys.length - 1];
  if (t >= last.t) return last;
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i], b = keys[i + 1];
    if (t >= a.t && t <= b.t) {
      let f = (b.t - a.t) > 0 ? (t - a.t) / (b.t - a.t) : 0;
      if (b.ease === 'smooth') f = f * f * (3 - 2 * f);   // Smoothstep
      return {
        x: a.x + (b.x - a.x) * f,
        y: a.y + (b.y - a.y) * f,
        z: a.z + (b.z - a.z) * f,
      };
    }
  }
  return last;
}

function stopAnimPreview() {
  if (!animPlaying) return;
  animPlaying = null;
  posePos = {};      // zurück in die Ruhestellung
  if (rig) updateRig();
  renderAnimList();  // ▶ in der Liste zurücksetzen
}

/** Vorschau der Animation i abspielen bzw. stoppen. */
function togglePlayAnim(i) {
  const anim = skeleton.animations[i];
  if (animPlaying && animPlaying.anim === anim) { stopAnimPreview(); return; }
  stopAnimPreview();
  activeAnimIndex = i;
  if (!anim || !anim.keys.length) {
    toast('Die Animation hat noch keine Keyframes', true);
    renderAnimList();
    return;
  }
  if (!rig) buildRig();
  animPlaying = { anim, t0: performance.now() };
  renderAnimList();
  renderKeyframeTable();
  const dur = Math.max(animDuration(anim), 0.01);
  const step = () => {
    if (!animPlaying || !rig) return;
    const t = ((performance.now() - animPlaying.t0) / 1000) % dur;
    setRigToAnimTime(anim, t);
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// --- Posen-Bibliothek ---

/** Pose in die Vorschau übernehmen (null = Ausgangslage). */
function applyPose(pose) {
  posePos = {};
  if (pose) {
    for (const jid of allJointIds()) {
      const p = pose.joints[jid];
      if (p) posePos[jid] = [...p];
    }
  }
  if (rig) updateRig();
}

/** Wie viele Punkte weicht die Pose von der modellierten Ruhelage ab? */
function poseMovedCount(pose) {
  return allJointIds().filter((jid) => {
    const j = jointById(jid), p = pose.joints[jid];
    return j && p && jointRestVec(j).distanceToSquared(new THREE.Vector3(p[0], p[1], p[2])) > 1e-10;
  }).length;
}

function addPose() {
  if (!skeleton.bones.length) { toast('Erst einen Knochen zeichnen', true); return; }
  const name = prompt('Name der Pose (z. B. "Arme unten"):', 'Pose ' + (skeleton.poses.length + 1));
  if (!name || !name.trim()) return;
  pushUndo();
  const pose = { id: 'p' + poseCounter++, name: name.trim(), joints: captureJoints() };
  skeleton.poses.push(pose);
  state.dirty = true;
  renderPoseList();
  toast(`Pose „${pose.name}" gespeichert – mit ☆ zur Standardpose machen`);
}

/** Pose mit der aktuellen Stellung der Punkte überschreiben. */
function updatePoseFromCurrent(i) {
  const pose = skeleton.poses[i];
  if (!pose) return;
  if (!confirm(`Pose "${pose.name}" mit der aktuellen Stellung überschreiben?`)) return;
  pushUndo();
  pose.joints = captureJoints();
  state.dirty = true;
  renderPoseList();
  if (rig) updateRig();      // war es die Standardpose, verschiebt sich die Ausgangslage
  toast(`Pose „${pose.name}" aktualisiert`);
}

function renamePose(i) {
  const pose = skeleton.poses[i];
  if (!pose) return;
  const name = prompt('Pose umbenennen:', pose.name);
  if (!name || !name.trim() || name.trim() === pose.name) return;
  pushUndo();
  pose.name = name.trim();
  state.dirty = true;
  renderPoseList();
}

function deletePose(i) {
  const pose = skeleton.poses[i];
  if (!pose) return;
  if (!confirm(`Pose "${pose.name}" löschen?`)) return;
  pushUndo();
  const wasDefault = skeleton.defaultPoseId === pose.id;
  skeleton.poses.splice(i, 1);
  if (wasDefault) { skeleton.defaultPoseId = null; applyPose(null); }
  state.dirty = true;
  renderPoseList();
  toast(wasDefault
    ? `Pose „${pose.name}" gelöscht – Ausgangslage ist wieder die Ruhelage`
    : `Pose „${pose.name}" gelöscht`);
}

/**
 * Pose als Standardpose (Ausgangslage) setzen; erneuter Klick hebt sie auf.
 * Ab dann steht das Modell in dieser Stellung, sobald nichts gezogen ist –
 * Animationen starten dort, und „↺ Ausgangslage" kehrt dorthin zurück.
 */
function toggleDefaultPose(i) {
  const pose = skeleton.poses[i];
  if (!pose) return;
  pushUndo();
  const on = skeleton.defaultPoseId !== pose.id;
  skeleton.defaultPoseId = on ? pose.id : null;
  applyPose(null);           // Vorschau auf die neue Ausgangslage stellen
  state.dirty = true;
  renderPoseList();
  renderKeyframeTable();
  toast(on
    ? `„${pose.name}" ist jetzt die Standardpose – wird beim Speichern übernommen`
    : 'Standardpose aufgehoben – Ausgangslage ist wieder die Ruhelage');
}

function renderPoseList() {
  const ul = $('pose-list');
  if (!ul) return;
  ul.innerHTML = '';
  if (!skeleton.poses.length) {
    ul.innerHTML = '<li class="empty">Noch keine Pose – Punkte im Viewer ziehen,<br>dann „＋ Aus Stellung" klicken.</li>';
    return;
  }
  const total = allJointIds().length;
  skeleton.poses.forEach((pose, i) => {
    const isDefault = skeleton.defaultPoseId === pose.id;
    const moved = poseMovedCount(pose);
    const li = document.createElement('li');
    li.className = 'anim-item pose-item' + (isDefault ? ' is-default' : '');
    li.innerHTML = `
      <span class="anim-name">${escapeHtml(pose.name)}
        <small>${isDefault ? 'Standardpose · ' : ''}${moved} von ${total} Punkten bewegt</small></span>
      <button class="icon-btn" data-act="def" title="${isDefault
        ? 'Standardpose aufheben – Ausgangslage wird wieder die modellierte Ruhelage'
        : 'Als Standardpose setzen: Das Modell steht künftig in dieser Stellung, Animationen starten hier. Wird mit „Speichern" in der config.json abgelegt.'}">${isDefault ? '⭐' : '☆'}</button>
      <button class="icon-btn" data-act="key" title="Keyframe „gehe in diese Pose" zur eingestellten Zeit in die aktive Animation setzen">🔑</button>
      <button class="icon-btn" data-act="upd" title="Pose mit der aktuellen Stellung der Punkte überschreiben">⟳</button>
      <button class="icon-btn" data-act="del" title="Pose löschen">🗑</button>`;
    li.title = 'Klick: Pose im Viewer einnehmen · Doppelklick: umbenennen';
    li.addEventListener('click', (e) => {
      if (e.target.closest('.icon-btn') || e.detail >= 2) return;
      applyPose(pose);
    });
    li.addEventListener('dblclick', (e) => {
      if (e.target.closest('.icon-btn')) return;
      renamePose(i);
    });
    li.querySelector('[data-act=def]').addEventListener('click', () => toggleDefaultPose(i));
    li.querySelector('[data-act=key]').addEventListener('click', () => addPoseKeyframe(pose));
    li.querySelector('[data-act=upd]').addEventListener('click', () => updatePoseFromCurrent(i));
    li.querySelector('[data-act=del]').addEventListener('click', () => deletePose(i));
    ul.appendChild(li);
  });
}

$('btn-add-pose').addEventListener('click', addPose);

// --- Persistenz Skelett ---

function skeletonToConfig() {
  if (!state.config) return;
  state.config.skeleton = {
    bones: skeleton.bones.map((b) => ({
      id: b.id, segment: b.segment,
      ...(b.parent ? { parent: b.parent } : {}),
      joints: b.joints.map((j) => ({ id: j.id, rest: [...j.rest] })),
    })),
    animations: skeleton.animations.map((a) => ({
      name: a.name, keys: a.keys.map((k) => ({ ...k })),
    })),
    poses: skeleton.poses.map((p) => ({ id: p.id, name: p.name, joints: { ...p.joints } })),
    defaultPoseId: skeleton.defaultPoseId,
    allowSeparation,
  };
}

function restoreSkeletonFromConfig() {
  resetSkeleton();
  const s = state.config?.skeleton;
  allowSeparation = !!s?.allowSeparation;
  if ($('cfg-allow-separation')) $('cfg-allow-separation').checked = allowSeparation;
  if (s && Array.isArray(s.bones)) {
    skeleton.bones = s.bones.map((b) => ({
      id: b.id, segment: b.segment,
      ...(b.parent ? { parent: b.parent } : {}),
      joints: (b.joints || []).map((j) => ({ id: j.id, rest: [...j.rest] })),
    }));
    skeleton.animations = (s.animations || []).map((a) => ({
      name: a.name, keys: (a.keys || []).map((k) => ({ ...k })),
    }));
    skeleton.poses = (s.poses || []).map((p) => ({
      id: p.id, name: p.name, joints: { ...(p.joints || {}) },
    }));
    // Standardpose nur übernehmen, wenn es sie noch gibt
    skeleton.defaultPoseId = poseById(s.defaultPoseId) ? s.defaultPoseId : null;
    const jids = skeleton.bones.flatMap((b) => b.joints.map((j) => j.id));
    jointCounter = jids.reduce((m, id) => Math.max(m, parseInt(String(id).replace(/\D/g, ''), 10) || 0), 0) + 1;
    boneCounter = skeleton.bones.reduce((m, b) => Math.max(m, parseInt(String(b.id).replace(/\D/g, ''), 10) || 0), 0) + 1;
    poseCounter = skeleton.poses.reduce((m, p) => Math.max(m, parseInt(String(p.id).replace(/\D/g, ''), 10) || 0), 0) + 1;
    activeSegName = skeleton.bones[0]?.segment || null;
  }
  renderSkeletonUi();
  if (skeletonTabActive) buildRig();
}

// --- Export: animiertes USDZ (Hierarchie + Timesamples) ---

async function exportAnimatedUsdz(animOverride = null, variantOverride = null) {
  if (!modelObject || !meshList.length) { toast('Kein Modell geladen', true); return false; }
  const usableBones = skeleton.bones.filter((b) => { const s = segmentByName(b.segment); return s && b.joints.length >= 2; });
  if (!usableBones.length) { toast('Erst einen Knochen zeichnen (Tab „Animation")', true); return false; }

  const anim = animOverride || currentAnim();
  const animated = anim && anim.keys.length > 0;
  const ratio = exportRatio('export-quality-skel');
  const variant = variantOverride || 'animated';
  toast(animated
    ? `Erzeuge animiertes USDZ („${anim.name}")…`
    : 'Erzeuge USDZ mit Knochen (ohne Animation)…');
  await new Promise((r) => setTimeout(r, 30));

  try {
    if (ratio < 1) await MeshoptSimplifier.ready;

    const usedNames = new Set();
    const uniqueName = (base) => {
      let n = base, i = 2;
      while (usedNames.has(n)) n = base + '_' + i++;
      usedNames.add(n);
      return n;
    };

    const exportScene = new THREE.Scene();
    const root = new THREE.Group();
    root.userData.usdName = uniqueName(toUsdName(state.config.name || state.config.slug));
    exportScene.add(root);

    const frames = animated ? Math.max(1, Math.round(animDuration(anim) * ANIM_FPS)) : 0;

    // Pro Segment ein geskinntes Mesh je Quell-Mesh (wie in der Vorschau) –
    // KEINE rigide Aufteilung pro Knochen mehr. Jeder Vertex wird weich über
    // alle Abschnitte verformt; an den geteilten Rändern hält das Glue die Naht
    // fest, sodass nichts aufreißt. Die Deformation wird Frame für Frame als
    // point timeSamples gebacken (identisch zur Live-Vorschau).
    scene.updateMatrixWorld(true);
    const invRoot = modelObject.matrixWorld.clone().invert();
    const animatedNames = new Set(usableBones.map((b) => b.segment));
    const lockedNames = new Set(state.segments.filter((s) => s.locked).map((s) => s.name));
    const owners = triOwners(animatedNames, lockedNames);
    const animatedSegs = new Set();
    for (const bone of usableBones) {
      animatedSegs.add(bone.segment);
      const seg = segmentByName(bone.segment);
      const restJoints = bone.joints.map((j) => jointRestVec(j));
      let total = 0;
      for (let i = 0; i < restJoints.length - 1; i++) total += restJoints[i].distanceTo(restJoints[i + 1]);
      const band = ROOT_GLUE_FRAC * (total || 1e-6);
      const jointIds = bone.joints.map((j) => j.id);

      const parentInfo = parentInfoFor(seg, restJoints[0], animatedNames);   // „bewegt sich mit"
      // Geskinnte Geometrien (ggf. dezimiert) + Abschnittszuordnung aufbauen –
      // exklusive Auswahl, damit ein Dreieck nicht von zwei Segmenten kommt
      let eff = exclusiveSelOf(bone.segment, owners);
      if (!Object.keys(eff).length) eff = seg.sel;
      const built = [];
      for (const mi in eff) {
        const entry = meshList[mi];
        if (!entry) continue;
        const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
        let geo = buildSubsetGeometry(entry, eff[mi], rel);
        if (!geo) continue;
        if (ratio < 1) geo = decimateGeometry(geo, ratio);
        const rest = new Float32Array(geo.attributes.position.array);
        const section = assignSections(rest, restJoints);
        // je Vertex der nächste ELTERN-Abschnitt (nahtdichter Anker)
        const psec = parentInfo ? assignSections(rest, parentInfo.restJoints) : null;
        const boundary = subsetBoundaryFlags(geo);
        const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
        built.push({ geo, rest, section, psec, boundary, mat });
      }
      const glueSkins = built.map((b) => ({ rest: b.rest, boundary: b.boundary }));
      try {
        // nur echte (geteilte) Nähte verankern; Nähte zu Kind-Segmenten bleiben frei
        restrictSeamsToOutside(glueSkins, eff, childSelsOf(bone.segment, animatedNames, owners));
      } catch (err) {
        console.warn('Außen-Naht-Filter übersprungen:', err);
      }
      const glues = computeSeamGlue(glueSkins, band);

      const base = toUsdName(bone.segment);
      built.forEach((b, bi) => {
        const glue = glues[bi];
        const mesh = new THREE.Mesh(b.geo, b.mat);
        mesh.userData.usdName = uniqueName(bi === 0 ? base : base + '_' + (bi + 1));
        if (animated) {
          const samples = [];
          const out = new Float32Array(b.rest.length);
          for (let f = 0; f <= frames; f++) {
            const t = f / ANIM_FPS;
            const cur = jointIds.map((id) => {
              const pp = evalJointPose(anim, id, t);
              return pp ? new THREE.Vector3(pp.x, pp.y, pp.z) : jointBaseVec(id);
            });
            const Ms = boneMatrices(restJoints, cur);
            // Eltern folgt mit; pMs/psec = nahtdichter Per-Vertex-Anker
            const pMs = evalParentMatrices(parentInfo, anim, t);
            const post = pMs ? (pMs[parentInfo.section] || null) : null;
            deformPositions(out, b.rest, b.section, glue, Ms, allowSeparation, post, pMs, b.psec);
            samples.push({ time: f, points: out.slice() });
          }
          mesh.userData.usdPointSamples = samples;
        }
        root.add(mesh);
      });
    }

    // Gesperrte Segmente OHNE eigenen Knochen als statische benannte Knoten.
    // (Gesperrte MIT Knochen animieren selbst; nicht gesperrte Teilsegmente
    // sind bereits Teil ihres animierten Eltern-Segments.)
    for (const seg of state.segments.filter((s) => s.locked && !boneOfSegment(s.name))) {
      const sel = exclusiveSelOf(seg.name, owners);
      const parts = [];
      for (const mi in sel) {
        const entry = meshList[mi];
        if (!entry) continue;
        const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
        let geo = buildSubsetGeometry(entry, sel[mi], rel);
        if (!geo) continue;
        if (ratio < 1) geo = decimateGeometry(geo, ratio);
        const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
        parts.push({ geo, mat });
      }
      if (!parts.length) continue;
      const bbox = new THREE.Box3();
      parts.forEach(({ geo }) => { geo.computeBoundingBox(); bbox.union(geo.boundingBox); });
      const center = bbox.getCenter(new THREE.Vector3());
      const base = toUsdName(seg.name);
      parts.forEach(({ geo, mat }, i) => {
        geo.translate(-center.x, -center.y, -center.z);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.copy(center);
        mesh.userData.usdName = uniqueName(i === 0 ? base : base + '_' + (i + 1));
        root.add(mesh);
      });
    }

    // Rest = herrenlose Dreiecke (kein Segment beansprucht sie)
    unownedParts(owners).forEach(({ geo, mat }) => {
      const exportGeo = ratio < 1 ? decimateGeometry(geo, ratio) : geo;
      const mesh = new THREE.Mesh(exportGeo, mat);
      mesh.userData.usdName = uniqueName('Rest');
      root.add(mesh);
    });
    const exporter = new USDZExporter();
    const data = await exporter.parseAsync(exportScene, {
      hierarchy: true,
      timeCodes: animated ? { start: 0, end: frames, fps: ANIM_FPS } : null,
    });
    const blob = new Blob([data], { type: 'model/vnd.usdz+zip' });
    const suffix = variant === 'animated' ? 'animated' : variant;
    const filename = `${state.config.slug}-${suffix}.usdz`;

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);

    try {
      await api('segmented&variant=' + encodeURIComponent(variant)
        + '&slug=' + encodeURIComponent(state.config.slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      });
      toast(`${filename} exportiert (${(blob.size / 1024 / 1024).toFixed(1)} MB) und auf dem Server gespeichert`);
    } catch (err) {
      toast(`${filename} heruntergeladen – Server-Kopie fehlgeschlagen: ${err.message}`, true);
    }
    return true;
  } catch (err) {
    console.error('Animations-Export fehlgeschlagen:', err);
    toast('Export fehlgeschlagen: ' + err.message, true);
    return false;
  }
}

/** Jede Animation als eigene USDZ-Datei exportieren (USD: 1 Zeitachse/Datei). */
async function exportAllAnimations() {
  const anims = skeleton.animations.filter((a) => a.keys.length);
  if (!anims.length) {
    toast('Keine Animationen mit Keyframes vorhanden', true);
    return;
  }
  for (let i = 0; i < anims.length; i++) {
    toast(`Exportiere Animation ${i + 1}/${anims.length}: „${anims[i].name}"…`);
    const ok = await exportAnimatedUsdz(anims[i],
      'anim-' + toUsdName(anims[i].name).toLowerCase());
    if (!ok) return;
  }
  toast(`${anims.length} Animation${anims.length === 1 ? '' : 'en'} exportiert`);
}

$('btn-export-all-anims').addEventListener('click', exportAllAnimations);

$('btn-export-skel').addEventListener('click', () => exportAnimatedUsdz());

// --- RealityKit-Export: ein UsdSkel-Rig mit ALLEN Animationen ---------------
//
// Ein Skelett (Anker-Joint „Root" + je Knochen-Abschnitt ein Joint) trägt die
// Hülle per Skinning-Gewichten. Die Gewichte kommen aus der Naht-Verankerung:
// jeder Vertex hängt zu (1−g) am festen Root-Anker und zu g an seinem
// Abschnitts-Joint – exakt die Verformung aus der Vorschau (per Test bewiesen),
// also reißt nichts auf. Alle Animationen werden auf EINER Zeitachse
// hintereinandergelegt; eine mitgelieferte Swift-Datei schneidet sie per
// Frame-Bereich in benannte Clips, die RealityKit als skeletale Animation
// erkennt.

/** Eindeutigen, USD-konformen Bezeichner aus einer Menge erzeugen. */
function makeUniquer() {
  const used = new Set();
  return (base) => { let n = base || 'X', i = 2; while (used.has(n)) n = base + '_' + i++; used.add(n); return n; };
}

/** Datei im Browser herunterladen. */
function downloadBlob(blob, filename) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

/** "mein-modell" → "MeinModell" (für Swift-Typnamen). */
function toPascalCase(s) {
  return String(s || 'Model').split(/[^A-Za-z0-9]+/).filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('') || 'Model';
}

/** "Linker Fuß" → "linkerFuss" (Swift-Case-Bezeichner). */
function toCamelCase(s) {
  const parts = toUsdName(s).split('_').filter(Boolean);
  if (!parts.length) return 'clip';
  return parts.map((p, i) => i === 0 ? p.charAt(0).toLowerCase() + p.slice(1)
    : p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

/**
 * Gewichts-Verschweißung: Alle Vertices, die in der RUHELAGE aufeinander
 * liegen (Nahtringe zwischen Teil-Meshes und Segmenten), bekommen exakt
 * DENSELBEN gemittelten Gewichtssatz. Danach können sie sich unter keiner
 * Pose mehr auseinanderbewegen – die Oberfläche dehnt sich, statt zu reißen.
 * Hebt dafür alle Meshes einheitlich auf 4 Einflüsse je Vertex (glTF-Norm;
 * UsdSkel-elementSize wird entsprechend 4). Gruppiert wird über ein feines
 * Positionsraster (~0,01 % der Modellgröße, wie beim Dezimieren).
 */
function weldSkinWeights(meshesOut) {
  // 1) Alles auf elementSize 4 heben
  for (const m of meshesOut) {
    if (m.elementSize === 4) continue;
    const n = m.geometry.attributes.position.count;
    const ji = new Array(n * 4).fill(0), jw = new Array(n * 4).fill(0);
    for (let v = 0; v < n; v++)
      for (let k = 0; k < m.elementSize; k++) {
        ji[v * 4 + k] = m.jointIndices[v * m.elementSize + k];
        jw[v * 4 + k] = m.jointWeights[v * m.elementSize + k];
      }
    m.jointIndices = ji; m.jointWeights = jw; m.elementSize = 4;
  }
  // 2) Koinzidente Positionen über alle Meshes gruppieren
  let min = Infinity, max = -Infinity;
  for (const m of meshesOut) {
    const pos = m.geometry.attributes.position.array;
    for (let i = 0; i < pos.length; i++) {
      if (pos[i] < min) min = pos[i];
      if (pos[i] > max) max = pos[i];
    }
  }
  const cell = (max > min ? max - min : 1) * 1e-4;
  const groups = new Map();   // Rasterschlüssel → flaches Array [meshIdx, vertIdx, …]
  meshesOut.forEach((m, mi) => {
    const pos = m.geometry.attributes.position.array;
    const n = m.geometry.attributes.position.count;
    for (let v = 0; v < n; v++) {
      const k = Math.round(pos[v * 3] / cell) + '|' + Math.round(pos[v * 3 + 1] / cell) + '|' + Math.round(pos[v * 3 + 2] / cell);
      let a = groups.get(k); if (!a) { a = []; groups.set(k, a); }
      a.push(mi, v);
    }
  });
  // 3) Je Gruppe: Gewichte je Joint mitteln, Top-4 renormieren, allen Mitgliedern
  //    denselben Satz geben
  for (const a of groups.values()) {
    if (a.length <= 2) continue;   // nur ein Vertex in der Zelle
    const count = a.length / 2;
    const acc = new Map();   // Joint-Index → gemitteltes Gewicht
    let identical = true;
    let ref = null;
    for (let j = 0; j < a.length; j += 2) {
      const m = meshesOut[a[j]], v = a[j + 1];
      const key = m.jointIndices.slice(v * 4, v * 4 + 4) + '|' + m.jointWeights.slice(v * 4, v * 4 + 4);
      if (ref === null) ref = key; else if (key !== ref) identical = false;
      for (let k = 0; k < 4; k++) {
        const w = m.jointWeights[v * 4 + k];
        if (!(w > 0)) continue;
        const jid = m.jointIndices[v * 4 + k];
        acc.set(jid, (acc.get(jid) || 0) + w / count);
      }
    }
    if (identical) continue;   // schon deckungsgleich → nichts zu tun
    const top = [...acc.entries()].sort((x, y) => y[1] - x[1]).slice(0, 4);
    const sum = top.reduce((s, e) => s + e[1], 0) || 1;
    for (let j = 0; j < a.length; j += 2) {
      const m = meshesOut[a[j]], v = a[j + 1];
      for (let k = 0; k < 4; k++) {
        m.jointIndices[v * 4 + k] = k < top.length ? top[k][0] : 0;
        m.jointWeights[v * 4 + k] = k < top.length ? top[k][1] / sum : 0;
      }
    }
  }
}

/**
 * Skelett + geskinnte Meshes für Skelett-Exporte aufbauen (gemeinsame Basis
 * von RealityKit-USDZ und MotionSync-GLB). Liefert:
 *   joints        – Joint-Pfade ("Root", "Root/torso", …), Index = Joint-Id
 *   bind          – Welt-Bind-Matrix je Joint
 *   rest          – LOKALE Ruhe-Matrix je Joint (relativ zum Eltern-Joint)
 *   animJoints    – Pfade der animierten Abschnitts-Joints
 *   sectionJoints – je Abschnitt {name, restA, restB, bindM, parentBindInv, aId, bId, …}
 *   meshesOut     – {usdName, geometry, material, jointIndices, jointWeights, elementSize}
 */
function buildSkelRig(usableBones, ratio) {
  {
    scene.updateMatrixWorld(true);
    const invRoot = modelObject.matrixWorld.clone().invert();
    const animatedNames = new Set(usableBones.map((b) => b.segment));
    const lockedNames = new Set(state.segments.filter((s) => s.locked).map((s) => s.name));
    const owners = triOwners(animatedNames, lockedNames);
    const X = new THREE.Vector3(1, 0, 0);

    // 1) Skelett: joints[0] = "Root" (Anker, identisch). Je Abschnitt ein Joint.
    //    Ein Segment, das einem Eltern-Segment folgt („bewegt sich mit"), wird
    //    als KIND-Joint des Eltern-Joints angelegt – UsdSkel komponiert dann die
    //    Bewegung automatisch (Schnabel folgt Körper). Eltern müssen vor Kindern
    //    im joints-Array stehen → größere Segmente (Eltern) zuerst verarbeiten.
    const orderedBones = [...usableBones].sort((a, b) =>
      segTriCountGlobal(segmentByName(b.segment)) - segTriCountGlobal(segmentByName(a.segment)));
    const joints = ['Root'];
    const bind = [new THREE.Matrix4()];
    const rest = [new THREE.Matrix4()];
    const animJoints = [];
    const sectionJoints = [];          // Reihenfolge = animJoints-Reihenfolge
    const boneInfo = new Map();         // boneId → { idxs, paths, binds, parented, parentAnchorIdx }
    const uniqJoint = makeUniquer();
    uniqJoint('Root');

    for (const bone of orderedBones) {
      const seg = segmentByName(bone.segment);
      const restJoints = bone.joints.map((j) => jointRestVec(j));
      // Eltern-Joint (am Wurzelpunkt des Kindes), falls dieses Segment folgt
      const pInfo = parentInfoFor(seg, restJoints[0], animatedNames);
      let parentPath = 'Root', parentBindWorld = null, parentAnchorIdx = 0;
      if (pInfo) {
        const pBone = boneOfSegment(findParentSegment(seg.name, animatedNames));
        const pi = pBone ? boneInfo.get(pBone.id) : null;
        if (pi) {
          parentPath = pi.paths[pInfo.section];
          parentBindWorld = pi.binds[pInfo.section];
          parentAnchorIdx = pi.idxs[pInfo.section];
        }
      }
      const parentBindInv = parentBindWorld ? parentBindWorld.clone().invert() : null;
      const idxs = [], paths = [], binds = [];
      // ECHTE Kette: Abschnitt k hängt unter Abschnitt k−1 (nicht als
      // Geschwister nebeneinander unter dem Eltern-Joint). So folgt der
      // Unterarm der Schulter auch in Blender/Unity/Panda3D von selbst –
      // kein Konsument muss die Hierarchie erraten. Die Anim-Locals werden
      // in den Frame-Schleifen relativ zum LIVE-Zustand des vorigen Glieds
      // berechnet (sectionWorldAt), damit sich nichts doppelt anwendet.
      let chainPath = parentPath;         // Pfad des vorherigen Kettenglieds
      let prevBindInv = parentBindInv;    // Welt-Bind⁻¹ des vorherigen Glieds
      let prevSj = null;                  // voriger Abschnitt derselben Kette
      for (let k = 0; k < restJoints.length - 1; k++) {
        const restA = restJoints[k], restB = restJoints[k + 1];
        const dir = new THREE.Vector3().subVectors(restB, restA);
        const restLen = dir.length() || 1e-6;
        const restDir = dir.clone().multiplyScalar(1 / restLen);
        const Ralign = new THREE.Quaternion().setFromUnitVectors(X, restDir);
        const bindM = new THREE.Matrix4().compose(restA.clone(), Ralign, new THREE.Vector3(1, 1, 1));
        const name = uniqJoint(toUsdName(bone.segment) + (restJoints.length > 2 ? '_b' + (k + 1) : ''));
        const path = chainPath + '/' + name;
        const gi = joints.length;
        joints.push(path);
        bind.push(bindM);   // bindTransforms sind WELT-Raum (unabhängig von der Hierarchie)
        // restTransform ist LOKAL (relativ zum vorigen Kettenglied bzw. Eltern-Joint)
        rest.push(prevBindInv ? new THREE.Matrix4().multiplyMatrices(prevBindInv, bindM) : bindM.clone());
        animJoints.push(path);
        const sj = { name, restA, restB, restDir, restLen, Ralign, bindM, parentBindInv, prev: prevSj,
          aId: bone.joints[k].id, bId: bone.joints[k + 1].id };
        sectionJoints.push(sj);
        idxs.push(gi); paths.push(path); binds.push(bindM);
        chainPath = path;
        prevBindInv = bindM.clone().invert();
        prevSj = sj;
      }
      boneInfo.set(bone.id, { idxs, paths, binds, parented: !!parentBindInv, parentAnchorIdx });
    }

    // 2) Geskinnte Meshes + Gewichte (2 Einflüsse je Vertex: Root-Anker + Abschnitt)
    const meshesOut = [];
    const uniqMesh = makeUniquer();
    for (const bone of usableBones) {
      const { idxs, parented, parentAnchorIdx } = boneInfo.get(bone.id);
      const restJoints = bone.joints.map((j) => jointRestVec(j));
      // folgt das Segment dem Eltern, ankert glue=0 am ELTERN-Joint (statt Root),
      // damit die Naht beim Mitbewegen geschlossen bleibt. Der Anker wird PRO
      // VERTEX auf den nächsten Eltern-Abschnitt gelegt – exakt die Transform,
      // die die Eltern-Oberfläche an derselben Stelle bekommt (nahtdicht auch
      // bei starkem Beugen des Eltern-Segments).
      const anchorIdx = parented ? parentAnchorIdx : 0;
      let pIdxs = null, pRestJ = null;
      if (parented) {
        const pBone = boneOfSegment(findParentSegment(bone.segment, animatedNames));
        const pi = pBone ? boneInfo.get(pBone.id) : null;
        if (pi) { pIdxs = pi.idxs; pRestJ = pBone.joints.map((j) => jointRestVec(j)); }
      }
      let total = 0;
      for (let i = 0; i < restJoints.length - 1; i++) total += restJoints[i].distanceTo(restJoints[i + 1]);
      const band = ROOT_GLUE_FRAC * (total || 1e-6);

      let eff = exclusiveSelOf(bone.segment, owners);
      if (!Object.keys(eff).length) eff = segmentByName(bone.segment).sel;
      const built = [];
      for (const mi in eff) {
        const entry = meshList[mi];
        if (!entry) continue;
        const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
        let geo = buildSubsetGeometry(entry, eff[mi], rel);
        if (!geo) continue;
        if (ratio < 1) geo = decimateGeometry(geo, ratio);
        const restArr = new Float32Array(geo.attributes.position.array);
        const section = assignSections(restArr, restJoints);
        const boundary = subsetBoundaryFlags(geo);
        const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
        built.push({ geo, restArr, section, boundary, mat });
      }
      const glueSkins = built.map((b) => ({ rest: b.restArr, boundary: b.boundary }));
      try {
        // nur echte (geteilte) Nähte verankern; Nähte zu Kind-Segmenten bleiben frei
        restrictSeamsToOutside(glueSkins, eff, childSelsOf(bone.segment, animatedNames, owners));
      } catch (err) {
        console.warn('Außen-Naht-Filter übersprungen:', err);
      }
      const glues = computeSeamGlue(glueSkins, band);

      const base = toUsdName(bone.segment);
      built.forEach((b, bi) => {
        const glue = glues[bi];
        const n = b.restArr.length / 3;
        // je Vertex der nächste ELTERN-Abschnitt (nahtdichter Anker)
        const psec = pIdxs ? assignSections(b.restArr, pRestJ) : null;
        const jointIndices = new Array(n * 2);
        const jointWeights = new Array(n * 2);
        for (let v = 0; v < n; v++) {
          const g = glue ? glue[v] : 1;
          jointIndices[v * 2] = psec ? pIdxs[psec[v]] : anchorIdx;   // Eltern-Abschnitt bzw. Root
          jointIndices[v * 2 + 1] = idxs[b.section[v]];
          jointWeights[v * 2] = 1 - g;
          jointWeights[v * 2 + 1] = g;
        }
        meshesOut.push({ usdName: uniqMesh(bi === 0 ? base : base + '_' + (bi + 1)),
          geometry: b.geo, material: b.mat, jointIndices, jointWeights, elementSize: 2 });
      });
    }

    // 2b) Restliches Modell mitnehmen: nicht-animierte Segmente (als eigene
    //     benannte Knoten) und der übrige Rest – fest an den Root-Anker gebunden
    //     (Gewicht 1, bleibt statisch). So enthält die Datei das GANZE Objekt.
    const staticParts = [];
    // Gesperrte Segmente als benannte, statische Knoten (owner-basiert: nicht
    // gesperrte Teilsegmente gehören bereits ihrem animierten Segment).
    for (const seg of state.segments) {
      if (animatedNames.has(seg.name)) continue;
      const eff = exclusiveSelOf(seg.name, owners);
      for (const mi in eff) {
        const entry = meshList[mi];
        if (!entry) continue;
        const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
        const geo = buildSubsetGeometry(entry, eff[mi], rel);
        if (!geo) continue;
        const mat = Array.isArray(entry.mesh.material) ? entry.mesh.material[0] : entry.mesh.material;
        staticParts.push({ geo, mat, name: toUsdName(seg.name) });
      }
    }
    // Herrenlose Dreiecke (kein Segment beansprucht sie) → „Rest"
    unownedParts(owners).forEach((p) => staticParts.push({ geo: p.geo, mat: p.mat, name: 'Rest' }));
    for (const part of staticParts) {
      const geo = ratio < 1 ? decimateGeometry(part.geo, ratio) : part.geo;
      const n = geo.attributes.position.count;
      meshesOut.push({ usdName: uniqMesh(part.name), geometry: geo, material: part.mat,
        jointIndices: new Array(n).fill(0), jointWeights: new Array(n).fill(1), elementSize: 1 });
    }

    // 3) Gewichts-Verschweißung: koinzidente Vertices (Nahtringe) bekommen
    //    exakt denselben Gewichtssatz → wasserdicht unter jeder Pose.
    //    (Hebt alle Meshes einheitlich auf elementSize 4.)
    weldSkinWeights(meshesOut);

    return { joints, bind, rest, animJoints, sectionJoints, meshesOut };
  }
}

async function exportRealityKitUsdz() {
  if (!modelObject || !meshList.length) { toast('Kein Modell geladen', true); return false; }
  const usableBones = skeleton.bones.filter((b) => { const s = segmentByName(b.segment); return s && b.joints.length >= 2; });
  if (!usableBones.length) { toast('Erst einen Knochen zeichnen (Tab „Animation")', true); return false; }
  const clips = skeleton.animations.filter((a) => a.keys.length);
  if (!clips.length) { toast('Keine Animationen mit Keyframes vorhanden', true); return false; }

  const ratio = exportRatio('export-quality-skel');
  toast(`Erzeuge RealityKit-USDZ (UsdSkel, ${clips.length} Animation${clips.length === 1 ? '' : 'en'})…`);
  await new Promise((r) => setTimeout(r, 30));

  try {
    if (ratio < 1) await MeshoptSimplifier.ready;
    const { joints, bind, rest, animJoints, sectionJoints, meshesOut } = buildSkelRig(usableBones, ratio);

    // 3) Alle Clips auf EINER Zeitachse hintereinander (Frame-Bereiche merken)
    const frames = [], rot = [], trans = [], scale = [];
    const clipRanges = [];
    const GAP = 1;
    let cursor = 0;
    for (const clip of clips) {
      const nf = Math.max(1, Math.round(animDuration(clip) * ANIM_FPS));
      for (let f = 0; f <= nf; f++) {
        const t = f / ANIM_FPS;
        frames.push(cursor + f);
        const rF = [], tF = [], sF = [];
        const tt = new THREE.Vector3(), rr = new THREE.Quaternion(), ss = new THREE.Vector3();
        for (const sj of sectionJoints) {
          // Lokale Anim-Matrix: Kettenglieder relativ zum vorigen Glied,
          // Kind-Wurzeln reiten am Eltern-Joint mit (sectionLocalAt).
          sectionLocalAt(sj, clip, t).decompose(tt, rr, ss);
          rF.push([rr.w, rr.x, rr.y, rr.z]);
          tF.push([tt.x, tt.y, tt.z]);
          sF.push([ss.x, ss.y, ss.z]);
        }
        rot.push(rF); trans.push(tF); scale.push(sF);
      }
      const startFrame = cursor, endFrame = cursor + nf;
      clipRanges.push({ name: clip.name, startSec: startFrame / ANIM_FPS,
        durSec: (endFrame - startFrame) / ANIM_FPS });
      cursor = endFrame + GAP;
    }
    const endFrame = Math.max(1, cursor - GAP);

    // 4) Export
    const rootName = toUsdName(state.config.name || state.config.slug);
    const exporter = new USDZExporter();
    const data = await exporter.parseAsync(new THREE.Scene(), {
      skel: { rootName, joints, bind, rest, animJoints, frames, rot, trans, scale, meshes: meshesOut },
      timeCodes: { start: 0, end: endFrame, fps: ANIM_FPS },
    });
    const blob = new Blob([data], { type: 'model/vnd.usdz+zip' });
    const filename = `${state.config.slug}-realitykit.usdz`;
    downloadBlob(blob, filename);

    // 5) Swift-Helfer mit Frame-Bereichen
    const swift = buildRealityKitSwift(rootName, state.config.slug, clipRanges);
    downloadBlob(new Blob([swift], { type: 'text/plain' }), `${toPascalCase(state.config.slug)}Animations.swift`);

    try {
      await api('segmented&variant=realitykit&slug=' + encodeURIComponent(state.config.slug), {
        method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob });
      toast(`${filename} exportiert (${(blob.size / 1024 / 1024).toFixed(1)} MB, ${clips.length} Clips) + Swift-Datei – auf dem Server gespeichert`);
    } catch (err) {
      toast(`${filename} heruntergeladen – Server-Kopie fehlgeschlagen: ${err.message}`, true);
    }
    return true;
  } catch (err) {
    console.error('RealityKit-Export fehlgeschlagen:', err);
    toast('RealityKit-Export fehlgeschlagen: ' + err.message, true);
    return false;
  }
}

/**
 * MotionSync-Export: GLB mit Skelett und ALLEN Animationen als separate,
 * BENANNTE Clips. glTF kennt – anders als USD – mehrere benannte Animationen
 * pro Datei; MotionSync (Panda3D), Blender & Co. laden die Datei direkt und
 * können jeden Clip per Namen ansteuern („arm_heben", „kick", …).
 */
async function exportMotionSyncGlb() {
  if (!modelObject || !meshList.length) { toast('Kein Modell geladen', true); return false; }
  const usableBones = skeleton.bones.filter((b) => { const s = segmentByName(b.segment); return s && b.joints.length >= 2; });
  if (!usableBones.length) { toast('Erst einen Knochen zeichnen (Tab „Animation")', true); return false; }
  // Animationen sind optional: eine GLB nur mit Skelett ist gültig und in
  // MotionSync/Blender brauchbar – Clips lassen sich dort nachträglich anlegen.
  const clips = skeleton.animations.filter((a) => a.keys.length);

  const ratio = exportRatio('export-quality-skel');
  toast(clips.length
    ? `Erzeuge MotionSync-GLB (${clips.length} Clip${clips.length === 1 ? '' : 's'})…`
    : 'Erzeuge MotionSync-GLB (nur Skelett, ohne Animation)…');
  await new Promise((r) => setTimeout(r, 30));

  try {
    if (ratio < 1) await MeshoptSimplifier.ready;
    const { joints, bind, rest, sectionJoints, meshesOut } = buildSkelRig(usableBones, ratio);

    // 1) Bone-Hierarchie: lokale Ruhe-Transform je Joint (Pfad = Hierarchie)
    const bones = joints.map((path, i) => {
      const b = new THREE.Bone();
      b.name = path.split('/').pop();
      const t = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
      rest[i].decompose(t, q, s);
      b.position.copy(t); b.quaternion.copy(q); b.scale.copy(s);
      return b;
    });
    joints.forEach((path, i) => {
      if (i === 0) return;
      const parentPath = path.split('/').slice(0, -1).join('/');
      const pi = joints.indexOf(parentPath);
      bones[pi >= 0 ? pi : 0].add(bones[i]);
    });
    const skel = new THREE.Skeleton(bones, bind.map((m) => m.clone().invert()));

    // 2) EIN geskinntes Mesh mit Material-Gruppen → genau EIN Skin in der
    //    Datei (der three.js-Exporter schriebe sonst pro SkinnedMesh einen
    //    eigenen, identischen Skin – daran stolpern Konsumenten wie
    //    panda3d-gltf). Die Gewichte sind bereits verschweißt und einheitlich
    //    4 Einflüsse je Vertex (weldSkinWeights in buildSkelRig).
    const exportRoot = new THREE.Group();
    exportRoot.name = toUsdName(state.config.name || state.config.slug);
    // Y-up für glTF: die Anzeige-Rotation aus der Config (z. B. −90° X bei
    // Z-up-USDZ) auf den Wurzelknoten übernehmen – die Figur steht dann in
    // jedem spezifikationstreuen Viewer aufrecht.
    exportRoot.quaternion.copy(modelRoot.quaternion);
    exportRoot.add(bones[0]);
    const positions = [], normals = [], uvs = [], skinIdx = [], skinWgt = [], indices = [];
    const groupList = [], matList = [];
    let vOff = 0, iOff = 0;
    for (const m of meshesOut) {
      const g = m.geometry;
      const pos = g.attributes.position, nor = g.attributes.normal, uv = g.attributes.uv;
      const n = pos.count;
      for (let v = 0; v < n; v++) {
        positions.push(pos.getX(v), pos.getY(v), pos.getZ(v));
        normals.push(nor ? nor.getX(v) : 0, nor ? nor.getY(v) : 0, nor ? nor.getZ(v) : 1);
        uvs.push(uv ? uv.getX(v) : 0, uv ? uv.getY(v) : 0);
        for (let k = 0; k < 4; k++) {
          skinIdx.push(k < m.elementSize ? m.jointIndices[v * m.elementSize + k] : 0);
          skinWgt.push(k < m.elementSize ? m.jointWeights[v * m.elementSize + k] : 0);
        }
      }
      const idx = g.index;
      const idxCount = idx ? idx.count : n;
      for (let i = 0; i < idxCount; i++) indices.push((idx ? idx.getX(i) : i) + vOff);
      let matIdx = matList.indexOf(m.material);
      if (matIdx < 0) { matList.push(m.material); matIdx = matList.length - 1; }
      groupList.push({ start: iOff, count: idxCount, materialIndex: matIdx });
      vOff += n; iOff += idxCount;
    }
    const mergedGeo = new THREE.BufferGeometry();
    mergedGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    mergedGeo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    mergedGeo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    mergedGeo.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIdx, 4));
    mergedGeo.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWgt, 4));
    mergedGeo.setIndex(indices);
    groupList.forEach((gr) => mergedGeo.addGroup(gr.start, gr.count, gr.materialIndex));
    const sm = new THREE.SkinnedMesh(mergedGeo, matList.length === 1 ? matList[0] : matList);
    sm.name = 'Figur';
    exportRoot.add(sm);
    sm.bind(skel, new THREE.Matrix4());

    // 3) Je Animation ein BENANNTER Clip (Position/Rotation/Skalierung je Joint)
    const clipsOut = clips.map((clip) => {
      const nf = Math.max(1, Math.round(animDuration(clip) * ANIM_FPS));
      const times = [];
      for (let f = 0; f <= nf; f++) times.push(f / ANIM_FPS);
      const tracks = [];
      const tt = new THREE.Vector3(), rr = new THREE.Quaternion(), ss = new THREE.Vector3();
      for (const sj of sectionJoints) {
        const pos = new Float32Array((nf + 1) * 3);
        const rot = new Float32Array((nf + 1) * 4);
        const scl = new Float32Array((nf + 1) * 3);
        for (let f = 0; f <= nf; f++) {
          const t = f / ANIM_FPS;
          // wie im USDZ-Export: Kettenglieder relativ zum vorigen Glied,
          // Kind-Wurzeln reiten am Eltern-Joint mit (sectionLocalAt)
          sectionLocalAt(sj, clip, t).decompose(tt, rr, ss);
          pos[f * 3] = tt.x; pos[f * 3 + 1] = tt.y; pos[f * 3 + 2] = tt.z;
          rot[f * 4] = rr.x; rot[f * 4 + 1] = rr.y; rot[f * 4 + 2] = rr.z; rot[f * 4 + 3] = rr.w;
          scl[f * 3] = ss.x; scl[f * 3 + 1] = ss.y; scl[f * 3 + 2] = ss.z;
        }
        tracks.push(new THREE.VectorKeyframeTrack(sj.name + '.position', times, pos));
        tracks.push(new THREE.QuaternionKeyframeTrack(sj.name + '.quaternion', times, rot));
        tracks.push(new THREE.VectorKeyframeTrack(sj.name + '.scale', times, scl));
      }
      return new THREE.AnimationClip(clip.name, nf / ANIM_FPS, tracks);
    });

    // 4) GLB + GLTF schreiben (gleicher Inhalt: GLB = binär kompakt,
    //    GLTF = lesbares JSON mit eingebetteten Buffern/Texturen als Data-URIs)
    const exportScene = new THREE.Scene();
    exportScene.add(exportRoot);
    const glb = await new GLTFExporter().parseAsync(exportScene, { binary: true, animations: clipsOut });
    const blob = new Blob([glb], { type: 'model/gltf-binary' });
    const filename = `${state.config.slug}-motionsync.glb`;
    downloadBlob(blob, filename);
    const gltfJson = await new GLTFExporter().parseAsync(exportScene, { binary: false, animations: clipsOut });
    const gltfBlob = new Blob([JSON.stringify(gltfJson)], { type: 'model/gltf+json' });
    downloadBlob(gltfBlob, `${state.config.slug}-motionsync.gltf`);
    toast(`${filename} + .gltf exportiert (${(blob.size / 1024 / 1024).toFixed(1)} MB) – Clips: ${clipsOut.map((c) => c.name).join(', ')}`);
    return true;
  } catch (err) {
    console.error('MotionSync-Export fehlgeschlagen:', err);
    toast('MotionSync-Export fehlgeschlagen: ' + err.message, true);
    return false;
  }
}

$('btn-export-motionsync').addEventListener('click', exportMotionSyncGlb);

/** Swift-Begleitcode: lädt das USDZ und schneidet die Clips per Frame-Bereich. */
function buildRealityKitSwift(rootName, slug, clipRanges) {
  const typeName = toPascalCase(slug);
  const used = new Set();
  const cases = clipRanges.map((c) => {
    let id = toCamelCase(c.name); let n = id, i = 2;
    while (used.has(n)) n = id + i++; used.add(n);
    return { id: n, name: c.name, start: c.startSec, dur: c.durSec };
  });
  const caseLines = cases.map((c) => `    case ${c.id}`).join('\n');
  const rangeLines = cases.map((c) =>
    `        case .${c.id}: return (start: ${c.start.toFixed(4)}, duration: ${c.dur.toFixed(4)})`).join('\n');
  const nameLines = cases.map((c) =>
    `        case .${c.id}: return ${JSON.stringify(c.name)}`).join('\n');

  return `// Auto-generiert vom 3D-Segment-Tool – RealityKit-Animationshelfer.
// Datei „${slug}-realitykit.usdz" enthält EIN Skelett mit allen Clips auf einer
// Zeitachse. Diese Clips werden hier per Frame-Bereich in benannte Animationen
// geschnitten. (Auf dem Gerät gegenprüfen; API-Namen ggf. an deine iOS-Version
// anpassen.)
import RealityKit
import Foundation

enum ${typeName}Clip: CaseIterable {
${caseLines}

    /// Start und Dauer (Sekunden) des Clips auf der gemeinsamen Zeitachse.
    var range: (start: TimeInterval, duration: TimeInterval) {
        switch self {
${rangeLines}
        }
    }

    var displayName: String {
        switch self {
${nameLines}
        }
    }
}

enum ${typeName}Loader {
    /// Lädt das Modell INKL. Animationen (Entity(named:), nicht ModelEntity!).
    static func load() async throws -> Entity {
        return try await Entity(named: "${slug}-realitykit")
    }
}

extension Entity {
    /// Spielt einen benannten Clip ab, indem die gemeinsame Animation auf seinen
    /// Frame-Bereich zugeschnitten wird.
    @discardableResult
    func play(_ clip: ${typeName}Clip, loop: Bool = false,
              transition: TimeInterval = 0.2) -> AnimationPlaybackController? {
        guard let full = availableAnimations.first else { return nil }
        let r = clip.range
        let view = AnimationView(
            source: full.definition,
            trimStart: r.start,
            trimEnd: r.start + r.duration,
            trimDuration: nil,
            offset: 0,
            delay: 0,
            speed: 1.0
        )
        guard let resource = try? AnimationResource.generate(with: view) else { return nil }
        let anim = loop ? resource.repeat() : resource
        return playAnimation(anim, transitionDuration: transition, startsPaused: false)
    }
}

// Beispiel:
//   let entity = try await ${typeName}Loader.load()
//   content.add(entity)
//   entity.play(.${cases[0] ? cases[0].id : 'clip'}, loop: true)
`;
}

$('btn-export-realitykit').addEventListener('click', exportRealityKitUsdz);

// --- Persistenz (RLE-komprimierte Dreiecksindizes in config.segments) ---

function rleEncode(sel) {
  const out = [];
  let i = 0;
  while (i < sel.length) {
    if (sel[i]) {
      const start = i;
      while (i < sel.length && sel[i]) i++;
      out.push(start, i - start);
    } else i++;
  }
  return out;
}

function rleDecode(ranges, length) {
  const sel = new Uint8Array(length);
  for (let i = 0; i < ranges.length; i += 2) {
    sel.fill(1, ranges[i], Math.min(ranges[i] + ranges[i + 1], length));
  }
  return sel;
}

/** Laufzeit-Segmente in die Konfiguration serialisieren (vor dem Speichern). */
function segmentsToConfig() {
  if (!state.config) return;
  state.config.segments = state.segments.map((seg) => {
    const meshes = {};
    for (const mi in seg.sel) {
      const entry = meshList[mi];
      const ranges = rleEncode(seg.sel[mi]);
      if (entry && ranges.length) meshes[entry.key] = ranges;
    }
    return { name: seg.name, color: seg.color, meshes, gesture: seg.gesture || null, locked: !!seg.locked };
  });
}

/** Segmente aus der Konfiguration wiederherstellen (nach dem Laden). */
function restoreSegmentsFromConfig() {
  state.segments = [];
  // Topologie-Check: passt das Modell noch zu den gespeicherten Daten?
  const topo = state.config?.topology;
  if (topo && meshList.length) {
    const tris = meshList.reduce((sum, e) => sum + e.triCount, 0);
    if (topo.meshes !== meshList.length || topo.tris !== tris) {
      toast('Achtung: Das Modell hat sich geändert – gespeicherte Segmente/' +
        'Joints passen evtl. nicht mehr (Topologie weicht ab)', true);
    }
  }
  const stored = state.config?.segments;
  if (Array.isArray(stored)) {
    for (const s of stored) {
      const sel = {};
      for (const key in s.meshes || {}) {
        const mi = meshList.findIndex((m) => m.key === key);
        if (mi >= 0) sel[mi] = rleDecode(s.meshes[key], meshList[mi].triCount);
      }
      const expected = Object.keys(s.meshes || {}).length;
      if (Object.keys(sel).length < expected) {
        toast(`Segment "${s.name}": Teile passen nicht mehr zum Modell`, true);
      }
      if (Object.keys(sel).length) {
        const seg = {
          name: s.name || 'Segment',
          color: s.color || SEGMENT_COLORS[state.segments.length % SEGMENT_COLORS.length],
          visible: true, locked: !!s.locked, sel,
          gesture: s.gesture || null,
          highlights: [],
        };
        state.segments.push(seg);
        rebuildSegmentHighlights(seg);
      }
    }
  }
  renderSegmentList();
  updateSelectionInfo();
}

// --- Export: segmentiertes USDZ ---

/** USD-konformer Knotenname: nur [A-Za-z0-9_], nicht mit Ziffer beginnend. */
function toUsdName(name) {
  let s = name
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!s) s = 'Segment';
  if (/^[0-9]/.test(s)) s = '_' + s;
  return s;
}

/** Indiziertes Teil-Geometrie-Subset eines Meshes (im Modell-Raum). */
function buildSubsetGeometry(entry, sel, relMatrix) {
  const g = entry.mesh.geometry;
  const pos = g.attributes.position, norm = g.attributes.normal, uv = g.attributes.uv;
  const index = g.index;
  const remap = new Map();
  const positions = [], normals = [], uvs = [], indices = [];

  for (let t = 0; t < sel.length; t++) {
    if (!sel[t]) continue;
    for (let k = 0; k < 3; k++) {
      const vi = index ? index.getX(t * 3 + k) : t * 3 + k;
      let ni = remap.get(vi);
      if (ni === undefined) {
        ni = remap.size;
        remap.set(vi, ni);
        positions.push(pos.getX(vi), pos.getY(vi), pos.getZ(vi));
        if (norm) normals.push(norm.getX(vi), norm.getY(vi), norm.getZ(vi));
        if (uv) uvs.push(uv.getX(vi), uv.getY(vi));
      }
      indices.push(ni);
    }
  }
  if (!indices.length) return null;

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  if (normals.length) geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  if (uvs.length) geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.applyMatrix4(relMatrix);          // Mesh-Lokal → Modell-Raum (inkl. Normalen)
  if (!normals.length) geo.computeVertexNormals();
  return geo;
}

/** Geometrie auf einen Anteil der Dreiecke reduzieren (meshoptimizer). */
function decimateGeometry(geo, ratio) {
  if (ratio >= 1) return geo;
  const pos = geo.attributes.position;
  const norm = geo.attributes.normal;
  const uv = geo.attributes.uv;

  // 1) Schweißen mit TOLERANZ: (nahezu) gleiche Positionen zusammenführen.
  //    Der USD-Loader liefert an geteilten Kanten oft minimal abweichende
  //    Floats – exaktes Bit-Vergleichen würde sie NICHT mergen, der Simplifier
  //    risse dann Löcher in die Oberfläche. Ein feines Positionsraster (~0,01 %
  //    der Modellgröße) verschweißt die Hülle zuverlässig → keine Risse.
  let minx = Infinity, miny = Infinity, minz = Infinity, maxx = -Infinity, maxy = -Infinity, maxz = -Infinity;
  for (let v = 0; v < pos.count; v++) {
    const x = pos.getX(v), y = pos.getY(v), z = pos.getZ(v);
    if (x < minx) minx = x; if (y < miny) miny = y; if (z < minz) minz = z;
    if (x > maxx) maxx = x; if (y > maxy) maxy = y; if (z > maxz) maxz = z;
  }
  const maxDim = Math.max(maxx - minx, maxy - miny, maxz - minz) || 1;
  const inv = 1 / (maxDim * 1e-4);
  const weldMap = new Map();
  const weldOf = new Uint32Array(pos.count);
  for (let v = 0; v < pos.count; v++) {
    const key = Math.round(pos.getX(v) * inv) + ',' + Math.round(pos.getY(v) * inv) + ',' + Math.round(pos.getZ(v) * inv);
    let w = weldMap.get(key);
    if (w === undefined) {
      w = weldMap.size;
      weldMap.set(key, w);
    }
    weldOf[v] = w;
  }
  const weldedCount = weldMap.size;
  const wPos = new Float32Array(weldedCount * 3);
  const wNorm = norm ? new Float32Array(weldedCount * 3) : null;
  const wUv = uv ? new Float32Array(weldedCount * 2) : null;
  const written = new Uint8Array(weldedCount);
  for (let v = 0; v < pos.count; v++) {
    const w = weldOf[v];
    if (written[w]) continue;
    written[w] = 1;
    wPos[w * 3] = pos.getX(v); wPos[w * 3 + 1] = pos.getY(v); wPos[w * 3 + 2] = pos.getZ(v);
    if (wNorm) { wNorm[w * 3] = norm.getX(v); wNorm[w * 3 + 1] = norm.getY(v); wNorm[w * 3 + 2] = norm.getZ(v); }
    if (wUv) { wUv[w * 2] = uv.getX(v); wUv[w * 2 + 1] = uv.getY(v); }
  }
  const srcCount = geo.index ? geo.index.count : pos.count;
  const srcIndex = new Uint32Array(srcCount);
  for (let i = 0; i < srcCount; i++) {
    srcIndex[i] = weldOf[geo.index ? geo.index.getX(i) : i];
  }

  // 2) Vereinfachen – target_error = 1 erzwingt die Ziel-Dreieckszahl.
  //    LockBorder hält Randvertices fest, damit Segment-Nähte exakt
  //    aufeinandertreffen (sonst klafft die Hülle an reduzierter Qualität).
  const target = Math.max(3, Math.floor((srcCount * ratio) / 3) * 3);
  const [newIndex] = MeshoptSimplifier.simplify(srcIndex, wPos, 3, target, 1.0, ['LockBorder']);

  // 3) Kompaktieren: nur noch genutzte Vertices behalten
  const remap = new Map();
  const outPos = [], outNorm = [], outUv = [], outIdx = [];
  for (const vi of newIndex) {
    let ni = remap.get(vi);
    if (ni === undefined) {
      ni = remap.size;
      remap.set(vi, ni);
      outPos.push(wPos[vi * 3], wPos[vi * 3 + 1], wPos[vi * 3 + 2]);
      if (wNorm) outNorm.push(wNorm[vi * 3], wNorm[vi * 3 + 1], wNorm[vi * 3 + 2]);
      if (wUv) outUv.push(wUv[vi * 2], wUv[vi * 2 + 1]);
    }
    outIdx.push(ni);
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(outPos, 3));
  if (outNorm.length) out.setAttribute('normal', new THREE.Float32BufferAttribute(outNorm, 3));
  if (outUv.length) out.setAttribute('uv', new THREE.Float32BufferAttribute(outUv, 2));
  out.setIndex(outIdx);
  return out;
}

function exportRatio(selectId) {
  return parseFloat($(selectId).value) || 1;
}

async function exportSegmentedUsdz() {
  if (!modelObject || !meshList.length) {
    toast('Kein Modell geladen', true);
    return;
  }
  if (!state.segments.length) {
    toast('Erst Segmente anlegen', true);
    return;
  }

  const ratio = exportRatio('export-quality-seg');
  toast(ratio < 1
    ? `Erzeuge segmentiertes USDZ (${Math.round(ratio * 100)} % der Dreiecke)…`
    : 'Erzeuge segmentiertes USDZ…');
  await new Promise((r) => setTimeout(r, 30));   // Toast rendern lassen

  try {
    if (ratio < 1) await MeshoptSimplifier.ready;
    scene.updateMatrixWorld(true);
    const invRoot = modelObject.matrixWorld.clone().invert();
    const exportScene = new THREE.Scene();
    const usedNames = new Set();

    const uniqueName = (base) => {
      let n = base, i = 2;
      while (usedNames.has(n)) n = base + '_' + i++;
      usedNames.add(n);
      return n;
    };

    // Alle Segment-Teile + "Rest" (alles Unsegmentierte)
    const jobs = state.segments.map((seg) => ({ name: toUsdName(seg.name), sel: seg.sel }));
    const restSel = {};
    meshList.forEach((entry, mi) => {
      const rest = new Uint8Array(entry.triCount).fill(1);
      state.segments.forEach((seg) => {
        const s = seg.sel[mi];
        if (s) for (let t = 0; t < rest.length; t++) if (s[t]) rest[t] = 0;
      });
      if (rest.some((v) => v)) restSel[mi] = rest;
    });
    if (Object.keys(restSel).length) jobs.push({ name: 'Rest', sel: restSel });

    for (const job of jobs) {
      // Geometrien sammeln, gemeinsames Zentrum bestimmen (= Pivot)
      const parts = [];
      const bbox = new THREE.Box3();
      for (const mi in job.sel) {
        const entry = meshList[mi];
        if (!entry || !job.sel[mi].some((v) => v)) continue;
        const rel = new THREE.Matrix4().multiplyMatrices(invRoot, entry.mesh.matrixWorld);
        let geo = buildSubsetGeometry(entry, job.sel[mi], rel);
        if (!geo) continue;
        if (ratio < 1) geo = decimateGeometry(geo, ratio);
        geo.computeBoundingBox();
        bbox.union(geo.boundingBox);
        const mat = Array.isArray(entry.mesh.material)
          ? entry.mesh.material[0] : entry.mesh.material;
        parts.push({ geo, mat });
      }
      if (!parts.length) continue;

      // Alle Teile eines Segments bekommen DENSELBEN Pivot (Segmentzentrum),
      // damit eine Rotation in RealityKit wie eine Gruppenrotation wirkt.
      const center = bbox.getCenter(new THREE.Vector3());
      const baseName = uniqueName(job.name);
      parts.forEach(({ geo, mat }, i) => {
        geo.translate(-center.x, -center.y, -center.z);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.copy(center);
        mesh.userData.usdName = i === 0 ? baseName : uniqueName(baseName + '_' + (i + 1));
        exportScene.add(mesh);
      });
    }

    const exporter = new USDZExporter();
    const data = await exporter.parseAsync(exportScene);
    const blob = new Blob([data], { type: 'model/vnd.usdz+zip' });
    const filename = state.config.slug + '-segmented.usdz';
    const sizeMb = blob.size / 1024 / 1024;
    if (sizeMb > 250) {
      console.warn(`Segmentiertes USDZ ist ${sizeMb.toFixed(0)} MB groß – ` +
        'USDZ speichert Geometrie als Text. Für iOS empfiehlt sich ein ' +
        'dezimiertes Ausgangsmodell (weniger Dreiecke).');
    }

    // 1) Download für Xcode
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);

    // 2) Kopie auf dem Server ablegen
    try {
      await api('segmented&slug=' + encodeURIComponent(state.config.slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      });
      toast(`${filename} exportiert (${(blob.size / 1024 / 1024).toFixed(1)} MB) und auf dem Server gespeichert`);
    } catch (err) {
      toast(`${filename} heruntergeladen – Server-Kopie fehlgeschlagen: ${err.message}`, true);
    }
  } catch (err) {
    console.error('Segment-Export fehlgeschlagen:', err);
    toast('Export fehlgeschlagen: ' + err.message, true);
  }
}

$('btn-export-segmented').addEventListener('click', exportSegmentedUsdz);

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function api(action, options = {}) {
  const res = await fetch('api.php?action=' + action, options);
  const data = await res.json().catch(() => ({ ok: false, error: 'Ungültige Server-Antwort' }));
  if (!data.ok) throw new Error(data.error || 'Unbekannter Fehler');
  return data;
}

async function refreshList(selectSlug = null) {
  const data = await api('list');
  state.models = data.models;
  renderList();
  if (selectSlug) {
    const m = state.models.find((x) => x.slug === selectSlug);
    if (m) selectModel(m);
  }
}

function renderList() {
  const ul = $('model-list');
  ul.innerHTML = '';
  for (const m of state.models) {
    const li = document.createElement('li');
    li.className = state.current?.slug === m.slug ? 'active' : '';
    const mb = (m.size / 1024 / 1024).toFixed(1);
    li.innerHTML = `<strong>${escapeHtml(m.config.name || m.slug)}</strong>
                    <span class="meta">${m.slug} · ${mb} MB</span>`;
    li.addEventListener('click', () => selectModel(m));
    ul.appendChild(li);
  }
  if (!state.models.length) {
    ul.innerHTML = '<li style="cursor:default;color:var(--text-dim)">Noch keine Modelle</li>';
  }
}

function selectModel(model) {
  if (state.dirty && !confirm('Ungespeicherte Änderungen verwerfen?')) return;
  state.current = model;
  state.config = structuredClone(model.config);
  state.dirty = false;
  clearUndoHistory();
  renderList();
  configToForm();
  loadModel(model);
}

// ---------------------------------------------------------------------------
// Upload (mit Fortschrittsanzeige via XHR)
// ---------------------------------------------------------------------------

function uploadFile(file) {
  if (!/\.usdz$/i.test(file.name)) {
    toast('Nur .usdz-Dateien werden unterstützt', true);
    return;
  }
  const form = new FormData();
  form.append('file', file);

  const progress = $('upload-progress'), bar = $('upload-bar'), label = $('upload-label');
  progress.hidden = false;
  bar.style.width = '0%';

  const xhr = new XMLHttpRequest();
  xhr.open('POST', 'api.php?action=upload');
  xhr.upload.onprogress = (e) => {
    if (e.lengthComputable) {
      const pct = Math.round((e.loaded / e.total) * 100);
      bar.style.width = pct + '%';
      label.textContent = `Lade hoch… ${pct} %`;
    }
  };
  xhr.onload = () => {
    progress.hidden = true;
    try {
      const data = JSON.parse(xhr.responseText);
      if (!data.ok) throw new Error(data.error);
      toast(data.cleaned
        ? 'Modell hochgeladen & automatisch entriggt: ' + data.slug
        : 'Modell hochgeladen: ' + data.slug);
      refreshList(data.slug);
    } catch (err) {
      toast('Upload fehlgeschlagen: ' + err.message, true);
    }
  };
  xhr.onerror = () => {
    progress.hidden = true;
    toast('Upload fehlgeschlagen (Netzwerkfehler)', true);
  };
  xhr.send(form);
}

$('file-input').addEventListener('change', (e) => {
  if (e.target.files[0]) uploadFile(e.target.files[0]);
  e.target.value = '';
});

// Drag & Drop auf den Viewer
const wrap = $('viewer-wrap');
['dragenter', 'dragover'].forEach((ev) =>
  wrap.addEventListener(ev, (e) => { e.preventDefault(); wrap.classList.add('dragover'); }));
['dragleave', 'drop'].forEach((ev) =>
  wrap.addEventListener(ev, (e) => { e.preventDefault(); wrap.classList.remove('dragover'); }));
wrap.addEventListener('drop', (e) => {
  const file = e.dataTransfer?.files?.[0];
  if (file) uploadFile(file);
});

// ---------------------------------------------------------------------------
// Formular <-> Konfiguration
// ---------------------------------------------------------------------------

function configToForm() {
  const c = state.config;
  if (!c) return;
  $('config-slug').textContent = c.slug;
  $('cfg-name').value = c.name ?? '';
  $('cfg-notes').value = c.notes ?? '';

  $('cfg-scale-uniform').checked = c.scale.uniform;
  $('cfg-scale-x').value = c.scale.x;
  $('cfg-scale-y').value = c.scale.y;
  $('cfg-scale-z').value = c.scale.z;

  $('cfg-pos-x').value = c.position.x;
  $('cfg-pos-y').value = c.position.y;
  $('cfg-pos-z').value = c.position.z;
  $('cfg-rot-x').value = c.rotation.x;
  $('cfg-rot-y').value = c.rotation.y;
  $('cfg-rot-z').value = c.rotation.z;
  $('cfg-ground').checked = !!c.groundAlign;

  $('cfg-anchor-type').value = c.anchor.type;
  $('cfg-anchor-class').value = c.anchor.classification;
  $('cfg-anchor-minx').value = c.anchor.minimumBounds.x;
  $('cfg-anchor-minz').value = c.anchor.minimumBounds.z;

  $('cfg-collision').value = c.collision.shape;
  $('cfg-physics').value = c.physics.mode;
  $('cfg-mass').value = c.physics.mass;

  $('cfg-gesture-move').checked = c.gestures.translation;
  $('cfg-gesture-rotate').checked = c.gestures.rotation;
  $('cfg-gesture-scale').checked = c.gestures.scale;
  $('cfg-shadow-ground').checked = c.shadows.grounding;
  $('cfg-shadow-cast').checked = c.shadows.cast;

  updateConditionalRows();
}

function formToConfig() {
  const c = state.config;
  if (!c) return;
  c.name = $('cfg-name').value.trim() || c.slug;
  c.notes = $('cfg-notes').value;

  c.scale.uniform = $('cfg-scale-uniform').checked;
  c.scale.x = num('cfg-scale-x', 1);
  c.scale.y = num('cfg-scale-y', 1);
  c.scale.z = num('cfg-scale-z', 1);

  c.position.x = num('cfg-pos-x', 0);
  c.position.y = num('cfg-pos-y', 0);
  c.position.z = num('cfg-pos-z', 0);
  c.rotation.x = num('cfg-rot-x', 0);
  c.rotation.y = num('cfg-rot-y', 0);
  c.rotation.z = num('cfg-rot-z', 0);
  c.groundAlign = $('cfg-ground').checked;

  c.anchor.type = $('cfg-anchor-type').value;
  c.anchor.classification = $('cfg-anchor-class').value;
  c.anchor.minimumBounds.x = num('cfg-anchor-minx', 0.2);
  c.anchor.minimumBounds.z = num('cfg-anchor-minz', 0.2);

  c.collision.shape = $('cfg-collision').value;
  c.physics.mode = $('cfg-physics').value;
  c.physics.mass = num('cfg-mass', 1);

  c.gestures.translation = $('cfg-gesture-move').checked;
  c.gestures.rotation = $('cfg-gesture-rotate').checked;
  c.gestures.scale = $('cfg-gesture-scale').checked;
  c.shadows.grounding = $('cfg-shadow-ground').checked;
  c.shadows.cast = $('cfg-shadow-cast').checked;
}

function num(id, fallback) {
  const v = parseFloat($(id).value);
  return isFinite(v) ? v : fallback;
}

function updateConditionalRows() {
  const type = $('cfg-anchor-type').value;
  const isPlane = type === 'horizontal' || type === 'vertical';
  $('row-classification').style.display = isPlane ? '' : 'none';
  $('row-minbounds').style.display = isPlane ? '' : 'none';
  $('row-mass').style.display = $('cfg-physics').value === 'dynamic' ? '' : 'none';
}

// Uniform-Skalierung: X-Wert auf Y/Z spiegeln
function syncUniformScale(sourceId) {
  if (!$('cfg-scale-uniform').checked) return;
  const v = $(sourceId).value;
  ['cfg-scale-x', 'cfg-scale-y', 'cfg-scale-z'].forEach((id) => { $(id).value = v; });
}

// Live-Update bei jeder Eingabe
const configPanel = $('config-panel');
configPanel.addEventListener('input', (e) => {
  if (!state.config) return;
  if (['cfg-scale-x', 'cfg-scale-y', 'cfg-scale-z'].includes(e.target.id)) {
    syncUniformScale(e.target.id);
  }
  formToConfig();
  state.dirty = true;
  updateConditionalRows();
  applyTransform();
});

// Zielgröße → Skalierungsfaktor berechnen
$('btn-apply-target').addEventListener('click', () => {
  if (!state.config) return;
  if (!state.rawSize) { toast('Originalmaße unbekannt (Vorschau fehlgeschlagen)', true); return; }
  const target = parseFloat($('cfg-target-size').value);
  if (!isFinite(target) || target <= 0) { toast('Bitte gültige Zielgröße eingeben', true); return; }
  const axis = $('cfg-target-axis').value;
  const raw = state.rawSize[axis];
  if (!raw) { toast('Modell hat auf dieser Achse keine Ausdehnung', true); return; }
  const factor = target / raw;
  state.config.scale = { uniform: true, x: factor, y: factor, z: factor };
  state.dirty = true;
  configToForm();
  applyTransform();
  toast(`Skalierung gesetzt: ×${factor.toPrecision(4)}`);
});

// ---------------------------------------------------------------------------
// Speichern / Löschen
// ---------------------------------------------------------------------------

$('btn-save').addEventListener('click', async () => {
  if (!state.config) return;
  formToConfig();
  segmentsToConfig();
  skeletonToConfig();
  if (meshList.length) {
    state.config.topology = {
      meshes: meshList.length,
      tris: meshList.reduce((sum, e) => sum + e.triCount, 0),
    };
  }
  try {
    await api('save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: state.config.slug, config: state.config }),
    });
    state.current.config = structuredClone(state.config);
    state.dirty = false;
    renderList();
    toast('Konfiguration gespeichert');
  } catch (err) {
    toast('Speichern fehlgeschlagen: ' + err.message, true);
  }
});

$('btn-delete').addEventListener('click', async () => {
  if (!state.current) return;
  if (!confirm(`Modell "${state.current.slug}" wirklich löschen?`)) return;
  try {
    await api('delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: state.current.slug }),
    });
    toast('Modell gelöscht');
    state.current = null;
    state.config = null;
    state.dirty = false;
    clearModel();
    $('viewer-empty').style.display = 'flex';
    $('viewer-empty').firstElementChild.innerHTML =
      'Kein Modell geladen.<br>USDZ hochladen oder links auswählen.';
    refreshList();
  } catch (err) {
    toast('Löschen fehlgeschlagen: ' + err.message, true);
  }
});

// Rigging (UsdSkel) entfernen – macht von Tripo/Blender geriggte Modelle,
// die der WASM-Viewer sonst ignoriert, wieder als statisches Modell sichtbar.
$('btn-clean').addEventListener('click', async () => {
  if (!state.current) { toast('Kein Modell ausgewählt', true); return; }
  const slug = state.current.slug;
  const btn = $('btn-clean');
  const prevLabel = btn.textContent;
  btn.disabled = true;
  btn.textContent = '🧹 Bereinige…';
  try {
    const data = await api('clean', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug }),
    });
    if (data.changed) {
      toast('Modell bereinigt: ' + data.reason);
      state.dirty = false;        // Reload nicht durch „ungespeichert" blockieren
      await refreshList(slug);    // lädt das Modell mit neuem Cache-Key neu
    } else {
      toast('Nichts zu bereinigen: ' + data.reason);
    }
  } catch (err) {
    toast('Clean fehlgeschlagen: ' + err.message, true);
  } finally {
    btn.disabled = false;
    btn.textContent = prevLabel;
  }
});

// ---------------------------------------------------------------------------
// Export: Swift-Code + JSON
// ---------------------------------------------------------------------------

const ANCHOR_SWIFT = {
  horizontal: (c) =>
    `AnchorEntity(.plane(.horizontal, classification: ${classSwift(c)}, minimumBounds: [${f(c.anchor.minimumBounds.x)}, ${f(c.anchor.minimumBounds.z)}]))`,
  vertical: (c) =>
    `AnchorEntity(.plane(.vertical, classification: ${classSwift(c)}, minimumBounds: [${f(c.anchor.minimumBounds.x)}, ${f(c.anchor.minimumBounds.z)}]))`,
  image: () => `AnchorEntity(.image(group: "AR Resources", name: "<ReferenzBild>"))`,
  face: () => `AnchorEntity(.face)`,
  world: () => `AnchorEntity(world: .zero)`,
};

const CLASS_SWIFT = {
  any: '.any', floor: '.floor', table: '.table',
  seat: '.seat', ceiling: '.ceiling', wall: '.wall',
};

function classSwift(c) { return CLASS_SWIFT[c.anchor.classification] || '.any'; }
function f(v) { return Number(v).toString().includes('.') ? Number(v) : Number(v) + '.0'; }
function pascal(slug) {
  return slug.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

/** Swift-taugliche camelCase-Bezeichner aus Segmentnamen. */
function swiftCase(name) {
  const parts = toUsdName(name).split('_').filter(Boolean);
  if (!parts.length) return 'segment';
  return parts
    .map((p, i) => (i === 0 ? p.charAt(0).toLowerCase() : p.charAt(0).toUpperCase()) + p.slice(1))
    .join('');
}

function generateSwift(c) {
  const name = pascal(c.slug);
  const hasSegments = Array.isArray(c.segments) && c.segments.length > 0;
  const hasSkeleton = Array.isArray(c.skeleton?.bones)
    && c.skeleton.bones.some((b) => b.segment && (b.joints?.length || 0) >= 2);
  const hasAnimations = hasSkeleton
    && (c.skeleton.animations || []).some((a) => a.keys?.length);
  // Asset-Wahl: animiertes USDZ (Knochen + Animation) > segmentiertes > Original
  const assetName = hasSkeleton ? c.slug + '-animated'
    : hasSegments ? c.slug + '-segmented' : c.slug;
  const lines = [];

  lines.push(`import RealityKit`);
  lines.push(`import ARKit`);
  lines.push(``);
  lines.push(`// MARK: - ${c.name}`);
  lines.push(`// Generiert vom RealityKit Model Manager am ${new Date().toLocaleDateString('de-DE')}`);
  if (c.notes) lines.push(`// Notizen: ${c.notes.replace(/\n/g, ' ')}`);
  lines.push(`// Datei "${assetName}.usdz" zum Xcode-Projekt hinzufügen (Target Membership setzen).`);
  if (hasSkeleton) {
    lines.push(`// (Das animierte USDZ über "Animiertes USDZ exportieren" im Tab "Animation" erzeugen.)`);
  } else if (hasSegments) {
    lines.push(`// (Das segmentierte USDZ über "Segmentiertes USDZ exportieren" erzeugen.)`);
  }
  lines.push(``);
  lines.push(`enum ${name}Model {`);
  lines.push(``);
  lines.push(`    /// Lädt das Modell, wendet die Konfiguration an und liefert einen fertigen Anchor.`);
  lines.push(`    @MainActor`);
  lines.push(`    static func makeAnchor() async throws -> AnchorEntity {`);
  lines.push(`        let entity = try await Entity(named: "${assetName}")`);
  lines.push(``);

  // Skalierung
  if (c.scale.x !== 1 || c.scale.y !== 1 || c.scale.z !== 1) {
    lines.push(`        // Skalierung (Originalgröße × Faktor)`);
    lines.push(`        entity.scale = SIMD3<Float>(${f(c.scale.x)}, ${f(c.scale.y)}, ${f(c.scale.z)})`);
    lines.push(``);
  }

  // Rotation
  if (c.rotation.x || c.rotation.y || c.rotation.z) {
    lines.push(`        // Rotation (Grad → Radiant)`);
    if (c.rotation.x) lines.push(`        entity.orientation *= simd_quatf(angle: ${f(c.rotation.x)} * .pi / 180, axis: [1, 0, 0])`);
    if (c.rotation.y) lines.push(`        entity.orientation *= simd_quatf(angle: ${f(c.rotation.y)} * .pi / 180, axis: [0, 1, 0])`);
    if (c.rotation.z) lines.push(`        entity.orientation *= simd_quatf(angle: ${f(c.rotation.z)} * .pi / 180, axis: [0, 0, 1])`);
    lines.push(``);
  }

  // Position
  if (c.position.x || c.position.y || c.position.z) {
    lines.push(`        // Positions-Offset relativ zum Anker (Meter)`);
    lines.push(`        entity.position = SIMD3<Float>(${f(c.position.x)}, ${f(c.position.y)}, ${f(c.position.z)})`);
    lines.push(``);
  }

  // Baseline
  if (c.groundAlign) {
    lines.push(`        // Baseline: Unterkante des Modells auf die Anker-Ebene setzen`);
    lines.push(`        let bounds = entity.visualBounds(relativeTo: nil)`);
    lines.push(`        entity.position.y -= bounds.min.y`);
    lines.push(``);
  }

  // Kollision
  if (c.collision.shape !== 'none') {
    lines.push(`        // Kollision`);
    if (c.collision.shape === 'box') {
      lines.push(`        let box = entity.visualBounds(relativeTo: entity)`);
      lines.push(`        entity.components.set(CollisionComponent(shapes: [.generateBox(size: box.extents)]))`);
    } else if (c.collision.shape === 'convex') {
      lines.push(`        // Convex Hull aus dem Mesh (async generiert)`);
      lines.push(`        entity.generateCollisionShapes(recursive: true, static: ${c.physics.mode !== 'dynamic'})`);
    } else {
      lines.push(`        entity.generateCollisionShapes(recursive: true)`);
    }
    lines.push(``);
  }

  // Physik
  if (c.physics.mode !== 'none') {
    const modeMap = { static: '.static', dynamic: '.dynamic', kinematic: '.kinematic' };
    lines.push(`        // Physik`);
    lines.push(`        entity.components.set(PhysicsBodyComponent(`);
    lines.push(`            massProperties: .init(mass: ${f(c.physics.mass)}),`);
    lines.push(`            material: .default,`);
    lines.push(`            mode: ${modeMap[c.physics.mode]}`);
    lines.push(`        ))`);
    lines.push(``);
  }

  // Anker
  lines.push(`        // Anker: ${c.anchor.type}`);
  lines.push(`        let anchor = ${ANCHOR_SWIFT[c.anchor.type](c)}`);
  lines.push(`        anchor.addChild(entity)`);
  lines.push(`        return anchor`);
  lines.push(`    }`);

  // Gesten (ARView / UIKit-Variante)
  const gestures = [];
  if (c.gestures.translation) gestures.push('.translation');
  if (c.gestures.rotation) gestures.push('.rotation');
  if (c.gestures.scale) gestures.push('.scale');
  if (gestures.length && c.collision.shape !== 'none') {
    lines.push(``);
    lines.push(`    /// Gesten aktivieren (benötigt eine CollisionComponent – ist oben konfiguriert).`);
    lines.push(`    @MainActor`);
    lines.push(`    static func installGestures(for entity: Entity, in arView: ARView) {`);
    lines.push(`        guard let model = entity as? HasCollision else { return }`);
    lines.push(`        arView.installGestures([${gestures.join(', ')}], for: model)`);
    lines.push(`    }`);
  } else if (gestures.length) {
    lines.push(``);
    lines.push(`    // Hinweis: Gesten (${gestures.join(', ')}) benötigen eine Kollisionsform –`);
    lines.push(`    // in der Konfiguration ist aktuell "Keine" gewählt.`);
  }

  // Animation: gebackene Knochen-Animation im animierten USDZ
  if (hasSkeleton) {
    lines.push(``);
    lines.push(`    // MARK: Animation`);
    lines.push(``);
    if (hasAnimations) {
      const animNames = c.skeleton.animations
        .filter((a) => a.keys?.length).map((a) => a.name).join('", "');
      lines.push(`    /// Eingebackene Animation abspielen ("${animNames}").`);
      lines.push(`    /// Die Bewegung (drehen + strecken) steckt fertig im USDZ.`);
      lines.push(`    @MainActor`);
      lines.push(`    static func playAnimation(on entity: Entity, loop: Bool = true) {`);
      lines.push(`        guard let animation = entity.availableAnimations.first else { return }`);
      lines.push(`        entity.playAnimation(loop ? animation.repeat(duration: .infinity) : animation)`);
      lines.push(`    }`);
    } else {
      lines.push(`    // Es sind Knochen definiert, aber noch keine Animation mit Keyframes.`);
    }
  }

  // Segmente: benannte Knoten im segmentierten USDZ
  if (hasSegments) {
    lines.push(``);
    lines.push(`    // MARK: Segmente`);
    lines.push(``);
    lines.push(`    /// Im segmentierten USDZ definierte Segmente (Knotennamen).`);
    lines.push(`    enum Segment: String, CaseIterable {`);
    for (const s of c.segments) {
      lines.push(`        case ${swiftCase(s.name)} = "${toUsdName(s.name)}"`);
    }
    lines.push(`    }`);
    lines.push(``);
    lines.push(`    /// Alle Entities eines Segments (ein Segment kann aus mehreren`);
    lines.push(`    /// Material-Teilen bestehen: "Name", "Name_2", …; alle teilen denselben Pivot).`);
    lines.push(`    static func entities(of segment: Segment, in root: Entity) -> [Entity] {`);
    lines.push(`        var result: [Entity] = []`);
    lines.push(`        func walk(_ e: Entity) {`);
    lines.push(`            if e.name == segment.rawValue || e.name.hasPrefix(segment.rawValue + "_") {`);
    lines.push(`                result.append(e)`);
    lines.push(`            }`);
    lines.push(`            for child in e.children { walk(child) }`);
    lines.push(`        }`);
    lines.push(`        walk(root)`);
    lines.push(`        return result`);
    lines.push(`    }`);
    lines.push(``);
    lines.push(`    /// Lässt ein Segment dauerhaft um die eigene Achse rotieren`);
    lines.push(`    /// (z. B. Räder während der Fahrt). Achse im lokalen Raum des Segments,`);
    lines.push(`    /// der Pivot liegt im Segmentzentrum.`);
    lines.push(`    @MainActor`);
    lines.push(`    static func spin(_ segment: Segment,`);
    lines.push(`                     in root: Entity,`);
    lines.push(`                     axis: SIMD3<Float> = [1, 0, 0],`);
    lines.push(`                     secondsPerTurn: Double = 1.0) {`);
    lines.push(`        for part in entities(of: segment, in: root) {`);
    lines.push(`            let base = part.transform`);
    lines.push(`            // Volle Umdrehung als vier Vierteldrehungen (eindeutige Quaternion-Pfade)`);
    lines.push(`            var steps: [AnimationResource] = []`);
    lines.push(`            for i in 0..<4 {`);
    lines.push(`                var from = base`);
    lines.push(`                from.rotation = base.rotation * simd_quatf(angle: Float(i) * .pi / 2, axis: axis)`);
    lines.push(`                var to = base`);
    lines.push(`                to.rotation = base.rotation * simd_quatf(angle: Float(i + 1) * .pi / 2, axis: axis)`);
    lines.push(`                let quarter = FromToByAnimation<Transform>(`);
    lines.push(`                    from: from, to: to,`);
    lines.push(`                    duration: secondsPerTurn / 4,`);
    lines.push(`                    timing: .linear,`);
    lines.push(`                    bindTarget: .transform`);
    lines.push(`                )`);
    lines.push(`                if let res = try? AnimationResource.generate(with: quarter) {`);
    lines.push(`                    steps.append(res)`);
    lines.push(`                }`);
    lines.push(`            }`);
    lines.push(`            if let sequence = try? AnimationResource.sequence(with: steps) {`);
    lines.push(`                part.playAnimation(sequence.repeat(duration: .infinity))`);
    lines.push(`            }`);
    lines.push(`        }`);
    lines.push(`    }`);
  }

  lines.push(`}`);
  lines.push(``);

  // Verwendungsbeispiel
  lines.push(`// MARK: - Verwendung (ARView)`);
  lines.push(`//`);
  lines.push(`// Task { @MainActor in`);
  lines.push(`//     let anchor = try await ${name}Model.makeAnchor()`);
  lines.push(`//     arView.scene.addAnchor(anchor)`);
  if (hasAnimations) {
    lines.push(`//     // Eingebackene Animation (Endlosschleife) starten:`);
    lines.push(`//     if let model = anchor.children.first {`);
    lines.push(`//         ${name}Model.playAnimation(on: model)`);
    lines.push(`//     }`);
  }
  if (hasSegments) {
    const first = swiftCase(c.segments[0].name);
    lines.push(`//     // Beispiel: Segment "${c.segments[0].name}" rotieren lassen`);
    lines.push(`//     if let model = anchor.children.first {`);
    lines.push(`//         ${name}Model.spin(.${first}, in: model, axis: [1, 0, 0], secondsPerTurn: 0.5)`);
    lines.push(`//     }`);
  }
  if (gestures.length && c.collision.shape !== 'none') {
    lines.push(`//     if let entity = anchor.children.first {`);
    lines.push(`//         ${name}Model.installGestures(for: entity, in: arView)`);
    lines.push(`//     }`);
  }
  lines.push(`// }`);

  if (c.shadows.grounding) {
    lines.push(`//`);
    lines.push(`// Grounding Shadow (ARView-Konfiguration):`);
    lines.push(`// arView.environment.sceneUnderstanding.options.insert(.receivesLighting)`);
    lines.push(`// arView.renderOptions.remove(.disableGroundingShadows)`);
  }

  return lines.join('\n');
}

// Dialog-Logik
const dialog = $('export-dialog');

$('btn-export').addEventListener('click', () => {
  if (!state.config) { toast('Kein Modell ausgewählt', true); return; }
  formToConfig();
  segmentsToConfig();
  skeletonToConfig();
  const c = state.config;
  $('export-swift').textContent = generateSwift(c);
  const json = JSON.stringify(c, null, 2);
  $('export-json').textContent = json;
  $('btn-download-json').href =
    'data:application/json;charset=utf-8,' + encodeURIComponent(json);
  $('btn-download-json').download = c.slug + '.config.json';
  dialog.showModal();
});

$('btn-close-dialog').addEventListener('click', () => dialog.close());

document.querySelectorAll('.dialog-tabs .tab').forEach((tab) => {
  tab.addEventListener('click', () => {
    document.querySelectorAll('.dialog-tabs .tab').forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');
    $('export-swift').hidden = tab.dataset.tab !== 'swift';
    $('export-json').hidden = tab.dataset.tab !== 'json';
  });
});

$('btn-copy').addEventListener('click', async () => {
  const visible = $('export-swift').hidden ? $('export-json') : $('export-swift');
  await navigator.clipboard.writeText(visible.textContent);
  toast('In Zwischenablage kopiert');
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let toastTimer = null;
function toast(message, isError = false) {
  const el = $('toast');
  el.textContent = message;
  el.className = 'toast' + (isError ? ' error' : '');
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { el.hidden = true; }, 3500);
}

function escapeHtml(s) {
  return s.replace(/[&<>"]/g, (ch) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
}

window.addEventListener('beforeunload', (e) => {
  if (state.dirty) e.preventDefault();
});

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

refreshList().then(() => {
  if (state.models.length) selectModel(state.models[0]);
}).catch((err) => toast('Modell-Liste konnte nicht geladen werden: ' + err.message, true));

// Test-/Debug-Hook (von test-e2e.mjs genutzt)
window.__rkDebug = () => {
  const r = renderer?.domElement.getBoundingClientRect();
  const toScreen = (pos) => {
    const v = pos.clone().project(camera);
    return { x: (v.x + 1) / 2 * r.width, y: (1 - v.y) / 2 * r.height };
  };
  return {
    segments: state.segments.length,
    segmentNames: state.segments.map((s) => s.name),
    selected: selection ? selectionCount(selection) : 0,
    hasGesture: !!activeGesture,
    depthRange: activeGesture ? [activeGesture.d0, activeGesture.d1] : null,
    handleScreens: handleGroup
      ? handleGroup.userData.handles.map((h) => toScreen(h.position))
      : null,
    bones: skeleton.bones.length,
    boneSegments: skeleton.bones.map((b) => b.segment),
    jointCount: skeleton.bones.reduce((n, b) => n + b.joints.length, 0),
    animations: skeleton.animations.map((a) => ({ name: a.name, keys: a.keys.length })),
    poses: skeleton.poses.map((p) => ({ id: p.id, name: p.name, moved: poseMovedCount(p) })),
    defaultPoseId: skeleton.defaultPoseId,
    // Ausgangslage aller Punkte – zeigt, ob die Standardpose wirklich greift
    baseJoints: allJointIds().map((jid) => jointBaseVec(jid).toArray().map((v) => +v.toFixed(4))),
    curJoints: allJointIds().map((jid) => jointCurVec(jid).toArray().map((v) => +v.toFixed(4))),
    selectedJoint: selectedJointId,
    activeSegment: activeSegName,
    boneTool,
    rigPieces: rig ? rig.skins.length : 0,
    jointScreens: rig
      ? Object.fromEntries(rig.spheres.map((s) =>
          [s.userData.jointId, toScreen(s.getWorldPosition(new THREE.Vector3()))]))
      : null,
  };
};
